/**
 * Shared API helper.
 *
 * - Sends X-User and (when set) X-API-Key on every request.
 * - Sends Content-Type only when there's a body (drops it on GET).
 * - Accepts an AbortSignal to cancel inflight requests.
 * - Surfaces 409 version conflicts as a typed error.
 */

/**
 * ClickPay Phase 4: the MasterData (ops) backend is mounted under
 * `/api/ops/*` in the unified app (see backend/ops_integration.py). Every
 * vendored call site still says `/api/products`, `/api/stream/...`, etc;
 * `withApiBase` rewrites `/api/X` → `/api/ops/X` in ONE place (this request
 * helper + the SSE EventSource in useSSE.ts + BackendStatus's health ping)
 * instead of hand-editing ~100 call sites.
 */
import { emitSessionExpired } from '../../lib/authEvents'
import { currentRequestSignal } from '../../lib/query/requestSignal'
import { noteServerDate } from '../../lib/clock'
import { operatorScopeHeaders } from '../../lib/operatorScope'
import { perfRecord } from '../../lib/perfMark'

export const API_BASE = '/api/ops'

export function withApiBase(path: string): string {
  // B7 guard (2026-07-08 launch-day 404 class): every call site is supposed
  // to pass the un-prefixed path (`/api/products`, `/api/fba-shipments/...`)
  // and let this function add the ONE `/ops` segment. A caller that already
  // wrote `/api/ops/...` used to silently double-prefix to `/api/ops/ops/...`
  // and 404. Fail loud in dev instead of shipping another one of those.
  if (path.startsWith('/api/ops/') || path === '/api/ops') {
    throw new Error(
      `withApiBase: "${path}" already starts with ${API_BASE} — pass the ` +
      `un-prefixed path (e.g. "/api/products") instead; withApiBase adds the ` +
      `/ops segment itself and would otherwise double-prefix to ` +
      `"${API_BASE}${path.slice('/api'.length)}".`,
    )
  }
  return path.startsWith('/api/') ? API_BASE + path.slice('/api'.length) : path
}

const USER_HEADER = 'X-User'
const API_KEY_HEADER = 'X-API-Key'

let _currentUser = 'joseph'
let _apiKey: string | null = null

export function setCurrentUser(name: string) {
  _currentUser = name
  try {
    localStorage.setItem('md.user', name)
  } catch {}
}

export function loadCurrentUser(): string {
  try {
    const v = localStorage.getItem('md.user')
    if (v) _currentUser = v
  } catch {}
  return _currentUser
}

export function setApiKey(key: string | null) {
  _apiKey = key
  try {
    if (key) localStorage.setItem('md.apiKey', key)
    else localStorage.removeItem('md.apiKey')
  } catch {}
}

export function loadApiKey(): string | null {
  try {
    _apiKey = localStorage.getItem('md.apiKey')
  } catch {}
  return _apiKey
}

export class VersionConflictError extends Error {
  current_version: number
  your_version: number | null
  /** The 409's `detail` as the server sent it. Usually the version payload;
   *  a refusal written for a person (PR 19's link Undo: "Can't undo: this
   *  line changed after it was linked …") arrives as a plain string, which
   *  `message` deliberately does not carry — read it from here. */
  detail: unknown
  constructor(detail: any) {
    super(detail?.message || 'version conflict')
    this.current_version = detail?.current_version
    this.your_version = detail?.your_version ?? null
    this.detail = detail
  }
}

export interface FetchOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: any
  ifMatch?: number
  isAdmin?: boolean
  signal?: AbortSignal
  /** When true, body is passed as-is (FormData) and Content-Type is left
   *  to the browser so the multipart boundary is set correctly. */
  isMultipart?: boolean
  /** Extra request headers merged into the default set (User, API key,
   *  If-Match, etc.). Useful for one-off headers like Idempotency-Key. */
  headers?: Record<string, string>
  /** Resolve with the response body as a Blob instead of parsed JSON (PR 18:
   *  the Receive Documents "View" drawer shows a PDF from the file proxy).
   *  Errors are reported exactly as for JSON calls. */
  asBlob?: boolean
}

export async function opsFetch<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const headers: Record<string, string> = {
    [USER_HEADER]: _currentUser,
  }
  if (_apiKey) headers[API_KEY_HEADER] = _apiKey
  if (opts.ifMatch != null) headers['If-Match'] = String(opts.ifMatch)
  if (opts.isAdmin) headers['X-Admin'] = 'true'
  // F4.6 — stamp the Operator scope (X-Operator-Scope) so the ops surfaces
  // (listings, etc.) scope to the selected book too. Empty for the default
  // 'all' scope → unscoped, identical to before. Caller headers still win.
  Object.assign(headers, operatorScopeHeaders())
  if (opts.headers) Object.assign(headers, opts.headers)

  const init: RequestInit = {
    method: opts.method || 'GET',
    headers,
    // UI overhaul PR-0: ambient fallback so the data layer can cancel a call
    // made through a wrapper that takes no `signal` (lib/query/requestSignal.ts).
    signal: opts.signal ?? currentRequestSignal(),
  }
  if (opts.body !== undefined) {
    if (opts.isMultipart) {
      // Browser sets multipart/form-data boundary automatically.
      init.body = opts.body
    } else {
      headers['Content-Type'] = 'application/json'
      init.body = JSON.stringify(opts.body)
    }
  }
  // Build 47 (Phase 13): the ops half's single fetch choke point. Every ops
  // call in the app funnels through here, so one timing pair instruments the
  // lot. Off unless the page was opened with ?perf=1 — perfRecord() returns
  // immediately otherwise, and nothing is sent anywhere either way.
  const _perfT0 = performance.now()
  const res = await fetch(withApiBase(path), init)
  perfRecord(
    withApiBase(path),
    performance.now() - _perfT0,
    res.status,
    // Present only on uncompressed responses; reported as absent, not zero,
    // when it isn't (see perfRecord's note on why we don't measure by
    // re-reading the body).
    Number(res.headers.get('content-length')) || null,
  )
  if (!res.ok) {
    // ClickPay login UI: session expired mid-flight (CLICKPAY_AUTH=on) —
    // notify the AuthProvider so the SPA routes to the Login page.
    if (res.status === 401) emitSessionExpired()
    const text = await res.text()
    let detail: any = text
    try {
      detail = JSON.parse(text)
    } catch {}
    if (res.status === 409) {
      throw new VersionConflictError(detail?.detail ?? detail)
    }
    throw new Error(`${res.status} ${res.statusText}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`)
  }
  noteServerDate(res)
  // 204 / empty body
  if (res.status === 204) return undefined as T
  if (opts.asBlob) return res.blob() as Promise<T>
  return res.json() as Promise<T>
}

// ---------------------------------------------------------------------------
// Generated-ish types (kept in lockstep with backend/models.py manually for
// now; switch to openapi-typescript-codegen when the route surface stabilizes).
// ---------------------------------------------------------------------------

export interface ListResponse<T> {
  /** Rows in THIS page — always equals items.length. */
  count: number
  items: T[]
  /** Rows the filters match in the database, ignoring limit/offset (Build 49).
   *  Only the endpoints that page report a number. `null` is what the ~20
   *  non-paging ops list routes actually put on the wire (the field is always
   *  present in the envelope); `undefined` covers a backend older than Build 49.
   *  Neither is the same as zero. See `lib/pagination.ts` for the arithmetic. */
  total?: number | null
}

/** `ListResponse` for the handful of endpoints backed by a process cache
 *  that can re-serve the SAME response body across many requests (the
 *  Restock Hub list today). `computed_at` (Build 48) is an ISO8601 UTC
 *  timestamp of when THAT body was built server-side, so it may read OLDER
 *  than "now" — that staleness is exactly the number the hub's "computed
 *  Xs ago" chip shows. Build 48 originally put this field on the base
 *  `ListResponse`, which put `computed_at: null` on the wire for every
 *  OTHER (non-caching) list endpoint too — noise dressed up as a field
 *  that looked meaningful. It now only exists on endpoints that actually
 *  have a cache behind it. `undefined` covers a backend older than that fix. */
export interface CachedListResponse<T> extends ListResponse<T> {
  computed_at?: string | null
}

export interface Product {
  uid: string
  product_name: string | null
  asin: string | null
  image_url: string | null
  seller_sku: string | null
  goflow_sku: string | null
  upc_ean: string | null
  drive_folder_id: string | null
  unit_cost: number | null
  landed_unit_cost: number | null
  pack_size: string | null
  pack_family: string | null
  variation_family: string | null
  category_family: string | null
  case_pack_qty: number | null
  goflow_case_pack: number | null
  sale_price_us: number | null
  sale_price_ca: number | null
  fba_fee_us: number | null
  referral_fee_pct: number | null
  buy_box_pct: number | null
  review_count: number | null
  rating: number | null
  bsr_rank: number | null
  category: string | null
  primary_supplier: string | null
  moq: number | null
  hts_code: string | null
  duty_rate_pct: number | null
  production_lead_days: number | null
  unit_weight_kg: number | null
  unit_length_cm: number | null
  unit_width_cm: number | null
  unit_height_cm: number | null
  carton_weight_kg: number | null
  carton_length_cm: number | null
  carton_width_cm: number | null
  carton_height_cm: number | null
  carton_cbm: number | null
  amazon_listing_url: string | null
  bb_uid_cost: number | null
  cogs_1yr: number | null
  reorder_cost: number | null
  goflow_cost: number | null
  cogs_source: string | null
  last_received_po: string | null
  hide_all: boolean
  hide_restock: boolean
  hide_reorder: boolean
  brand: string | null
  active_marketplaces: string[] | null
  // 2026-06-03: joined from md_product_managers (ASIN key). Editable
  // inline on the Products tab; PATCH /api/products/{uid} routes the
  // update to md_product_managers.
  manager: string | null
  // Keepa main + sub category (migration 098): LEFT JOIN on ASIN from
  // bbpro_asin_product_attributes. Read-only (the Keepa product_attributes
  // sync is the only writer); NULL until the next daily sync fills each ASIN.
  main_category: string | null
  sub_category: string | null
  // #76 / F6 provenance (step 23/24/27): where THIS uid's landed cost comes
  // from — 'po' when at least one Received PO prices a real landed cost for
  // it, 'catalog_seed' when only landed_unit_cost (a hand-entered cost-sheet
  // number, no PO backing it) is set, 'none' when neither exists. Mirrors
  // backend ProductOut.landed_cost_basis. null on a response that didn't
  // request it (or an old cached shape).
  landed_cost_basis?: string | null
  // Compliance answers (ops/110, PR 17); null = not answered yet.
  age_min_months?: number | null
  cpsia_warning?: string | null
  batteries_required?: boolean | null
  batteries_included?: boolean | null
  battery_type?: string | null
  battery_count?: number | null
  hazmat_regulation?: string | null
  // The stage of this uid's ACTIVE launch (1..7), its plain name and id (PR 17);
  // null when it is not being launched. Filled by GET /api/products and POST.
  launch_stage?: number | null
  launch_stage_label?: string | null
  launch_id?: number | null
  version: number
  synced_at?: string | null
}

/** Static FALLBACK allowlist for `Product.manager` — the pre-T7/P0 fixed
 *  trio. No live call site should read this directly anymore: the actual
 *  picker options are the live ACTIVE bbpro_operators roster via
 *  `getOperators()` (mirrors the backend's `manager_allowlist()` dynamic
 *  allowlist, products.py:267) so a new hire is assignable with zero
 *  frontend changes. Kept only as an offline/error-path fallback. */
export const PRODUCT_MANAGER_OPTIONS = ['Joseph', 'Mendy', 'Isaac'] as const
export type ProductManagerOption = (typeof PRODUCT_MANAGER_OPTIONS)[number]

// PR 2A/3 — per-(uid, supplier_code) link row.
export interface ProductSupplier {
  uid: string
  supplier_code: string
  supplier_name: string | null
  is_primary: boolean
  seller_sku: string | null
  goflow_sku: string | null
  unit_fob: number | null
  case_pack_qty: number | null
  moq: number | null
  lead_days: number | null
  cogs_freight_pct: number | null
  cogs_duty_pct: number | null
  last_landed_unit_cost: number | null
  last_landed_at: string | null
  last_po_number: string | null
  notes: string | null
  version: number
  source?: string | null
  synced_at?: string | null
}

export interface ProductSupplierPatch {
  seller_sku?: string | null
  goflow_sku?: string | null
  unit_fob?: number | null
  case_pack_qty?: number | null
  moq?: number | null
  lead_days?: number | null
  cogs_freight_pct?: number | null
  cogs_duty_pct?: number | null
  notes?: string | null
}

export async function listProductSuppliers(uid: string): Promise<ListResponse<ProductSupplier>> {
  return opsFetch<ListResponse<ProductSupplier>>(`/api/products/${encodeURIComponent(uid)}/suppliers`)
}

export async function patchProductSupplier(
  uid: string, supplierCode: string, patch: ProductSupplierPatch, ifMatch: number,
): Promise<ProductSupplier> {
  return opsFetch<ProductSupplier>(
    `/api/products/${encodeURIComponent(uid)}/suppliers/${encodeURIComponent(supplierCode)}`,
    { method: 'PATCH', body: patch, ifMatch },
  )
}

export async function setPrimarySupplier(
  uid: string, supplierCode: string,
): Promise<ListResponse<ProductSupplier>> {
  return opsFetch<ListResponse<ProductSupplier>>(
    `/api/products/${encodeURIComponent(uid)}/suppliers/${encodeURIComponent(supplierCode)}/set-primary`,
    { method: 'POST' },
  )
}

// PR 3 — Products detail page envelope.
export interface ProductSupplierMini {
  supplier_code: string
  supplier_name: string | null
  is_primary: boolean
  unit_fob: number | null
  lead_days: number | null
  last_landed_unit_cost: number | null
  last_landed_at: string | null
}

/** PR 2 (one COGS) — one product's facts, every value with its basis.
 *  Mirrors backend product_facts.ProductFactsOut (also GET /api/product-facts/{key}).
 *  Basis tokens and their screen labels: lib/productFacts.ts. */
export interface ProductFactsOut {
  uid: string
  asin: string | null
  title: string | null
  brand: string | null
  manager: string | null
  image_url: string | null
  hidden: boolean | null
  md_version: number | null
  cost: {
    value: number | null
    /** 'po' | 'fifo' | 'fifo_seed' | 'catalog' | 'estimate'. */
    basis: string | null
    label: string | null
    is_estimate: boolean
    po_number: string | null
    as_of: string | null
    /** False when the cost comes from a purchase order (edited there). */
    editable: boolean
  }
  price: { value: number | null; basis: string | null; label: string | null; is_estimate: boolean }
  fees: {
    referral_usd: number | null
    referral_pct: number | null
    referral_basis: string | null
    fba_fee: number | null
    fba_basis: string | null
    label: string | null
    is_estimate: boolean
  }
  break_even_acos: number | null
  break_even_is_estimate: boolean
  /** False when the cost is an estimate — automations then use the preset target. */
  drives_targets: boolean
  target_acos: number | null
  target_acos_source: string | null
  pack_size: number | null
  case_pack_qty: number | null
  unit_dims_cm: number[] | null
  unit_weight_kg: number | null
  carton_dims_cm: number[] | null
  carton_weight_kg: number | null
  stock: {
    fba_available: number | null
    fba_reserved: number | null
    fba_inbound: number | null
    fba_total: number | null
    warehouse_qty: number | null
    sold_via_fba: boolean | null
    as_of: string | null
  }
}

export interface ProductDetailOut {
  product: Product
  suppliers: ProductSupplierMini[]
  /** PR 2 — the same cost/price/fees every surface shows. Null in split mode
   *  (and absent from a pre-PR-2 backend). */
  facts?: ProductFactsOut | null
}

/** PATCH /api/ops/products/{uid} with only `landed_unit_cost` — the product's
 *  catalog landed cost (PR 2). This is the ONE place the PPC product page
 *  writes a cost: md_products, stamped `landed_cost_owner='manual'` so the
 *  hourly sheet sync never overwrites it. `version` is md_products.version
 *  (the facts' md_version) sent as If-Match; a stale one throws
 *  VersionConflictError (409). `null` clears the catalog cost. */
export async function patchCatalogLandedCost(
  uid: string, value: number | null, version: number | null,
): Promise<Product> {
  return opsFetch<Product>(`/api/products/${encodeURIComponent(uid)}`, {
    method: 'PATCH',
    body: { landed_unit_cost: value },
    ifMatch: version ?? undefined,
  })
}

/** GET /api/ops/products/{uid}/detail — the product-page envelope.
 *
 *  `opts.signal` is NOT optional decoration. Bug row 26: ProductDetailPage
 *  built an AbortController and had nowhere to pass it, because this function
 *  took no options — so `ctrl.abort()` cancelled nothing and every discarded
 *  page left its request running to completion. Panes are discarded constantly
 *  in this shell (a tab evicted at the 8-tab cap, a tab closed, the
 *  operator-scope re-key that remounts every open pane), and a page whose uid
 *  does change in place could additionally have an older response resolve last
 *  and win. Callers must pass a signal and abort it on cleanup. */
export async function getProductDetail(
  uid: string,
  opts?: { signal?: AbortSignal },
): Promise<ProductDetailOut> {
  return opsFetch<ProductDetailOut>(`/api/products/${encodeURIComponent(uid)}/detail`, {
    signal: opts?.signal,
  })
}

/** One competitor row on the product page (Build 24). Mirrors backend
 *  models.CompetitorColumnRow.
 *
 *  Three sources, all optional and all NULL until their feed lands:
 *  the watchlist itself (`bbpro_competitor_asins`), Keepa-shaped enrichment
 *  (`bbpro_competitor_attributes` — the table exists but NO WRITER does, in
 *  this build or on the scheduler, so price/rating/reviews/BSR are null for
 *  every row and the card omits those columns entirely), and Jungle Scout's
 *  per-keyword SERP winners (`js_sov_top_asins_daily`, filling from the daily
 *  14:55 UTC run).
 *
 *  `tracked: false` marks a SUGGESTION — an ASIN Jungle Scout keeps returning on
 *  this product's own keywords that nobody has added to the watchlist. It is not
 *  an endorsed competitor and the UI must not render it as one. */
export interface CompetitorRow extends Record<string, unknown> {
  competitor_asin: string
  tracked: boolean
  source: string
  rank: number | null
  note: string | null
  added_at: string | null
  title: string | null
  brand: string | null
  star_rating: number | null
  review_count: number | null
  sales_rank: number | null
  buy_box_price: number | null
  list_price: number | null
  image_url: string | null
  enriched: boolean
  attrs_pulled_at: string | null
  js_keywords: number | null
  js_best_rank: number | null
  js_clicks: number | null
  js_conversion_rate: number | null
  js_pulled_at: string | null
}

/** Envelope for GET /api/ops/products/{uid}/competitors.
 *
 *  `available: false` is never an error — `reason` names which honest empty
 *  state it is so the card can print the right sentence AND the right next
 *  action.
 *
 *  `js_available` / `enrichment_available` both mean DATA IS PRESENT on this
 *  page, not "the table exists". `js_available: false` is a "not yet" the card
 *  says out loud (the daily Jungle Scout run is real and will fill it).
 *  `enrichment_available: false` is a "not built" — it gates the columns
 *  themselves off, because nothing writes that table. */
export interface ProductCompetitorsOut {
  uid: string
  asin: string | null
  marketplace: string
  available: boolean
  reason: 'no_asin' | 'marketplace_unsupported' | 'tables_missing' | 'none_found' | null
  js_available: boolean
  enrichment_available: boolean
  count: number
  items: CompetitorRow[]
}

/** GET /api/ops/products/{uid}/competitors — competitor ASIN columns.
 *
 *  `opts.signal` is required for the same reason getProductDetail's is (bug
 *  row 26): panes in this shell are discarded constantly, and a card whose uid
 *  changes in place can otherwise have an older response resolve last and paint
 *  one product's competitors under another product's name. */
export function getProductCompetitors(
  uid: string,
  marketplace: string,
  opts?: { signal?: AbortSignal },
): Promise<ProductCompetitorsOut> {
  const qs = new URLSearchParams({ marketplace })
  return opsFetch<ProductCompetitorsOut>(
    `/api/products/${encodeURIComponent(uid)}/competitors?${qs}`,
    { signal: opts?.signal },
  )
}

// ── Product Linking (GoFlow SKU<->UID<->marketplace map) ────────────────────
export interface ProductLink {
  uid: string
  product_name: string | null
  marketplace: string | null
  channel: string | null
  store_name: string | null
  sku: string | null
  fnsku: string | null
  status: string | null
  price: number | null
  currency: string | null
  store_page_url: string | null
  title: string | null
  // PR-12: extracted marketplace listing identifier (Amazon ASIN, Walmart
  // item id, Target+ TCIN, eBay item number) + what kind it is. Falls back
  // to sku ('sku_only') for channels with no store_page_url.
  listing_id?: string | null
  listing_id_kind?: string | null
  // Last GoFlow sync touch (owner ask, 2026-07-07 price-matrix column) — powers
  // the "synced N days ago" line in the price-cell tooltip.
  synced_at?: string | null
  /** PR 10 (plan I3) — the ONE status every other surface shows for this
   *  (uid, marketplace): Amazon's own status wins over GoFlow's mirror on
   *  Amazon marketplaces (backend listing_status.load_goflow_status).
   *  `status` above stays GoFlow's raw value. `status_source` says which
   *  source supplied it ('amazon' = the SP-API overlay). */
  resolved_status?: 'active' | 'in_review' | 'inactive' | null
  status_source?: 'amazon' | 'goflow' | null
}

// ── Per-product P&L (md_pnl) ────────────────────────────────────────────────
export interface ProductPnl {
  uid: string
  product_name: string | null
  asin: string | null
  image_url: string | null
  brand: string | null
  bsr_rank: number | null
  marketplace: string
  period_days: number
  units: number | null
  sales: number | null
  refunds: number | null
  refund_units: number | null
  amazon_fees: number | null
  ads: number | null
  cogs: number | null
  uncovered_units: number | null
  promo: number | null
  net_profit: number | null
  est_payout: number | null
  margin: number | null
  roi: number | null
  real_acos: number | null
}

// ── Per-product × per-marketplace P&L MATRIX (Marketplaces hub #3) ──────────
/** The P&L metrics for one (product, marketplace) cell. Costs are positive.
 *  P3 added refund_units/cogs/uncovered_units/est_payout/roi (range-mode only)
 *  so PnlProductsTable has full parity with the legacy ProductPnlView. */
export interface ProductPnlCell {
  units: number | null
  orders: number | null
  sales: number | null
  refunds: number | null
  refund_units: number | null
  ads: number | null
  fees: number | null
  cogs: number | null
  uncovered_units: number | null
  net_profit: number | null
  est_payout: number | null
  margin: number | null
  roi: number | null
  tacos: number | null
  /** Estimated-fee transparency (PR6/ops-046): the portion of `fees` the
   *  engine estimated rather than read from a posted event, and/or a flag
   *  that this cell's fees are estimate-based at all. Optional/either-shape
   *  because the backend field lands independently of this client — support
   *  both a numeric split (`est_fees`) and a boolean flag (`estimated`) until
   *  the exact shape lands; both default to "no signal" (actuals) when absent. */
  est_fees?: number | null
  estimated?: boolean
  /** Real 3-state listing status for this (uid, marketplace) cell — see
   *  ListingStatus. Per-marketplace only; the 'All' rollup cell never has
   *  one (a blended status across marketplaces isn't meaningful). */
  listing_status?: ListingStatus | null
  /** Pending-order honesty badge (C-pending-badge): units/USD-sales on
   *  orders in this window whose order_status is still Pending (unshipped).
   *  ALREADY INCLUDED in units/sales above — nothing is subtracted; this is
   *  a display-only signal computed at READ time, never a stored/folded
   *  number. ~3.5% of pending orders historically cancel and restate to 0
   *  in place as the day settles (~3 days after it ends); late-arriving
   *  orders can nudge it up too. `null` = orders mirror unavailable this
   *  request (pending UNKNOWN); `0` = measured none — never invented. */
  pending_units?: number | null
  /** #52 — distinct CANCELLED orders in this window. Not included in
   *  `orders` (which excludes them since #52) and never in units/sales.
   *  null = the orders mirror can't answer; 0 = measured none. */
  cancelled_orders?: number | null
  pending_sales?: number | null
  /** #81 basis honesty: which stream this cell's numbers came from —
   *  'settled_utc_day' (the default: md_pnl_daily, UTC calendar day) |
   *  'live_pt' (a live per-marketplace overlay: units/sales come straight
   *  from the SAME PT-windowed sales stream the "Today (live)" tile reads;
   *  fees/ads/refunds/net_profit/margin/roi/tacos/est_payout are ALL null —
   *  they settle overnight and must never sit next to today's live PT units
   *  looking final) | 'mixed_pt' (the uid's cells['All'] rollup when at
   *  least one component marketplace cell is live_pt). null (the pre-#81
   *  default) on every response that didn't request live_today. Mirrors
   *  backend ProductPnlCell.basis. */
  basis?: string | null
  /** PR 10 (plan I1/I8) — the same-window ad split, filled only when the
   *  request asked for `adSplit` (null/absent otherwise). Over EXACTLY the
   *  cell's [date_from, date_to]:
   *    ad_sales         Amazon ad-attributed sales (Sponsored Products +
   *                     Display + Brands purchased-product), USD.
   *    ad_spend_amazon  the Amazon part of `ads` (on the 'All' cell the sum
   *                     of this product's Amazon cells), so an all-marketplace
   *                     ACoS never divides Temu spend by Amazon ad sales.
   *    ad_impressions / ad_clicks  SP + SD traffic.
   *  ACoS = ad_spend_amazon ÷ ad_sales; TACoS stays ads ÷ sales. On a Today
   *  window ad_sales stays null — the daily ad reports end yesterday. */
  ad_sales?: number | null
  ad_spend_amazon?: number | null
  ad_impressions?: number | null
  ad_clicks?: number | null
  /** PR 10 (#85) — preliminary ad spend for TODAY (live overlay only):
   *  Amazon Marketing Stream spend so far, allocated to products by ad sales,
   *  USD. `ads` stays null on a live cell (settled spend posts overnight).
   *  `ads_prelim_basis` = 'ams' | 'pacing' | 'ams+pacing'. null when the
   *  stream was unavailable — never a fabricated $0. */
  ads_prelim?: number | null
  ads_prelim_basis?: string | null
  /** PR 22b (B19) — true on a live Today cell: its Net profit / Margin are the
   *  tile's preliminary definition (live sales, estimated fees, today's ad
   *  spend so far) and settle overnight. */
  net_is_prelim?: boolean
  /** PR 22b — true when today's ad spend could not be read: the cell's Net
   *  profit then leaves ad spend out (the tile's own caveat). */
  ads_unknown?: boolean
}
/** PR 22b — what the Today overlay of the P&L matrix knew; drives the status
 *  line above the table. null/absent on every other window. */
export interface PnlMatrixTodayLive {
  pt_date: string
  sales_through_utc: string | null
  ads_prelim: {
    /** Spend is known PER PRODUCT. */
    available: boolean
    /** The marketplace's total spend was read (even if it could not be split
     *  by product — then it sits on the "not yet tied to a product" row). */
    total_available?: boolean
    basis: string | null
    spend_through_utc: string | null
    /** Plain English, already free of feed jargon. */
    note: string | null
  }
  refunds_available: boolean
  unallocated_ads: Record<string, number>
  unmapped_sales: Record<string, { asins: number; sales: number }>
  /** USD of refunds posted today on SKUs no product claims, per marketplace. */
  unmapped_refunds?: Record<string, number>
  marketplaces: string[]
}
export interface PnlMatrixResponse extends ListResponse<ProductPnlMatrixRow> {
  today_live?: PnlMatrixTodayLive | null
}
/** One product row of the P&L matrix; `cells` keyed by marketplace name
 *  (plus 'All' = cross-channel rollup). */
export interface ProductPnlMatrixRow {
  uid: string
  product_name: string | null
  asin: string | null
  image_url: string | null
  brand: string | null
  bsr_rank: number | null
  cells: Record<string, ProductPnlCell>
  /** #76 / F6 (step 23/24): this uid's COGS provenance over the SAME
   *  [date_from, date_to] window the cells above sum — from
   *  cogs_fifo.cogs_basis_by_uid: {po_backed_dollars, catalog_dollars,
   *  uncovered_units, method_mix}. Lets the table badge a cell whose cost is
   *  entirely (or partly) a catalog cost-sheet seed — never PO-backed — the
   *  same way est_fees/uncovered_units already badge every other estimated
   *  leg. null on the legacy fixed-bucket path (no daily md_cogs_daily grain
   *  to derive it from) and on any response that didn't request it. Mirrors
   *  backend ProductPnlMatrixRow.cogs_basis. */
  cogs_basis?: {
    po_backed_dollars: number
    catalog_dollars: number
    uncovered_units: number
    method_mix: Record<string, number>
  } | null
}
export function getPnlMatrix(
  opts: {
    period?: number
    /** Inclusive ISO start/end (YYYY-MM-DD). When both are set the backend sums
     *  the daily-grain rollup over [dateFrom, dateTo] (any range incl. Lifetime,
     *  #11) instead of the fixed `period` bucket. */
    dateFrom?: string
    dateTo?: string
    q?: string
    marketplaces?: string[]
    /** Live-COGS estimate (v2 #23, mirrors getPnlSummary's own `liveCogs`):
     *  when the window includes today, fold an estimated COGS (from
     *  unit_cost) for today's uncovered units into each cell's cogs/
     *  net_profit/uncovered_units, so COGS/U stops reading blank on a Today/
     *  fresh window. No effect on a past-only window or the legacy
     *  fixed-bucket `period` path (no dateFrom/dateTo). */
    liveCogs?: boolean
    /** #81 fix: when [dateFrom, dateTo] is EXACTLY today's Pacific calendar
     *  date, overlay Amazon US/CA cells with the SAME live PT sales stream
     *  the "Today (live)" tile reads, so this table's Today row and the tile
     *  above it can never disagree again. A no-op on any other window. Each
     *  overlaid cell carries `basis: 'live_pt'` (or 'mixed_pt' on the uid's
     *  'All' rollup); every untouched cell gets `basis: 'settled_utc_day'`. */
    liveToday?: boolean
    /** PR 10 (plan I1) — fill each Amazon cell's same-window ad split
     *  (ad_sales / ad_spend_amazon / ad_impressions / ad_clicks), so the
     *  organic/ad split covers the SAME window as Sales. */
    adSplit?: boolean
    signal?: AbortSignal
  },
): Promise<PnlMatrixResponse> {
  const p = new URLSearchParams()
  if (opts.dateFrom && opts.dateTo) {
    p.set('date_from', opts.dateFrom)
    p.set('date_to', opts.dateTo)
  } else if (opts.period != null) {
    p.set('period', String(opts.period))
  }
  if (opts.q) p.set('q', opts.q)
  if (opts.marketplaces?.length) p.set('marketplaces', opts.marketplaces.join(','))
  if (opts.liveCogs) p.set('live_cogs', 'true')
  if (opts.liveToday) p.set('live_today', 'true')
  if (opts.adSplit) p.set('ad_split', 'true')
  return opsFetch<PnlMatrixResponse>(`/api/products/pnl-matrix?${p}`, {
    signal: opts.signal,
  })
}

// ── Headline P&L grid — pnl-columns (Sellerboard redesign) ──────────────────
// METRIC rows × time-PERIOD columns (+ a single Total). The inverse axis of
// pnl-matrix. Backed by GET /api/ops/products/pnl-columns. Mirrors the backend
// Pydantic models (PnlBucket / PnlColumnRow / PnlColumnsResponse).

/** One time-period COLUMN of the grid. `partial` flags the still-accumulating
 *  current bucket (fees settle ~10 days post-order). Newest-first; the Total
 *  column is NOT a bucket. */
export interface PnlBucket {
  key: string          // '2026-06' | '2026-W23' | '2026-06-26'
  label: string        // human header ('May 2026', '16–22 Jun', '26 Jun')
  start: string        // inclusive ISO start
  end: string          // inclusive ISO end
  partial: boolean
}
/** One per-marketplace slice of a money/cost metric on the All-Marketplaces
 *  aggregate grid (the "expand to a per-marketplace breakdown" payload). Only
 *  present on the aggregate (marketplace='All') grid when `breakdown=true`, and
 *  only for the additive money rows. `values` (PR 22) is the marketplace's
 *  value per period, aligned 1:1 with `PnlColumnsResponse.buckets`; absent on
 *  a response from before PR 22 (the grid then shows "—" per period). */
export interface PnlMarketplaceShare {
  marketplace: string
  total: number | null
  values?: (number | null)[] | null
  /** PR 22 review — fee row only: the estimated part of each period value
   *  (1:1 with `values`) and of `total`, ALREADY INCLUDED in them. Absent
   *  when the slice has no estimate in range. */
  estimated_values?: (number | null)[] | null
  estimated_total?: number | null
}
/** One METRIC row across all buckets + a Total. `values` aligns 1:1 with
 *  `PnlColumnsResponse.buckets`; an entry is null for an empty bucket, a
 *  not-yet-fed gap metric, or an undefined ratio. `kind` drives formatting;
 *  `polarity` the heatmap direction; `expandable` flags a waterfall row.
 *  `per_marketplace` (aggregate + breakdown only, money rows only) carries the
 *  metric's split across the marketplaces with data, per period and in total.
 *  `components` (fee row only today) carries FULL child metric rows — the
 *  per-fee-type breakdown (Referral / FBA fulfillment / Storage / Inbound
 *  placement & transport / Other / Refund fee credit / Estimated, not yet
 *  split), whose totals sum exactly to the parent; absent/null until the
 *  first post-045 pnl rebuild and on channels with no fee detail. */
export interface PnlColumnRow {
  metric_key: string
  label: string
  kind: 'int' | 'money' | 'pct' | 'cost'
  polarity: 'good' | 'bad' | 'neutral'
  expandable: boolean
  values: (number | null)[]
  total: number | null
  per_marketplace?: PnlMarketplaceShare[] | null
  components?: PnlColumnRow[] | null
  /** #109: the ESTIMATED portion of each value (1:1 with `values`) and of
   *  `total` — fees for recent orders Amazon hasn't reported yet, ALREADY
   *  INCLUDED in values/total (never add it again). Set only on fee child
   *  rows carrying an estimate in the range (Referral, FBA, the "not yet
   *  split" remainder); null/absent everywhere else. */
  estimated_values?: (number | null)[] | null
  estimated_total?: number | null
}
/** Per-marketplace data-coverage boundaries over md_pnl_daily (ops/046). Each
 *  `*_through` is the last ISO date that component actually has data for that
 *  marketplace; `ads_from` is the first ads date; `estimated_from` is the
 *  first day carrying engine-estimated fees/refunds AFTER the channel's last
 *  fully-settled day (PR 22: a trailing boundary; on 'All' the boundary of
 *  the channels with a live fee feed, the rest in `estimating_channels`). All null when the
 *  component has no data at all. Drives the "fees estimated through <date>" /
 *  "no fee data" pills (PR6, Phase 5).
 *
 *  `dark_since` (phase 17, dark-channel): the last sale date for a channel
 *  gone quiet well past ITS OWN normal cadence (per-channel derived
 *  threshold, not a fixed day count). Null for a healthy channel, a channel
 *  inside its own normal quiet stretch, a channel with no sales history at
 *  all (`sales_through === null` — that's "no data", never "dark"), and the
 *  'All' rollup. Drives the "dark since <date>" pill. */
export interface PnlCoverage {
  marketplace: string
  sales_through: string | null
  fees_through: string | null
  refunds_through: string | null
  ads_through: string | null
  ads_from: string | null
  estimated_from: string | null
  /** PR 22 review — the channel's first day of real fees when it carries
   *  estimates BEFORE that day (Walmart's months before its fee feed began):
   *  fees before it are estimates too. Null otherwise / on older responses. */
  estimated_before?: string | null
  /** Set (to the last real-fee date) only when the channel HAS real fee
   *  history but that boundary has gone stale beyond normal settlement lag —
   *  e.g. Walmart's real fee feed stops at 2026-06-30 while its sync job
   *  still reports 'ok'. Distinct from `estimated_from`/`fees_through ===
   *  null`: this is "the real feed died", not "still settling" or "never had
   *  fee data". Null otherwise. (Phase 17 pnl-honesty, additive.) */
  fees_stale_since: string | null
  dark_since: string | null
  /** PR 22 — 'All' only: the channels whose fees are estimates outside the
   *  live-feed boundary `estimated_from` reports — `stale` (their real fee
   *  feed stopped; `since` = the first estimated day after it) or `no_feed`
   *  (no real fee feed; `since` null). Empty / absent elsewhere. */
  estimating_channels?: EstimatingChannel[] | null
}
export interface EstimatingChannel {
  marketplace: string
  since: string | null
  /** Fees before this day are estimates too (the channel's
   *  `estimated_before`); null / absent when there is no such span. */
  before?: string | null
  reason: 'stale' | 'no_feed' | 'before_feed' | string
}
export interface PnlColumnsResponse {
  buckets: PnlBucket[]
  rows: PnlColumnRow[]
  /** The requested marketplace's coverage boundaries (Phase 5); null when
   *  md_pnl_daily isn't synced yet. */
  coverage?: PnlCoverage | null
  /** #55: the exact inclusive days every cell and the Total sum over. A
   *  bucket's own start/end are whole weeks/months, so a custom range's
   *  first/last bucket can stick out past these — the fee drawer clamps to
   *  them. Absent on an empty grid (or an older backend). */
  range_start?: string | null
  range_end?: string | null
}
export function getPnlColumns(
  opts: {
    /** month_12 / week_13 / day_30 / custom (default month_12 on the backend). */
    preset?: string
    /** month / week / day; for custom, auto by span unless overridden. */
    grain?: string
    /** Inclusive ISO start/end (YYYY-MM-DD); required when preset=custom. */
    dateFrom?: string
    dateTo?: string
    /** EXACTLY one marketplace (or 'All'); never multi — avoids double-count. */
    marketplace?: string
    q?: string
    /** Aggregate ('All') only: attach a per-marketplace split to the money rows
     *  so a row can expand to a per-marketplace breakdown. */
    breakdown?: boolean
    signal?: AbortSignal
  } = {},
): Promise<PnlColumnsResponse> {
  const p = new URLSearchParams()
  if (opts.preset) p.set('preset', opts.preset)
  if (opts.grain) p.set('grain', opts.grain)
  if (opts.dateFrom) p.set('date_from', opts.dateFrom)
  if (opts.dateTo) p.set('date_to', opts.dateTo)
  if (opts.marketplace) p.set('marketplace', opts.marketplace)
  if (opts.q) p.set('q', opts.q)
  if (opts.breakdown) p.set('breakdown', 'true')
  return opsFetch<PnlColumnsResponse>(`/api/products/pnl-columns?${p}`, {
    signal: opts.signal,
  })
}

// ── P&L fee drawer — pnl-fee-history (#55) ────────────────────────────────
// One fee's history, what it is made of, and who it belongs to. Backed by
// GET /api/ops/products/pnl-fee-history; mirrors the backend Pydantic models
// (PnlFeeHistoryPoint / PnlFeeSubtype / PnlFeeByProduct / PnlFeeHistoryOut).
// Money is USD and POSITIVE = cost (the refund fee credit reads negative).

/** Which fee: the grid's fee row (`all`) or one of its child rows. */
export type PnlFeeKey =
  | 'all' | 'fee_referral' | 'fee_fba' | 'fee_storage' | 'fee_inbound'
  | 'fee_other' | 'fee_refund_credit' | 'fee_estimated'

/** One period of the history: a calendar month or a day. `actual` is what
 *  Amazon reported, `estimated` ClickPay's estimate for fees not reported
 *  yet; `total` is both. */
export interface PnlFeeHistoryPoint {
  key: string        // '2026-08' | '2026-08-14'
  label: string      // 'Aug 2026' | '14 Aug'
  start: string      // inclusive ISO start
  end: string        // inclusive ISO end
  actual: number
  estimated: number
  total: number
}
/** One line of "What's in it" (e.g. Monthly storage, Inbound placement). */
export interface PnlFeeSubtype {
  fee_type: string
  label: string
  amount: number
}
/** One product in the drawer's "By product" table. `reported` = the fee
 *  charged to the product (what the marketplace reported plus ClickPay's
 *  estimate for fees not reported yet); `estimated` = that estimate part;
 *  `allocated` = its share of the account-level amount, split by ClickPay
 *  at read time (shown, never booked). */
export interface PnlFeeByProduct {
  uid: string
  product_name: string | null
  reported: number
  /** Optional for an older backend that didn't send it. */
  estimated?: number
  allocated: number
  total: number
}
export interface PnlFeeHistory {
  fee: string
  label: string
  /** Plain-English explainer for the fee (the drawer's description). */
  help: string
  marketplace: string
  date_from: string
  date_to: string
  history: 'month' | 'day'
  /** Oldest → newest: 13 calendar months ending at date_to's month, or each
   *  day of the last ≤90 days up to date_to. */
  series: PnlFeeHistoryPoint[]
  /** The clicked window [date_from, date_to] — not the whole series. */
  window_total: number
  window_estimated: number
  /** The part of window_total charged to the account rather than a product
   *  (0 under operator scope). */
  account_total: number
  subtypes: PnlFeeSubtype[]
  /** Top 50 by total; `by_product_more` counts the rest. */
  by_product: PnlFeeByProduct[]
  by_product_more: number
  /** How the account-level amount was split; null when nothing to split. */
  allocation_note: string | null
  /** PR 22b (B18): set when (nearly) all account-level fees sit on one run
   *  day — the feed dated them with its own day. null/absent otherwise. */
  dating?: { latest_day: string; share: number; note: string } | null
}
export interface PnlFeeHistoryParams {
  fee: PnlFeeKey
  /** EXACTLY one marketplace or 'All' (the rollup rows, never a sum). */
  marketplace: string
  /** Inclusive ISO start/end of the clicked cell (YYYY-MM-DD). */
  dateFrom: string
  dateTo: string
  history: 'month' | 'day'
  /** The P&L page's product search — the drawer then covers exactly the
   *  products the clicked (searched) cell does, with no account-level row. */
  q?: string
}
/** The query string for `getPnlFeeHistory` — split out so the request a
 *  drawer makes can be asserted without a network. */
export function pnlFeeHistoryQuery(opts: PnlFeeHistoryParams): string {
  const p = new URLSearchParams()
  p.set('fee', opts.fee)
  p.set('marketplace', opts.marketplace)
  p.set('date_from', opts.dateFrom)
  p.set('date_to', opts.dateTo)
  p.set('history', opts.history)
  if (opts.q) p.set('q', opts.q)
  return p.toString()
}
export function getPnlFeeHistory(
  opts: PnlFeeHistoryParams & { signal?: AbortSignal },
): Promise<PnlFeeHistory> {
  return opsFetch<PnlFeeHistory>(`/api/products/pnl-fee-history?${pnlFeeHistoryQuery(opts)}`, {
    signal: opts.signal,
  })
}

// ── Per-marketplace fee/data-coverage boundaries (ops/046, Phase 5 badges) ───
/** GET /api/ops/products/pnl-coverage — one PnlCoverage row per marketplace
 *  (including 'All'). Drives the "fees estimated through <date>" pill and the
 *  "no fee data" variant for channels with no fee feed at all
 *  (fees_through === null). Display-only; no params. */
export function getPnlCoverage(
  opts: { signal?: AbortSignal } = {},
): Promise<ListResponse<PnlCoverage>> {
  return opsFetch<ListResponse<PnlCoverage>>('/api/products/pnl-coverage', {
    signal: opts.signal,
  })
}

// ── TOTAL daily sales/units trend (Marketplaces hub Products tracker, R5-2) ──
/** One day of the TOTAL (organic + PPC) sales/units trend. */
export interface PnlTrendPoint {
  date: string
  sales: number | null
  units: number | null
  /** Daily ad spend = SUM(md_pnl_daily.ads) at the SAME scope as sales/units
   *  (marketplace / 'All' rollup / uid drilldown). Null when the ads column is
   *  unsynced or all-NULL for the day — the PPC overlay must stay null-tolerant
   *  (connectNulls) rather than plot a false 0. */
  ad_spend?: number | null
  /** T4 (Wave 3 B6 charts overhaul) — daily P&L composition, same scope +
   *  null-tolerance contract as ad_spend above. amazon_fees/refunds fold in
   *  the engine's estimated component (ops/046), so they reconcile with
   *  net_profit (which already includes the estimate). Powers
   *  CombinedPerfChart's stacked-P&L primary series (P1-7 removed the other
   *  reader, TacosMarginTrendChart). */
  amazon_fees?: number | null
  cogs?: number | null
  refunds?: number | null
  net_profit?: number | null
}
/** Daily sales/units series over md_pnl_daily for the Products tracker chart.
 *  Aggregate by default; pass `uid` to drill to one product. `marketplace`
 *  undefined/'all' reads the 'All' cross-channel rollup. */
export function getPnlTrend(
  opts: {
    dateFrom: string
    dateTo: string
    marketplace?: string
    /** §4.8 — CSV IN-set of component marketplaces (PR-A's `marketplaces`
     *  contract on `pnl-trend`): every SELECT column there is a SUM, additive
     *  over the set. Mutually exclusive with `marketplace` — pass one or the
     *  other. Ships backend-complete but currently frontend-dark (no caller
     *  yet passes it; a future "by date" toggle on the Amazon tab's
     *  multi-selection can consume it). */
    marketplaces?: string[]
    uid?: string
    /** Marketplace product-filter scope (multi-product) — supersedes `uid` on
     *  the backend when both are present. Absent/empty = whole marketplace. */
    uids?: string[]
    signal?: AbortSignal
  },
): Promise<ListResponse<PnlTrendPoint>> {
  const p = new URLSearchParams()
  p.set('date_from', opts.dateFrom)
  p.set('date_to', opts.dateTo)
  if (opts.marketplace && opts.marketplace !== 'all') p.set('marketplace', opts.marketplace)
  if (opts.marketplaces && opts.marketplaces.length > 0) p.set('marketplaces', opts.marketplaces.join(','))
  if (opts.uid) p.set('uid', opts.uid)
  if (opts.uids && opts.uids.length > 0) p.set('uids', opts.uids.join(','))
  return opsFetch<ListResponse<PnlTrendPoint>>(`/api/products/pnl-trend?${p}`, {
    signal: opts.signal,
  })
}

// ── ONE P&L summary over a window — Sellerboard tile cards (Products tab) ────
/** One P&L summary over an inclusive [date_from, date_to] window, for exactly
 *  one marketplace ('All' rollup or one channel). Backed by
 *  GET /api/ops/products/pnl-summary; mirrors the backend PnlSummaryOut.
 *  Costs (ads/refunds/amazon_fees/cogs) are POSITIVE. `orders` (PR1) is a
 *  genuine COUNT(DISTINCT order_id) for the window — it no longer mirrors
 *  `units`; a multi-item order counts once, so per-product order counts don't
 *  sum to the marketplace total. Ratios (margin/real_acos) are ratio-of-sums
 *  or null. Reconciles with the headline grid's Total for the same {window, mp}. */
export interface PnlSummary {
  date_from: string
  date_to: string
  marketplace: string
  sales: number
  units: number
  orders: number
  refunds: number
  refund_units: number
  ads: number
  amazon_fees: number
  cogs: number
  promo: number
  net_profit: number
  est_payout: number
  gross_profit: number
  margin: number | null
  real_acos: number | null
  /** Item 2 — freshness stamp: ISO UTC instant of `MAX(md_pnl_daily._synced_at)`
   *  over the window (the last hourly P&L rebuild that touched it). Null when
   *  the table has no rows for the window (empty period) — never fall back to
   *  the client clock; that states when we asked, not how fresh the answer is. */
  data_as_of?: string | null
  /** Live-COGS (Today tile): portion of `cogs` estimated from unit_cost for
   *  today's not-yet-costed units; `uncovered_units` = units still lacking any
   *  basis. Both 0 unless `liveCogs` was requested over a window incl. today. */
  cogs_estimated?: number
  uncovered_units?: number
  /** #76 / F6 (step 24): sibling of `cogs_estimated` above, but a DIFFERENT
   *  kind of estimate: the portion of `cogs` (ALREADY included there — this
   *  is a disclosure, never a fold) that was priced entirely off the catalog
   *  cost-sheet (no PO landed cost, or only a pre-PO deficit seed) rather
   *  than a real received PO. 0 when every dollar shown is PO-backed FIFO
   *  cost. Mirrors backend PnlSummaryOut.cogs_catalog_estimated. */
  cogs_catalog_estimated?: number
  /** Estimated fees/refunds (ops/046): the portion of `amazon_fees`/`refunds`
   *  the engine estimated (Amazon deferred-settlement window, or configured
   *  channel commissions) rather than read from posted events. ALREADY
   *  INCLUDED in amazon_fees/refunds/net_profit above — these exist purely so
   *  a tile/table can badge "includes $X estimated fees" honestly. 0 when
   *  everything shown is actuals. */
  est_fees?: number
  est_refunds?: number
  /** Item 3 (2026-07-16): the two P&L legs the marketplace-bar tooltip was
   *  missing (Temu ships a shipping-revenue leg; several marketplaces carry
   *  shipping_cost). Cost-column semantics (0 when absent, never null) —
   *  `sales + shipping_revenue - refunds - promo - amazon_fees - ads - cogs -
   *  shipping_cost === net_profit` holds exactly. Mirrors the backend
   *  PnlSummaryOut fields of the same name. */
  shipping_cost: number
  shipping_revenue: number
  /** #55: the account-level Amazon fees (storage, inbound placement, the
   *  seller subscription …) ALREADY INCLUDED in `amazon_fees`/`net_profit`
   *  above — surfaced only for the fee line's "includes $X of account-level
   *  fees" tooltip. 0 when the response is products-only (a `uids` filter or
   *  operator scope); absent on an older cached response. */
  account_fees?: number
  /** P1-3 marketplace bars: set ONLY when the request asked marketplace='All'
   *  AND breakdown=true — a per-marketplace split of THIS SAME response.
   *  Summing every field here across the list reproduces the response's own
   *  totals above, by construction (one query, two views — see the backend
   *  PnlMarketplaceBreakdown docstring). undefined everywhere else. */
  breakdown?: PnlMarketplaceBreakdown[]
  /** §4.8 — echo of the applied `marketplaces` IN-set (PR-A's contract):
   *  null on every legacy single-`marketplace` request; the exact requested
   *  list when `marketplaces` was used. `marketplace` above becomes the
   *  joined CSV string in that case (never claims 'All'). */
  marketplaces?: string[] | null
  /** Pending-order honesty badge (C-pending-badge): units/USD-sales, over
   *  this SAME {window, marketplace(s), uids, scope}, on orders whose
   *  order_status is still Pending (unshipped). ALREADY INCLUDED in
   *  units/sales above — a display-only signal, never subtracted, never a
   *  stored/folded number (computed at READ time). `null` = orders mirror
   *  unavailable this request (pending UNKNOWN); `0` = measured none —
   *  never invented either direction. Mirrors the backend
   *  ProductPnlCell.pending_units docstring for the drift-down mechanism
   *  (~3.5% of pending orders cancel in place; a day settles ~3 days
   *  after it ends). */
  pending_units?: number | null
  pending_sales?: number | null
  /** Day-basis honesty: which calendar day this row is bucketed on.
   *  'settled_utc_day' (every settled pnl-summary payload — md_pnl_daily's
   *  grain is the UTC calendar day, NOT the Pacific day Seller Central/
   *  Sellerboard use) | 'live_pt' | 'settled_pt' | 'mixed_pt' (pnl-today-live
   *  payloads only — see PnlTodayLive). undefined on an old cached response
   *  shape; treat the same as 'settled_utc_day'. */
  basis?: string
}
/** One marketplace's slice of a `breakdown=true` pnl-summary response (P1-3
 *  marketplace bars) — the SAME fields as PnlSummary, minus the window/
 *  marketplace-scope echo (this row's own `marketplace` name IS the scope). */
export interface PnlMarketplaceBreakdown {
  marketplace: string
  sales: number
  units: number
  /** Today-integrity plan Phase 3 (Bug 2, owner decision Q1): null on a LIVE
   *  (basis 'live_pt') row — there is no honest PT-window distinct-order
   *  count (orders_daily has no sub-day order-id grain), so a live row never
   *  shows a real settled count sitting next to live-stream units. A settled
   *  ('settled_pt' / plain pnl-summary breakdown) row keeps its genuine
   *  COUNT(DISTINCT order_id). Render as a dash + tooltip, never as 0. */
  orders: number | null
  refunds: number
  refund_units: number
  ads: number
  amazon_fees: number
  cogs: number
  promo: number
  net_profit: number
  est_payout: number
  gross_profit: number
  margin: number | null
  real_acos: number | null
  cogs_estimated: number
  uncovered_units: number
  est_fees: number
  est_refunds: number
  /** Item 3 (2026-07-16): see PnlSummary's field of the same name — the two
   *  P&L legs (shipping cost/revenue) the marketplace-bar tooltip's mini-P&L
   *  needed to reconcile Sales -> Net for Temu (shipping-revenue leg) and
   *  Target+ (large shipping_cost). */
  shipping_cost: number
  shipping_revenue: number
  /** Today-PT-unify: mirrors the backend's new PnlMarketplaceBreakdown.basis field
   *  — 'settled' for the ordinary settled pnl-summary path (its default), or
   *  'live_pt'/'settled_pt' when this row came from the pnl-today-live 'All' fan-out
   *  (Amazon US/CA get live_pt, every other channel gets settled_pt). Lets the
   *  marketplace-bar tooltip badge each row's basis honestly instead of assuming
   *  every row in a response shares one basis. */
  basis: string
  /** Today-integrity plan Phase 4 fix (Step 1): true when this row's LIVE
   *  sales leg failed and the whole channel was zeroed out (a real
   *  undercount, not a genuine $0 day) — pnl-today-live subset/'All' rows
   *  only; absent/false on every plain pnl-summary breakdown row. */
  degraded?: boolean
  degraded_note?: string | null
  /** Today-integrity plan Fix 3 (ads honesty invisible in the UI): this
   *  row's own ads-leg health — an Amazon US/CA row's traffic/pacing blend;
   *  a settled Temu row's fold figure (true, `md_pnl_daily.ads`); or a
   *  settled row for a channel with no ad-spend feed at all (false by design).
   *  False means `ads`/`real_acos` here are NOT a trustworthy zero.
   *  Defaults true on the wire for every plain pnl-summary breakdown row
   *  (ads there is always a real, settled figure). */
  ads_available?: boolean
}
export function getPnlSummary(
  opts: {
    /** Inclusive ISO start/end (YYYY-MM-DD). Both required. */
    dateFrom: string
    dateTo: string
    /** EXACTLY one marketplace (or 'All'); never multi — avoids double-count. */
    marketplace?: string
    /** §4.8 — CSV IN-set of component marketplaces (PR-A's `marketplaces`
     *  contract on `pnl-summary`): sums this exact set, ratios re-derived
     *  ratio-of-sums. Mutually exclusive with `marketplace` — pass one or the
     *  other (never both); 'All' is rejected inside the set (double-count
     *  guard) — omit the param for the aggregate instead. Empty/absent =
     *  legacy single-marketplace behavior, byte-identical to today. */
    marketplaces?: string[]
    /** Live-COGS profit: when the window includes today, fold an estimated COGS
     *  (from unit_cost) for today's uncovered units into net_profit so the Today
     *  tile auto-updates as real costs land. No effect on past-only windows. */
    liveCogs?: boolean
    /** P1-3 marketplace bars: on the 'All' aggregate only, attach a per-
     *  marketplace split of this SAME response (`PnlSummary.breakdown`) — the
     *  single source of truth the marketplace bar chart renders from, instead
     *  of a second, potentially-drifted query. No-op on a single-marketplace
     *  request. */
    breakdown?: boolean
    /** Marketplace product-filter scope — CSV of product uids sent as `uids`
     *  when non-empty; absent/empty = whole marketplace (byte-identical to
     *  today). */
    uids?: string[]
    signal?: AbortSignal
  },
): Promise<PnlSummary> {
  const p = new URLSearchParams()
  p.set('date_from', opts.dateFrom)
  p.set('date_to', opts.dateTo)
  if (opts.marketplace) p.set('marketplace', opts.marketplace)
  if (opts.marketplaces && opts.marketplaces.length > 0) p.set('marketplaces', opts.marketplaces.join(','))
  if (opts.liveCogs) p.set('live_cogs', 'true')
  if (opts.breakdown) p.set('breakdown', 'true')
  if (opts.uids && opts.uids.length > 0) p.set('uids', opts.uids.join(','))
  return opsFetch<PnlSummary>(`/api/products/pnl-summary?${p}`, {
    signal: opts.signal,
  })
}

// ── "Today (live)" — Pacific-day, Sellerboard/Seller-Central-parity P&L ─────
// GET /api/ops/products/pnl-today-live — accepts 'Amazon US' | 'Amazon CA' | any
// other hub marketplace | 'All'. On 'All' the backend fans out every channel
// internally (Amazon US/CA get the 5 live legs, every other channel incl. Amazon
// MX gets a settled-to-PT leg) and returns basis='mixed_pt' with headline ==
// Σ breakdown by construction — never a second query. Pass breakdown=true to get
// the per-marketplace split (`.breakdown`, basis per-row); it's a documented
// no-op on single-marketplace requests. Each leg (sales/cogs/fees/ads/refunds)
// degrades independently; only sales is must-have (missing sales → 503, and the
// caller should fall back to getPnlSummary). `legs` is {} on the 'All' response
// (per-channel legs aren't meaningful at the rollup level) — always optional-
// chain reads of `.legs`. See PnlPeriodTiles.tsx's isLiveTodayCard for the gate.

/** One live-leg's degradation state: whether the component came back at all,
 *  what basis it was computed on (e.g. 'bq_stream', 'financial_events_posted',
 *  'ams+pacing'), how fresh it is, and an optional human note for when it's
 *  degraded/excluded (e.g. "ad spend excluded"). */
export interface PnlLiveLeg {
  available: boolean
  basis: string
  as_of?: string
  note?: string
}

/** `pnl-today-live` response — a `PnlSummary` (same shape the tile already
 *  renders) PLUS live-specific provenance: the resolved Pacific-day UTC
 *  window, when each leg was computed, the ad-spend blend basis, the fee
 *  source mix (how many ASINs' fees came from real SP-API rates vs a seed
 *  default vs a manual override vs an outright assumption), and per-leg
 *  availability/basis/notes for the "More" panel + badges.
 *
 *  Today-integrity plan Phase 3 (Bug 2, owner decision Q1): `orders` widens
 *  to `number | null` here (null on a live basis 'live_pt' channel/rollup —
 *  no honest PT-window order-id source exists). A plain `extends PnlSummary`
 *  can't express that override (TS interface extension requires every member
 *  to remain assignable to the base — `number | null` is NOT assignable to
 *  `number`), so this is `Omit<PnlSummary, 'orders'> & {...}` instead of
 *  `interface ... extends PnlSummary`. */
export type PnlTodayLive = Omit<PnlSummary, 'orders'> & {
  orders: number | null
  /** 'live_pt' (single Amazon US/CA request) | 'settled_pt' | 'mixed_pt' (the
   *  'All' fan-out — Amazon US/CA live, every other channel settled-to-PT).
   *  Backend's PnlTodayLiveOut.basis is typed `str` with no enum, so `string`
   *  is the honest match rather than a closed literal union. */
  basis: string
  window_start_utc: string
  window_end_utc: string
  as_of: string
  spend_through_utc?: string
  spend_basis?: string
  /** Today-integrity plan Phase 2 (Bug 4 — honest sales coverage): the sales
   *  stream is only ever fresh through the last CLOSED hour Amazon has
   *  emitted (the current in-progress hour is simply ABSENT, not late) — so
   *  `as_of` above ("assembled at") overstates freshness for the sales
   *  figure specifically. ISO UTC instant of the freshest landed hour + 1h
   *  (clamped to `window_end_utc`), or null when the stream had no rows at
   *  all in the window. For 'All'/a `marketplaces` subset this is the
   *  EARLIEST across every contributing LIVE channel (most conservative),
   *  null if no live channel contributed. `as_of` keeps its original
   *  "assembled at" meaning — this is a new, additive field, never a
   *  replacement. */
  sales_through_utc?: string | null
  fee_source_mix: {
    sp_api: number
    default: number
    manual: number
    assumed: number
  }
  /** Per-leg health map for a SINGLE Amazon marketplace (Amazon US/CA). It is
   *  an EMPTY object ({}) for the 'All' rollup (basis 'mixed_pt') and for a
   *  settled non-Amazon channel — so every key is optional. Read defensively
   *  (`live.legs?.refunds?.available`), never `live.legs.sales` unguarded. */
  legs: Partial<Record<'sales' | 'cogs' | 'fees' | 'ads' | 'refunds', PnlLiveLeg>>
  /** Today-integrity plan Phase 4 fix (Step 1): true when ANY live (Amazon
   *  US/CA) channel folded into this response had its sales leg fail and
   *  got zeroed — the headline/sum is then a real undercount, not a genuine
   *  low-sales instant. Absent/false on a healthy response. */
  degraded?: boolean
  /** Today-integrity plan Fix 3 (ads honesty invisible in the UI): rollup-
   *  level equivalent of `PnlMarketplaceBreakdown.ads_available` above, and
   *  the 'All'/subset counterpart of `legs.ads.available` (only meaningful
   *  on a single Amazon US/CA request — `legs` is `{}` on this shape).
   *  False when a contributing LIVE Amazon channel's ad-spend blend failed
   *  this refresh — the headline `ads`/`real_acos` are then an undercount
   *  of real spend, never a settled non-Amazon channel's permanent
   *  no-feed state. Absent/undefined treated as healthy (true) — every
   *  legacy response shape is unaffected. */
  ads_available?: boolean
}

export function getPnlTodayLive(
  opts: {
    /** 'Amazon US' | 'Amazon CA' | any other hub marketplace | 'All' — the
     *  backend widened this from Amazon-US/CA-only; 'All' fans out every
     *  channel server-side (see the module comment above). Omit when passing
     *  `marketplaces` (defaults the wire param to 'All', the backend's
     *  documented pass-through value alongside an explicit subset — NOT a
     *  conflict, see `marketplaces` below). */
    marketplace?: string
    /** Today-integrity plan Phase 4 (Bug 1 — additive multi-marketplace
     *  selection): CSV IN-set of component marketplaces (ANY combination,
     *  Amazon MX included — directly requestable since Phase 4-BE widened
     *  `_VALID_MARKETPLACES`). The backend sums this EXACT set server-side
     *  (`_assemble_subset`) — headline == Σ breakdown by construction, one
     *  request, no client-side summing. Mutually exclusive with a non-'All'
     *  `marketplace` (mirrors pnl-summary's own contract); omit `marketplace`
     *  entirely when using this (or pass 'All', its conventional pass-through
     *  value). A single-element array is legal and matches the plain
     *  `marketplace=<that name>` request exactly. */
    marketplaces?: string[]
    /** Per-marketplace split of this response (`.breakdown`, basis per-row) —
     *  meaningful only on 'All', a `marketplaces` subset, or any other
     *  multi-channel rollup; a documented no-op on a single-marketplace
     *  request. */
    breakdown?: boolean
    /** Marketplace product-filter scope — CSV of product uids sent as `uids`
     *  when non-empty; absent/empty = whole marketplace (byte-identical to
     *  today). Scopes sales/cogs/fees via the ASIN bridge and (per owner
     *  decision #2) also ads/refunds via LIVE per-product attribution. */
    uids?: string[]
    /** Day-basis WP2: 0 (default, omitted from the wire) is TODAY's
     *  in-progress Pacific day — byte-identical to before this option
     *  existed. 1 is YESTERDAY — the complete prior Pacific calendar day
     *  (pacing excluded from the ad-spend blend server-side). Sent as the
     *  `day_offset` query param only when 1 (never sends `day_offset=0`). */
    dayOffset?: 0 | 1
    signal?: AbortSignal
  },
): Promise<PnlTodayLive> {
  const p = new URLSearchParams()
  p.set('marketplace', opts.marketplace ?? 'All')
  if (opts.marketplaces && opts.marketplaces.length > 0) p.set('marketplaces', opts.marketplaces.join(','))
  if (opts.breakdown) p.set('breakdown', 'true')
  if (opts.uids && opts.uids.length > 0) p.set('uids', opts.uids.join(','))
  if (opts.dayOffset) p.set('day_offset', String(opts.dayOffset))
  return opsFetch<PnlTodayLive>(`/api/products/pnl-today-live?${p}`, {
    signal: opts.signal,
  })
}

// ── TACoS split (cp_uid_tacos, migration 074) — Products tracker, R5-2 ───────
// NOTE: the /api/tacos router is mounted on the MAIN AdBetter app, NOT under
// /api/ops, so it must be reached at its ABSOLUTE path — bypassing the ops
// `opsFetch()` helper's `/api/X → /api/ops/X` rewrite (which would 404). One view
// scan per (marketplace, window); the table joins the {uid: row} map by uid.
export interface TacosRow {
  uid: string
  marketplace: string
  window_days: number
  ad_spend: number | null
  ads_sales: number | null
  acos: number | null
  total_sales: number | null
  total_units: number | null
  tacos: number | null
  sales_window_complete: boolean
  ads_window_complete: boolean
  currency: string
  /** Ad CTR support (additive) — raw Amazon Ads impressions/clicks for this
   *  uid over the SAME (marketplace, window). Amazon-only (US/CA); null on
   *  any other marketplace or a uid with no ad activity in the window. */
  ad_impressions?: number | null
  ad_clicks?: number | null
}
export interface TacosBulkResponse {
  marketplace: string
  window_days: number
  count: number
  items: Record<string, TacosRow>
}
/** Per-uid TACoS map for ONE (marketplace, window). Hits the main-app endpoint
 *  directly (NOT under /api/ops). Degrades to an empty map on any failure so a
 *  missing view never breaks the tracker. */
export async function getTacosBulk(
  marketplace: string,
  window: 30 | 90 | 365,
  signal?: AbortSignal,
): Promise<Record<string, TacosRow>> {
  const p = new URLSearchParams({ marketplace, window: String(window) })
  try {
    const res = await fetch(`/api/tacos/bulk?${p}`, {
      headers: { [USER_HEADER]: loadCurrentUser() },
      signal,
    })
    if (!res.ok) return {}
    const body = (await res.json()) as TacosBulkResponse
    return body.items ?? {}
  } catch {
    return {}
  }
}

// ── All listings ↔ UID (Marketplaces hub #5) ────────────────────────────────
// Reuses the ProductLink shape (same md_goflow_listings source as /linking).
export function getListings(
  opts: { q?: string; marketplace?: string; status?: string; signal?: AbortSignal },
): Promise<ListResponse<ProductLink>> {
  const p = new URLSearchParams()
  if (opts.q) p.set('q', opts.q)
  if (opts.marketplace && opts.marketplace !== 'all') p.set('marketplace', opts.marketplace)
  if (opts.status) p.set('status', opts.status)
  return opsFetch<ListResponse<ProductLink>>(`/api/products/listings?${p}`, {
    signal: opts.signal,
  })
}
export function getListingMarketplaceCounts(): Promise<Record<string, number>> {
  return opsFetch<Record<string, number>>('/api/products/listing-marketplace-counts')
}

// ── Pricing model (seller-fulfilled marketplaces only) ──────────────────────
// Costs each live listing end-to-end — what we charge, what the same product
// costs on Amazon, and what it costs us to buy AND ship — then sorts it into a
// work queue. Amazon is excluded server-side (it IS the benchmark).
export type PricingFlag =
  | 'OVERPRICED_NO_SALES' | 'BELOW_FLOOR' | 'PRICED_HIGH'
  | 'NO_SALES_PRICE_OK' | 'NO_BENCHMARK' | 'OK'

export interface PricingRow {
  uid: string
  product_name: string | null
  title: string | null
  listing_id: string | null
  store_page_url: string | null
  status: string | null
  price: number | null
  benchmark_price: number | null
  benchmark_source: string | null
  ratio: number | null
  /** Landed cost per unit — PR 2: the product-facts cost (the same number the
   *  P&L and PPC use). */
  unit_cogs: number | null
  /** Where unit_cogs came from ('po' | 'fifo' | 'fifo_seed' | 'catalog' |
   *  'estimate'); null in split mode or on a pre-PR-2 backend. */
  cost_basis?: string | null
  /** True when unit_cogs is a fallback the screen marks "est.". */
  cost_is_estimate?: boolean
  ship_per_unit: number | null
  ship_source: string | null
  fee_pct: number | null
  margin_now: number | null
  price_floor: number | null
  suggested_price: number | null
  pack_size: string | null
  units_180d: number
  units_90d: number
  sales_180d: number
  amazon_units_180d: number
  last_sale: string | null
  flag: PricingFlag
  duplicate_of: string | null
}

export interface PricingResponse {
  count: number
  items: PricingRow[]
  marketplace: string
  fee_pct: number
  min_margin: number
  markup: number
  overpriced_ratio: number
  fee_missing: boolean
}

/** Marketplaces the pricing model applies to — mirrors the server's
 *  PRICING_MARKETPLACES (and engine/mpl_shipping.MPL_MARKETPLACES). */
export const PRICING_MARKETPLACES = ['Target+', 'Walmart', 'Temu', 'TikTok', 'eBay']

export function getPricing(
  opts: { marketplace: string; status?: string; signal?: AbortSignal },
): Promise<PricingResponse> {
  const p = new URLSearchParams({ marketplace: opts.marketplace })
  if (opts.status) p.set('status', opts.status)
  return opsFetch<PricingResponse>(`/api/products/pricing?${p}`, { signal: opts.signal })
}

// PR 4/5 — Receive Documents.
export type DocType =
  | 'I' | '7501' | 'PL' | 'BOL' | 'FF'
  | 'SUPPLIER_INVOICE' | 'DOMESTIC_PO' | 'PAYMENT_PROOF'
  | 'INSPECTION' | 'QC_PHOTO' | 'ETA_SHEET' | 'MISC'

export interface ScanShiflEntry {
  ref: string
  filename: string
  size: number
  type: 'allowed' | 'unknown'
  skipped: boolean
}

export interface ClassifyPart {
  part_name: string                              // sheet name in the xlsx
  code: string
  reasoning: string
  confidence_enum: 'low' | 'medium' | 'high'
  confidence_float: number
  suggested_link_po: string[]
  suggested_link_uid: string[]
  suggested_link_shipment: string[]
}

export interface ClassifyResult {
  code: string
  reasoning: string
  confidence_enum: 'low' | 'medium' | 'high'
  confidence_float: number
  suggested_link_po: string[]
  suggested_link_uid: string[]
  suggested_link_shipment: string[]
  dropped: Record<string, string[]>
  // Populated for multi-tab xlsx uploads. When non-empty, the drawer
  // ignores the top-level `code` and renders one row per part instead.
  parts: ClassifyPart[]
  /** Provenance: which signal drove the final doc_type. Backend stamps
   *  'filename' when the smart classifier was uncertain and the filename
   *  keyword extractor took over; 'filename_fast_path' / 'cache' when
   *  Sonnet was never called. Drawer treats these as "instant" results
   *  and shows a ⚡ badge. */
  source?: 'content' | 'content+filename' | 'filename' | 'filename_fast_path' | 'cache'
  // When the file was rejected before Sonnet (legacy .xls, mis-extensioned
  // PDF/CSV, unknown blob), this tag tells the drawer to render an amber
  // warning banner with the reasoning instead of a low-confidence MISC row.
  error_kind?: string | null
}

export interface EtaDiffRow {
  shifl_ref: string
  etd: string | null
  eta: string | null
  carrier: string | null
  container: string | null
  current_etd: string | null
  current_eta: string | null
  current_status: string | null
  changed: boolean
  write_mode: 'insert' | 'update' | 'blocked'
}

// ─── Split-classify (Round-4 Area A unified Document-Receive App) ────────
// A combined multi-page PDF is split into pages, each page classified, and
// contiguous same-type pages grouped into logical doc "splits" (a page-range
// that is one PO / Packing List / Invoice / BOL). The review UI renders one
// SplitReviewCard per split before the owner confirms.
export interface DocSplit {
  split_id: number | null
  page_start: number          // 1-based, inclusive
  page_end: number            // inclusive
  doc_type: string
  confidence: number
  link_po: string[]
  link_uid: string[]
  link_shipment: string[]
  reasoning?: string | null
}

export interface SplitClassifyResult {
  bundle_id: number | null
  original_name: string
  page_count: number
  splits: DocSplit[]
  capped: boolean
  warnings: string[]
}

export interface FileDocPayload {
  doc_type: DocType
  link_po: string[]
  link_uid: string[]
  link_shipment: string[]
  source: 'shifl_drop' | 'upload'
  source_path?: string | null
  target_folder_id?: string | null
  target_mode: 'auto' | 'pick' | 'skip'
}

export interface DocumentOut {
  id: number
  doc_type: string
  drive_file_id: string | null
  drive_url: string | null
  final_name: string | null
  source: string
  link_po: string[] | null
  link_uid: string[] | null
  link_shipment: string[] | null
  user_overrode: boolean
  ingested_at: string
  actor: string
  client_host: string | null
}

export async function scanShifl(): Promise<{ files: ScanShiflEntry[] }> {
  return opsFetch(`/api/documents/scan-shifl`, { method: 'POST' })
}

export async function classifyUpload(
  file: File,
  opts: { jobId?: string; signal?: AbortSignal } = {},
): Promise<ClassifyResult> {
  const fd = new FormData()
  fd.append('upload', file)
  const qs = opts.jobId ? `?job_id=${encodeURIComponent(opts.jobId)}` : ''
  return opsFetch(`/api/documents/classify${qs}`, {
    method: 'POST', body: fd, isMultipart: true, signal: opts.signal,
  })
}

/** POST /api/documents/classify/{job_id}/cancel — best-effort soft cancel
 *  for an in-flight classify. The Sonnet call may still finish in the
 *  background; the frontend discards the result. */
export async function cancelClassifyJob(jobId: string): Promise<{ ok: boolean }> {
  return opsFetch<{ ok: boolean }>(
    `/api/documents/classify/${encodeURIComponent(jobId)}/cancel`,
    { method: 'POST' },
  )
}

// ─── Classify progress SSE event payloads ──────────────────────────────
/** Phases broadcast on the `classify_progress` SSE channel by the backend
 *  `_publish_classify_phase` helper (routes/documents.py). Each event
 *  carries the row's job_id so the frontend can route by row. */
export type ClassifyPhase =
  | 'reading_pdf'
  | 'calling_sonnet'
  | 'parsing_response'
  | 'done'

export interface ClassifyProgressEvent {
  channel: 'classify_progress'
  ts: number
  job_id: string
  phase: ClassifyPhase
  /** Provenance of the result. 'filename_fast_path' / 'filename' means
   *  Sonnet was skipped entirely; 'cache' would mean a cache hit. */
  source?: string
  /** When phase='done': final doc_type code (e.g. 'PL', 'I', 'MISC'). */
  code?: string
  /** When phase='done': 0..1 classifier confidence. */
  confidence?: number
  /** When phase='calling_sonnet': model name (claude-haiku-4-5-… or sonnet). */
  model?: string
  /** When phase='reading_pdf': raw byte count + filename. */
  bytes?: number
  filename?: string
  is_pdf?: boolean
}

export async function extractEta(file: File): Promise<EtaDiffRow[]> {
  const fd = new FormData()
  fd.append('upload', file)
  return opsFetch(`/api/documents/eta/extract`, { method: 'POST', body: fd, isMultipart: true })
}

export async function commitEta(rows: EtaDiffRow[]): Promise<{ inserted: number; updated: number; blocked: number; blocked_refs: string[] }> {
  return opsFetch(`/api/documents/eta/commit`, { method: 'POST', body: { rows } })
}

// ─── Receive Documents v1a+ ────────────────────────────────────────────
export interface ReceiveUidCandidate {
  uid: string
  // Post-migration 021: ship_key disambiguates same-(po, uid) split rows.
  // '__OPEN__' for the unshipped remainder; equals shipping_number for
  // shipped children. Default for legacy callers.
  ship_key?: string
  // PO# this candidate belongs to. Now populated by /receive/extract too,
  // not just the per-PO autocomplete: a doc line can resolve by supplier SKU
  // with no PO printed on the row, and a commit targets
  // (po_number, uid, ship_key), so a UID without its PO is not actionable.
  po_number?: string | null
  description: string
  supplier: string | null
  current_qty: number | null
  current_cost: number | null
  current_cartons: number | null
  current_cbm: number | null
  current_shipping_number: string | null
  status: string | null
  version: number | null
  score: number
  score_explain: string | null
}
export interface ReceiveCurrentValues {
  qty: number | null
  cartons: number | null
  cbm: number | null
  item_cost_fob: number | null
  shipping_number: string | null
  supplier: string | null
  description: string | null
}
export interface ReceiveProposedValues {
  qty: number | null
  cartons: number | null
  cbm: number | null
  item_cost_fob: number | null
  shipping_number: string | null
}
export type ReceiveSourceTab = 'PL' | 'INVOICE'
export interface ReceiveProposedDiffRow {
  /** Null when the document prints its PO in the header rather than per row.
   *  A supplier SKU can resolve such a line on its own. */
  po_number: string | null
  description: string
  uid: string | null
  current: ReceiveCurrentValues | null
  proposed: ReceiveProposedValues
  changed_fields: string[]
  if_match_version: number | null
  suggested_candidates: ReceiveUidCandidate[]
  source_tabs: ReceiveSourceTab[]
  warnings: string[]
  // Reconcile-first: true when the detected shipment already has a line
  // matching this row (same PO + qty). Advisory 'already recorded?' hint.
  already_in_shipment?: boolean
  // Frontend-populated after the user accepts a candidate. Used by
  // buildCommitPayload so a split-then-accepted-child row commits against
  // the child's ship_key instead of defaulting to __OPEN__ (the parent,
  // whose version has already been bumped by the split → 409 conflict).
  ship_key?: string
}
export interface ReceiveExistingLine {
  po_number: string
  uid: string
  description: string | null
  qty: number | null
  cartons: number | null
  status: string | null
}
export interface ReceiveExistingShipment {
  shipment_no: string
  total_lines: number
  by_status: Record<string, number>
  lines: ReceiveExistingLine[]
  all_extracted_present: boolean
}
export interface ReceiveHeaderInfo {
  shipment_no: string | null
  containers: string[]
  po_numbers: string[]
  supplier_guess: string | null
  invoice_date: string | null
  pl_date: string | null
  filename: string
  sheet_names: string[]
}
export interface ReceiveConfidence {
  overall: number
  per_field: Record<string, number>
  per_sheet: Array<Record<string, unknown>>
}
export interface ReceiveExtractResult {
  header_info: ReceiveHeaderInfo
  proposed_diff: ReceiveProposedDiffRow[]
  // Reconcile-first: what ClickPay already has for the detected shipment
  // (null when no ship# detected or the shipment has no lines yet).
  existing_shipment?: ReceiveExistingShipment | null
  confidence: ReceiveConfidence
  extraction_method: 'rules' | 'sonnet' | 'hybrid' | 'sonnet_pdf'
  warnings: string[]
}
export interface ReceiveCommitRowPatch {
  shipping_number?: string | null
  qty?: number | null
  item_cost_fob?: number | null
  cartons?: number | null
  line_cbm?: number | null
}
export interface ReceiveCommitRowSplit {
  /** Parent (__OPEN__) row version to enforce optimistic concurrency. */
  parent_if_match_version: number
  /** Units to carve off into the new child row. Must be < parent qty. */
  child_qty: number
  /** Optional pro-rated cartons; defaults to floor(parent_cartons * child_qty / parent_qty). */
  child_cartons?: number | null
}
export interface ReceiveCommitRowCreate {
  qty: number
  cartons?: number | null
  item_cost_fob?: number | null
}
export interface ReceiveCommitRow {
  po_number: string
  uid: string
  patch: ReceiveCommitRowPatch
  /** Required to patch or split — the optimistic lock. Omitted only on a
   *  create, which has no existing version to lock against. */
  if_match_version?: number | null
  /** Optional ship_key to target an existing split row instead of the open row. */
  ship_key?: string
  /** If present, the row is treated as a child creation off the open row. */
  split?: ReceiveCommitRowSplit | null
  /** If present, the row is INSERTed — the doc shows a line md_orders does
   *  not have. Mutually exclusive with `split`. */
  create?: ReceiveCommitRowCreate | null
}
export interface ReceiveCommitFileDoc {
  source: 'upload' | 'shifl_drop'
  source_path: string | null
  target_mode: 'skip' | 'pick'
  target_folder_id: string | null
  link_po: string[]
  link_uid: string[]
  link_shipment: string[]
}
export interface ReceiveCommitRequest {
  shipment_no: string | null
  containers: string[]
  rows: ReceiveCommitRow[]
  file_doc: ReceiveCommitFileDoc
}
export interface ReceiveCommitPatched {
  po_number: string
  uid: string
  new_version: number
  auto_promoted_to: string | null
  ship_key?: string | null
  child_ship_key?: string | null
}
export interface ReceiveCommitResult {
  patched: ReceiveCommitPatched[]
  document_id: number | null
  drive_url: string | null
  idempotent_replay: boolean
  skipped_server?: { uid?: string | null; reason?: string | null }[]
}

/** POST /api/documents/receive/extract */
export async function extractReceiveDoc(
  file: File,
  opts: { signal?: AbortSignal } = {},
): Promise<ReceiveExtractResult> {
  const fd = new FormData()
  fd.append('upload', file)
  return opsFetch(`/api/documents/receive/extract`, {
    method: 'POST', body: fd, isMultipart: true, signal: opts.signal,
  })
}

/** POST /api/documents/split-classify — Round-4 Area A. Splits a combined
 *  multi-page PDF into pages, classifies each (via the Claude CLI on the
 *  mini), and groups contiguous same-type pages into logical doc-splits. */
export async function splitClassify(
  file: File,
  opts: { signal?: AbortSignal; persist?: boolean } = {},
): Promise<SplitClassifyResult> {
  const fd = new FormData()
  fd.append('upload', file)
  const q = opts.persist === false ? '?persist=false' : ''
  return opsFetch(`/api/documents/split-classify${q}`, {
    method: 'POST', body: fd, isMultipart: true, signal: opts.signal,
  })
}

/** POST /api/documents/splits/extract — extract line items from ONE split
 *  (a page-range of the combined PDF) by re-uploading the bundle + range. */
export async function extractSplit(
  file: File,
  pageStart: number,
  pageEnd: number,
  opts: { signal?: AbortSignal } = {},
): Promise<ReceiveExtractResult> {
  const fd = new FormData()
  fd.append('upload', file)
  fd.append('page_start', String(pageStart))
  fd.append('page_end', String(pageEnd))
  return opsFetch(`/api/documents/splits/extract`, {
    method: 'POST', body: fd, isMultipart: true, signal: opts.signal,
  })
}

/** POST /api/documents/receive/commit. `idempotencyKey` defaults to a
 *  randomized client-id so a double-click cleanly replays. */
export async function commitReceiveDoc(
  payload: ReceiveCommitRequest,
  idempotencyKey?: string,
): Promise<ReceiveCommitResult> {
  const key = idempotencyKey ?? `recv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  return opsFetch(`/api/documents/receive/commit`, {
    method: 'POST',
    body: payload,
    headers: { 'Idempotency-Key': key },
  })
}

export async function fileDocument(payload: FileDocPayload): Promise<DocumentOut> {
  return opsFetch(`/api/documents/file`, { method: 'POST', body: payload })
}

/**
 * Upload a document to Google Drive for real (Phase D, 2026-06-09). Unlike
 * fileDocument (catalog-only), this physically puts the file in the doc's
 * Drive folder via a multipart POST. Pass the File for upload-source rows;
 * shifl_drop rows omit it and the server reads the bytes from the inbox by
 * source_path.
 *
 * ClickPay note: POST /file-upload is part of MasterData's uncommitted
 * Phase-D backend stratum (routes/documents.py in the dirty working tree)
 * and is NOT yet vendored into backend/ops — until it lands this call
 * returns 404 and IntakePanel surfaces it as a per-row filing error.
 * Form-field names match the WIP route signature exactly
 * (doc_type/source/source_path/link_po/link_uid/link_shipment/file).
 */
export async function fileDocumentToDrive(payload: FileDocPayload, file?: File): Promise<DocumentOut> {
  const fd = new FormData()
  fd.append('doc_type', payload.doc_type)
  fd.append('source', payload.source)
  if (payload.source_path) fd.append('source_path', payload.source_path)
  fd.append('link_po', payload.link_po.join(','))
  fd.append('link_uid', payload.link_uid.join(','))
  fd.append('link_shipment', payload.link_shipment.join(','))
  if (file) fd.append('file', file)
  return opsFetch<DocumentOut>('/api/documents/file-upload', { method: 'POST', body: fd, isMultipart: true })
}

// ─── Receive Queue (orphan-shipment triage) ─────────────────────────────
// Backend: routes/receive_queue.py. The header-mounted ReceiveDocsButton
// polls /count + subscribes to the 'receive_queue' SSE channel.
export interface ReceiveQueueItemOut {
  shipping_number: string
  queued_at: string
  status: 'pending' | 'in_progress' | 'dismissed' | 'resolved' | string
  status_at: string | null
  reason: string
  actor: string | null
  ship_status: string | null
  ship_eta: string | null
  ship_supplier_code: string | null
  ship_carrier: string | null
  auto_fetched_at: string | null
  /** Free-form cache payload — typically `{ drive_files: [...], shifl_data: {...} }`. */
  auto_fetched_data: ReceiveAutoFetchedData | null
  version: number
  /** PR 18 (#111): is the shipment's paperwork verified against Shifl.
   *  `resolved` only means "some line points here"; the Completed lane is
   *  `check_state === 'complete'`. Null when the server's check is switched
   *  off (CLICKPAY_RECEIVE_VERIFY=0) — then resolved = Completed, as before. */
  check_state?: ReceiveCheckState | null
}

/** Cached payload pre-fetched for one orphan shipment. Loose shape — backend
 *  fills it asynchronously and the drawer renders what's present. */
/** Why Shifl could not be read. `key_rejected` = Shifl refused ClickPay's API
 *  key (every shipment is blocked until an admin installs a new one). */
export interface ShiflProblem {
  kind: 'key_rejected' | 'firewall_blocked' | 'not_found' | 'unavailable'
  message: string
  since?: string | null
  help_url?: string | null
}

export interface ReceiveAutoFetchedData {
  drive_files?: ReceiveAutoFetchedDriveFile[]
  shifl_data?: Record<string, unknown> | null
  /** PDFs sitting in C:\Users\…\Downloads\Shifl\{ref}\ that haven't been
   *  filed/uploaded yet. Non-empty → fresh docs available to upload now. */
  shifl_inbox?: ReceiveAutoFetchedDriveFile[]
  /** Why the Shifl half failed, in plain English, and which kind of failure
   *  it was (null when Shifl answered). */
  shifl_error?: string | null
  shifl_error_kind?: ShiflProblem['kind'] | null
  /** Set when a refresh failed but the earlier good Shifl data was kept. */
  shifl_refresh_error?: string | null
  shifl_refresh_error_kind?: ShiflProblem['kind'] | null
  /** Documents previously uploaded for this ship# but never linked to any
   *  line items (md_documents rows tagged to ship_no with no
   *  link_po/link_uid resolution). Surfaces stalled receives — docs exist
   *  but the receive flow was abandoned partway through. */
  previous_documents?: ReceivePreviousDocument[]
  [key: string]: unknown
}
export interface ReceiveAutoFetchedDriveFile {
  id?: string
  name: string
  mime_type?: string | null
  web_view_link?: string | null
  /** Optional pre-classification (doc_type code) when auto-fetch ran shifl_docs. */
  suggested_code?: string | null
}
/** A document already in md_documents that's tagged to this ship# but
 *  hasn't been wired up to PO/UID line items. Used by the amber "stalled
 *  receive" indicator on the receive-queue button. */
export interface ReceivePreviousDocument {
  id: number
  doc_type: string | null
  final_name: string | null
  drive_url: string | null
  ingested_at: string | null
  actor: string | null
}

export interface ReceiveQueueCount {
  pending: number
  in_progress: number
  /** PR 18: linked (resolved) shipments not yet verified against Shifl. */
  needs_check?: number
}

export function getReceiveQueue(
  opts: { status?: string; limit?: number; signal?: AbortSignal } = {},
): Promise<ReceiveQueueItemOut[]> {
  const q = new URLSearchParams()
  if (opts.status) q.set('status', opts.status)
  if (opts.limit != null) q.set('limit', String(opts.limit))
  const qs = q.toString() ? `?${q}` : ''
  return opsFetch<ReceiveQueueItemOut[]>(`/api/receive-queue${qs}`, { signal: opts.signal })
}

export function getReceiveQueueCount(
  opts: { signal?: AbortSignal } = {},
): Promise<ReceiveQueueCount> {
  return opsFetch<ReceiveQueueCount>(`/api/receive-queue/count`, { signal: opts.signal })
}

export function triggerAutoFetch(shippingNumber: string): Promise<ReceiveQueueItemOut> {
  return opsFetch<ReceiveQueueItemOut>(
    `/api/receive-queue/${encodeURIComponent(shippingNumber)}/auto-fetch`,
    { method: 'POST' },
  )
}

export function startQueueItem(shippingNumber: string): Promise<ReceiveQueueItemOut> {
  return opsFetch<ReceiveQueueItemOut>(
    `/api/receive-queue/${encodeURIComponent(shippingNumber)}/start`,
    { method: 'POST' },
  )
}

export function dismissQueueItem(
  shippingNumber: string, reason: string,
): Promise<ReceiveQueueItemOut> {
  return opsFetch<ReceiveQueueItemOut>(
    `/api/receive-queue/${encodeURIComponent(shippingNumber)}/dismiss`,
    { method: 'POST', body: { reason } },
  )
}

// ─── Orders balance-ledger (Orders-flow epic) ───────────────────────────
// Deterministic PO -> shipment -> COGS reconciliation surfaced from
// md_orders_ledger. unit_balance_state ∈ balanced|over_shipped|under_shipped|
// unconfirmed|open_only; dollar_balance_state ∈ costed|partially_costed|
// uncosted|not_shipped.
export interface OrdersLedgerSummary {
  n_lines: number
  unit_states: Record<string, number>
  dollar_states: Record<string, number>
  units: {
    ordered: number; shipped: number; open: number
    confirmed: number; received: number; costed: number
  }
  dollars: { ordered: number; landed: number }
}
export interface OrdersLedgerRow {
  po_number: string; uid: string; supplier: string | null
  ordered_qty: number; shipped_qty: number; open_qty: number
  confirmed_qty: number; received_qty: number; costed_qty: number
  unit_balance_state: string
  dollar_ordered: number; dollar_landed: number; dollar_balance_state: string
  n_shipments: number; shipments: string | null; notes: string | null
  computed_at?: string
}
export function getOrdersLedgerSummary(
  opts: { signal?: AbortSignal } = {},
): Promise<OrdersLedgerSummary> {
  return opsFetch<OrdersLedgerSummary>(`/api/orders-ledger/summary`, { signal: opts.signal })
}
export function getOrdersLedger(
  params: { state?: string; po?: string; uid?: string; unbalanced_only?: boolean } = {},
  opts: { signal?: AbortSignal } = {},
): Promise<{ count: number; items: OrdersLedgerRow[] }> {
  const q = new URLSearchParams()
  if (params.state) q.set('state', params.state)
  if (params.po) q.set('po', params.po)
  if (params.uid) q.set('uid', params.uid)
  if (params.unbalanced_only) q.set('unbalanced_only', 'true')
  const qs = q.toString()
  return opsFetch(`/api/orders-ledger${qs ? '?' + qs : ''}`, { signal: opts.signal })
}

// ─── Per-shipment reconciliation (confirmed packing-list vs linked PO) ────
export interface ShipmentReconLine {
  po_number: string; uid: string
  linked_qty: number; confirmed_qty: number
  cartons: number | null; cbm: number | null
  state: 'balanced' | 'phantom' | 'missing' | 'unconfirmed'
}
export interface ShipmentRecon {
  shipping_number: string
  count: number
  confirmed_available: boolean
  totals: { linked: number; confirmed: number; balanced: number; phantom: number; missing: number; unconfirmed: number }
  items: ShipmentReconLine[]
}
export function getShipmentReconciliation(
  ship: string, opts: { signal?: AbortSignal } = {},
): Promise<ShipmentRecon> {
  return opsFetch(`/api/orders-ledger/shipment/${encodeURIComponent(ship)}`, { signal: opts.signal })
}

// ─── Payments v2 (Orders-flow epic) — real outflows + allocation ──────────
export interface PaymentV2 {
  payment_id: number
  payment_date: string | null
  amount: number
  currency: string
  method: string | null
  supplier_code: string | null
  supplier_name: string | null
  reference: string | null
  notes: string | null
  allocated: number
  unallocated: number
}
export function getPaymentsV2(
  opts: { signal?: AbortSignal } = {},
): Promise<{ count: number; items: PaymentV2[] }> {
  return opsFetch(`/api/payments-v2`, { signal: opts.signal })
}
export function autoAllocatePayment(
  paymentId: number, commit: boolean,
): Promise<{ payment_id: number; proposed_total: number; unallocated_remaining: number; committed: boolean }> {
  return opsFetch(`/api/payments-v2/${paymentId}/auto-allocate?commit=${commit ? 'true' : 'false'}`,
    { method: 'POST' })
}

// ─── QBO needs-review queue (migration 058) ──────────────────────────────
// QBO-synced payments whose supplier→PO match was ambiguous (0 or >1
// candidates) land here with needs_review=true instead of being silently
// auto-allocated. review_candidates carries exactly {po_number, uid,
// open_amount} — no score field (deterministic open-lines match, not fuzzy).
export interface PaymentReviewCandidate {
  po_number: string
  uid: string
  open_amount: number
}
export interface PaymentNeedsReview {
  payment_id: number
  payment_date: string | null
  amount: number
  currency: string
  supplier_name: string | null
  reference: string | null
  notes: string | null
  review_candidates: PaymentReviewCandidate[]
}
/** Payments flagged needs_review=true by the QBO sync — unresolved queue. */
export function getPaymentsNeedsReview(
  opts: { signal?: AbortSignal } = {},
): Promise<{ count: number; items: PaymentNeedsReview[] }> {
  return opsFetch(`/api/payments-v2/needs-review`, { signal: opts.signal })
}
/** Manual free-form allocation — same body shape as the backend's AllocBody
 *  (list of {po_number, uid, amount} and/or {cost_id, amount}). Clears
 *  needs_review once the payment is fully allocated. */
export function allocatePaymentV2(
  paymentId: number,
  allocations: { po_number?: string; uid?: string; cost_id?: number; amount: number }[],
): Promise<{ ok: boolean; allocated: number; n: number }> {
  return opsFetch(`/api/payments-v2/${paymentId}/allocate`, {
    method: 'POST', body: { allocations },
  })
}
/** Clear needs_review without allocating (e.g. a bank fee, not PO-related). */
export function dismissPaymentReview(paymentId: number): Promise<{ ok: boolean }> {
  return opsFetch(`/api/payments-v2/${paymentId}/dismiss-review`, { method: 'POST' })
}

// ─── True COGS (Orders-flow epic) ────────────────────────────────────────
export interface TrueCogsRow {
  uid: string
  /** Null when the unit is UNCOSTED. It used to be COALESCEd to 0 server-side,
   *  which reported an unknown cost as a specific, cheaper-looking $0.00. */
  fifo_unit_cost: number | null
  /** Real zero is meaningful here: a mold-free product genuinely amortizes $0. */
  amort_unit_cost: number
  /** Null whenever fifo_unit_cost is null - an unknown component must not
   *  produce a complete-looking total. */
  true_cogs_unit: number | null
  n_sources: number
  sources: string | null
  // Phase 2 PR-8 — Payments COGS Hub canonical chain (unit_economics.py),
  // riding alongside the fields above (transition safety, nothing removed).
  /** PO/catalog-precedence landed cost — DISTINCT from fifo_unit_cost (the
   *  FIFO-ledger md_cogs_daily figure). Null when neither source has data. */
  landed_unit?: number | null
  /** Qty-weighted avg md_orders.cbm_per_unit over Received lines in the
   *  selected range; the trailing 365 days when no range is sent. */
  cbm_unit?: number | null
  /** Qty-weighted avg md_orders.duty_per_unit over Received lines in the
   *  selected range; the trailing 365 days when no range is sent. */
  duty_unit?: number | null
  /** Owner-entered manual per-unit warehouse cost ("WH $/u (Manual)"). */
  warehouse_manual_unit?: number | null
  /** Reference-only 3PL rate-card figure ("WH $/u (Calc)"); null until the
   *  nightly/rebuild job has populated md_warehouse_cost_calc for this uid. */
  warehouse_calc_unit?: number | null
  /** "WH $/u (Calc)" tooltip components — trailing-365d receiving + storage. */
  warehouse_calc_receiving_cost?: number | null
  warehouse_calc_storage_cost?: number | null
  warehouse_calc_carton_months?: number | null
  warehouse_calc_units_basis?: number | null
  /** True when md_products.case_pack_qty was NULL/0 for this uid (case pack
   *  treated as 1 — cartons_received/carton_months are unit-for-unit stand-ins). */
  warehouse_calc_estimated?: boolean | null
  /** THE canonical COGS: landed_unit + warehouse_manual_unit. */
  total_unit?: number | null
  /** total_unit + amortized tooling delta. */
  absolute_unit?: number | null
  // ─── PR-16 additions ──────────────────────────────────────────────────
  /** Qty-weighted avg item_cost_fob over Received lines in the selected
   *  range (all-time when no range is passed). */
  raw_cogs_unit?: number | null
  /** item_cost_fob of the most recent PO line (any non-Cancelled status) —
   *  what it costs to reorder today. Not range-scoped. */
  latest_fob_unit?: number | null
  /** SUM(qty), non-Cancelled lines, in the selected range (all-time when no
   *  range is passed). */
  units_ordered?: number
  /** SUM(units sold), in the selected range (all-time when no range is
   *  passed). */
  units_sold?: number
  /** Display-only coverage flags for cbm_unit/duty_unit — how many Received
   *  lines in the window actually carry the figure, out of how many total,
   *  plus up to 3 PO numbers missing it ('(split)' = a partially-costed
   *  ship_key split). Never changes cbm_unit/duty_unit's math. */
  cbm_coverage?: CogsCoverage
  duty_coverage?: CogsCoverage
  /** PR 18: what `landed_unit` is MADE OF — the latest eligible received PO
   *  line (same eligibility as landed_unit_bulk). Null when landed_unit came
   *  from the product record or is unknown. */
  landed_breakdown?: LandedBreakdown | null
  /** 'po' (breakdown exists) | 'catalog' (product record, no received PO
   *  yet) | null (no landed figure at all). */
  landed_source?: 'po' | 'catalog' | null
}
export interface LandedBreakdown {
  po_number: string
  ship_key: string | null
  /** The PO line's ordered date (ISO). */
  as_of: string | null
  fob_unit: number | null
  freight_unit: number | null
  duty_unit: number | null
  landed_unit: number | null
}
export interface CogsCoverage {
  n_costed: number
  n_total: number
  missing_pos: string[]
  /** True when missing_pos was capped at 3 and more POs are missing than
   *  are listed — distinguishes "exactly 3 missing" from "more than 3". */
  missing_pos_truncated?: boolean
}
export function getTrueCogs(
  opts: { signal?: AbortSignal; all?: boolean; dateFrom?: string; dateTo?: string } = {},
): Promise<{ count: number; items: TrueCogsRow[] }> {
  // ?all=true (R5-4 COGS hub): Regular/Total for EVERY product (amort 0 when no
  // mold/tooling → Total == Regular). Default path stays the filtered panel set.
  // date_from/date_to (PR-16): scope the Raw COGS/u, Units Ordered/Sold, and
  // CBM/Duty $/u + coverage columns to the COGS-hub range selector. When
  // omitted, CBM/Duty $ per unit and their coverage default to the TRAILING
  // 365 DAYS; Raw COGS, Latest FOB and units are all-time. Only meaningful
  // alongside ?all=true.
  const p = new URLSearchParams()
  if (opts.all) p.set('all', 'true')
  if (opts.dateFrom) p.set('date_from', opts.dateFrom)
  if (opts.dateTo) p.set('date_to', opts.dateTo)
  const qs = p.toString()
  return opsFetch(`/api/true-cogs${qs ? `?${qs}` : ''}`, { signal: opts.signal })
}

// ─── WH $/u (Calc) breakdown — COGS Hub row-expand tab ───────────────────────
export interface WarehouseCalcPoLine {
  po_number: string
  ship_key: string
  supplier: string | null
  ordered_date: string | null
  qty: number
}
export interface WarehouseCalcDailyPoint {
  day: string
  warehouse_qty: number
  cartons: number
}
export interface WarehouseCalcDetail {
  uid: string
  window: { since: string; until: string; days: number }
  rates: { receiving_per_carton: number; storage_per_carton_month: number }
  case_pack: { qty: number | null; estimated: boolean }
  receiving: {
    units_received: number
    cartons_received: number
    receiving_cost: number
    pos: WarehouseCalcPoLine[]
  }
  storage: {
    carton_months: number
    storage_cost: number
    daily_series: WarehouseCalcDailyPoint[]
    coverage: {
      table_min_day: string | null
      table_max_day: string | null
      days_available: number
      window_days: number
    }
  }
  units_basis: { value: number; source: 'received' | 'shipped' | 'sold' | 'none' }
  per_unit: number | null
  estimated: boolean
}
/** Full derivation of one uid's "WH $/u (Calc)" figure (rates, case pack,
 *  contributing Received POs, the daily warehouse_qty series, units-basis
 *  tier, and the resulting per-unit) — the COGS Hub row-expand's WH
 *  breakdown tab. On-demand; not part of the nightly bulk rebuild. */
export function getWarehouseCalcDetail(
  uid: string, opts: { signal?: AbortSignal } = {},
): Promise<WarehouseCalcDetail> {
  return opsFetch(`/api/true-cogs/warehouse-calc/${encodeURIComponent(uid)}`, { signal: opts.signal })
}

// ─── Unit-economics settings (Phase 2, PR-7/PR-8) ────────────────────────────
export interface UnitEconSettings {
  warehouse_manual_per_unit_default: number
  wh3pl_storage_per_carton_month: number
  wh3pl_receiving_per_carton: number
  expected_price_window_days: number
}
export function getUnitEconSettings(
  opts: { signal?: AbortSignal } = {},
): Promise<UnitEconSettings> {
  return opsFetch(`/api/unit-econ/settings`, { signal: opts.signal })
}
export function patchUnitEconSettings(
  patch: Partial<UnitEconSettings>,
): Promise<UnitEconSettings> {
  return opsFetch(`/api/unit-econ/settings`, { method: 'PATCH', body: patch })
}
/** On-demand full recompute of md_warehouse_cost_calc ("WH $/u (Calc)") —
 *  same job the nightly scheduler runs; POST after a rate-card change so the
 *  hub reflects it without waiting for the next tick. */
export function rebuildWarehouseCalc(): Promise<{ ok: boolean; count: number }> {
  return opsFetch(`/api/unit-econ/warehouse-calc/rebuild`, { method: 'POST' })
}

// ─── Bulk-edit "WH $/u (Manual)" (COGS Hub product-view checkbox toolbar) ────
export interface BulkWarehouseCostRow {
  uid: string
  before: number | null
  after: number | null
}
export interface BulkWarehouseCostResponse {
  /** Every uid found in md_products, including no-op re-applies. */
  updated: BulkWarehouseCostRow[]
  /** uids from the request that aren't in md_products at all. */
  not_found: string[]
}
/** Bulk set/clear the manual WH $/u override (md_products.warehouse_cost_per_unit)
 *  across many uids in one call.
 *    value=<number> (incl. 0)  → forces every uid to that EXPLICIT figure.
 *    value=null                → CLEARS back to unset (falls through to the
 *                                 calculated/default figure) — not the same
 *                                 as writing a zero.
 *  `reason` is required — one audit row per uid whose value actually changed. */
export function bulkSetWarehouseCost(
  uids: string[], value: number | null, reason: string,
): Promise<BulkWarehouseCostResponse> {
  return opsFetch(`/api/products/warehouse-cost/bulk`, {
    method: 'POST', body: { uids, value, reason },
  })
}

// ─── ASIN -> UID collision resolution (Build 21) ─────────────────────────────
export interface AsinCollisionClaimant {
  uid: string
  product_name: string | null
}
export interface AsinCollision {
  asin: string
  uids: string[]
  claimants: AsinCollisionClaimant[]
  n_uids: number
  /** The FNSKU-phantom signature: an identifier minted its own product row
   *  where uid == asin (B08WJQPNQJ is the known case). The resolve dialog
   *  pre-suggests quarantine when this is true — it never auto-applies it. */
  uid_equals_asin: boolean
  resolution: 'assign' | 'quarantine' | null
  winner_uid: string | null
  note: string | null
  resolved_by: string | null
  resolved_at: string | null
  /** Whole days since this asin was first seen as an UNRESOLVED collision;
   *  null once resolved, and null for a brand-new collision no sync has
   *  stamped a first-seen marker for yet — never 0-as-a-guess. */
  unresolved_age_days: number | null
}
export interface AsinResolveResponse {
  asin: string
  winner_uid: string | null
  resolution: string
  note: string | null
  resolved_by: string | null
  resolved_at: string
  losers: string[]
}
/** Every asin owned by >1 uid — resolved ones are included (with their
 *  resolution attached) so the UI can show an undo action rather than
 *  hiding them once fixed. */
export function getAsinCollisions(
  opts: { signal?: AbortSignal } = {},
): Promise<ListResponse<AsinCollision>> {
  return opsFetch(`/api/products/asin-collisions`, { signal: opts.signal })
}
/** Resolve one asin collision. `winner_uid` need not be one of the asin's
 *  current claimants (assign only); omit/ignore it for 'quarantine' — the
 *  FNSKU case, where nobody wins the asin. Never auto-applied — always an
 *  explicit owner action. */
export function resolveAsinCollision(body: {
  asin: string
  winner_uid?: string | null
  resolution: 'assign' | 'quarantine'
  note?: string | null
}): Promise<AsinResolveResponse> {
  return opsFetch(`/api/products/asin-collisions/resolve`, {
    method: 'POST', body,
  })
}
/** Undo a resolution — the resolved asin returns to "unresolved" and the
 *  next sync tick restores BQ's state for the affected uids on its own. */
export function deleteAsinResolution(asin: string): Promise<{ asin: string; deleted: boolean }> {
  return opsFetch(`/api/products/asin-collisions/${encodeURIComponent(asin)}`, {
    method: 'DELETE',
  })
}

// ─── COGS hub range totals + landed-cost breakdown ───────────────────────────
export interface CogsBreakdownComponent {
  total: number
  per_unit: number | null
}
export interface CogsSummary {
  date_from: string
  date_to: string
  units_ordered: number
  units_sold: number
  avg_fifo_unit: number | null
  total_landed_cogs: number
  po_count: number
  breakdown: {
    fob: CogsBreakdownComponent
    duty: CogsBreakdownComponent
    freight: CogsBreakdownComponent
    landed: CogsBreakdownComponent
    /** Warehousing/trucking is folded into `freight` (not separable at the
     *  md_orders line grain). false → UI labels the gap, never fabricates. */
    warehousing_separable: boolean
  }
}
/** Range-scoped COGS totals + landed-cost breakdown for the hub header. Dates
 *  ISO YYYY-MM-DD; omit both for the backend default (trailing 365 days). */
export function getCogsSummary(
  opts: { dateFrom?: string; dateTo?: string; signal?: AbortSignal } = {},
): Promise<CogsSummary> {
  const p = new URLSearchParams()
  if (opts.dateFrom) p.set('date_from', opts.dateFrom)
  if (opts.dateTo) p.set('date_to', opts.dateTo)
  const qs = p.toString()
  return opsFetch(`/api/true-cogs/summary${qs ? `?${qs}` : ''}`, { signal: opts.signal })
}

// ─── R5-4 COGS hub — read-only PO/Product regroups of the locked basis ───────
// invoice = invoice_po_cost (FOB); allocated = target_type='po_line' only.
export interface PoCogsStatus {
  po_number: string
  invoice_total: number
  allocated_total: number
  owed: number
  pct_paid: number
  state: 'paid' | 'partial' | 'unpaid'
}
/** Per-PO paid/owed/%paid across all POs in one call (PO-view columns). */
export function getPaymentsPoStatus(
  opts: { signal?: AbortSignal } = {},
): Promise<{ count: number; items: PoCogsStatus[] }> {
  return opsFetch(`/api/payments-v2/po-status`, { signal: opts.signal })
}

export interface ProductPayment {
  uid: string
  product_name: string | null
  image_url: string | null
  invoice_total: number
  allocated: number
  owed: number
  pct_paid: number
  state: 'paid' | 'partial' | 'unpaid'
  n_pos: number
}
/** Per-UID COGS-allocation regroup (Product-view rows). */
export function getPaymentsByProduct(
  opts: { signal?: AbortSignal } = {},
): Promise<{ count: number; items: ProductPayment[] }> {
  return opsFetch(`/api/payments-v2/by-product`, { signal: opts.signal })
}

// #75: why a freight or duty leg reads '$0.00'/'—' — an explanation beside
// the number, never a replacement for it. See costGaps.ts for the wording.
export type CostGapCode =
  | 'not_shipped'            // ship_key __OPEN__ — not on a shipment yet
  | 'domestic'                // ship_key DOMESTIC — vendor freight, nothing to allocate
  | 'history_import'          // ship_key GOFLOW-HIST — pre-ClickPay, no shipment ever will exist
  | 'no_shipment_record'      // ship_key names no md_shipments row at all
  | 'shipment_missing_invoice' // shipment exists, no total_shipping_usd entered
  | 'shipment_missing_duty'   // shipment exists, no customs_duty_usd/duty_pct entered
  | 'not_allocated'           // shipment is costed, this line took no share

export interface ProductPoShipment {
  shipping_number: string
  status: string | null
  eta: string | null
  delivered_date: string | null
  container_number: string | null
  carrier: string | null
  vessel_name: string | null
  has_freight_invoice: boolean
  has_duty: boolean
}

export interface ProductPoContribution {
  po_number: string
  supplier: string | null  // md_orders.supplier — the vendor this PO was placed with
  ordered_date: string | null
  // ACTUAL warehouse arrival: md_shipments.delivered_date joined on
  // shipping_number. NOT md_orders.received_at (that is a row write-timestamp,
  // not a receiving event). NULL on GOFLOW-HIST / DOMESTIC POs with no shipment
  // record — renders '—', never guessed.
  received_date: string | null
  invoice: number
  allocated: number
  owed: number
  state: 'paid' | 'partial' | 'unpaid'
  // Per-PO, per-UNIT cost breakdown (owner request 2026-06-30). NULL → render
  // '—' (no fabrication): the column exists but md_orders had no stored value.
  qty: number
  final_unit: number | null    // landed_po_cost/qty  (FOB + freight + duty)
  raw_unit: number | null      // item_cost_fob       (FOB-only RAW)
  tariff_unit: number | null   // duty_per_unit        (7501 customs duty/unit)
  shipping_unit: number | null // freight residual/unit (landed − FOB − duty)
  // #75: shipment(s) this PO travelled on, and why freight/duty is empty.
  ship_keys: string[]
  shipments: ProductPoShipment[]
  freight_gap: CostGapCode | null
  duty_gap: CostGapCode | null
}
/** Which POs contributed a UID's invoice/allocation (Product-row expand). */
export function getProductPos(
  uid: string, opts: { signal?: AbortSignal } = {},
): Promise<{ uid: string; count: number; items: ProductPoContribution[] }> {
  return opsFetch(`/api/payments-v2/by-product/${encodeURIComponent(uid)}/pos`, { signal: opts.signal })
}

export interface MissingCogsRow {
  uid: string
  product_name: string | null
  image_url: string | null
  qty: number
  n_lines: number
  n_pos: number
  missing: string[]   // subset of 'raw' | 'final' | 'tariff'
}
/** UIDs whose per-unit COGS basis is missing (COGS-hub "missing COGS" view). */
export function getMissingCogs(
  opts: { signal?: AbortSignal } = {},
): Promise<{ count: number; items: MissingCogsRow[] }> {
  return opsFetch(`/api/payments-v2/missing-cogs`, { signal: opts.signal })
}

export interface PoPaymentRecord {
  payment_id: number
  payment_date: string | null
  method: string | null
  reference: string | null
  supplier_name: string | null
  payment_amount: number
  allocated_to_po: number
}
/** The actual md_payments outflows allocated to a PO (PO-row expand). */
export function getPoPayments(
  po: string, opts: { signal?: AbortSignal } = {},
): Promise<{ po_number: string; count: number; items: PoPaymentRecord[] }> {
  return opsFetch(`/api/payments-v2/po/${encodeURIComponent(po)}/payments`, { signal: opts.signal })
}

export interface PoLineStatus {
  uid: string
  supplier: string | null
  invoice: number
  allocated: number
  state: 'paid' | 'partial' | 'unpaid'
}
/** Per-line paid status for one PO (PO-row expand, joined with true-cogs). */
export function getPoLineStatus(
  po: string, opts: { signal?: AbortSignal } = {},
): Promise<{ po_number: string; po_state: string; invoice_total: number; allocated_total: number; lines: PoLineStatus[] }> {
  return opsFetch(`/api/payments-v2/po/${encodeURIComponent(po)}/status`, { signal: opts.signal })
}

// ─── Per-PO tooling/mold fee lines (COGS Hub follow-up, PR-16) ───────────
export interface PoFeeLine {
  id: number
  po_number: string
  uid: string
  fee_type: string
  description: string | null
  amount: number
  created_at: string | null
  created_by: string | null
}
/** List fee lines, optionally scoped to a PO and/or a uid (the product-row
 *  expansion fetches by uid and groups the result by po_number). */
export function getPoFeeLines(
  opts: { poNumber?: string; uid?: string; signal?: AbortSignal } = {},
): Promise<{ count: number; items: PoFeeLine[] }> {
  const p = new URLSearchParams()
  if (opts.poNumber) p.set('po_number', opts.poNumber)
  if (opts.uid) p.set('uid', opts.uid)
  const qs = p.toString()
  return opsFetch(`/api/po-fee-lines${qs ? `?${qs}` : ''}`, { signal: opts.signal })
}
export function addPoFeeLine(body: {
  po_number: string; uid: string; fee_type?: string
  description?: string | null; amount: number
}): Promise<{ ok: boolean; item: PoFeeLine }> {
  return opsFetch(`/api/po-fee-lines`, { method: 'POST', body })
}
export function deletePoFeeLine(id: number): Promise<{ ok: boolean }> {
  return opsFetch(`/api/po-fee-lines/${id}`, { method: 'DELETE' })
}

// ─── Receive Queue manual entry (no docs available) ─────────────────────
export interface ManualLineItemRow {
  po_number: string
  uid: string
  qty: number
  cartons?: number | null
  item_cost_fob?: number | null
}
export interface ManualLineItemsRequest {
  /** Canonical field name. The backend accepted only `items` while this
   *  client sent `rows`, so every submit 422'd; it now takes both, and `rows`
   *  is a deprecated alias kept for one release so an open tab running the
   *  previous bundle keeps working. */
  items: ManualLineItemRow[]
}
export interface ManualLineItemsResponse {
  created: number
  skipped_existing: number
  shipping_number: string
  created_keys: { po_number: string; uid: string; ship_key: string }[]
}
/** POST /api/receive-queue/{ship}/manual-entry — operator-typed line items
 *  for an orphan shipment when no docs exist anywhere (Drive empty + Shifl
 *  inbox empty + no previous_documents). Creates one md_orders row per
 *  payload row, attached to this ship#. */
export function submitManualLineItems(
  shippingNumber: string, body: ManualLineItemsRequest,
): Promise<ManualLineItemsResponse> {
  return opsFetch<ManualLineItemsResponse>(
    `/api/receive-queue/${encodeURIComponent(shippingNumber)}/manual-entry`,
    { method: 'POST', body },
  )
}


export type OrderStatus =
  | 'Ordered'
  | 'Production'
  | 'Ready'
  | 'Scheduled'
  | 'In Transit'
  | 'Customs'
  | 'Received'
  | 'Cancelled'

export type OrderLogEventType = 'po_added' | 'promotion' | 'milestone' | 'edit'

export interface OrderLogEvent {
  id: number
  ts: string
  status_bucket: string
  event_type: OrderLogEventType | string
  entity: 'orders' | 'shipments' | string
  entity_key: string
  po_number: string | null
  uid: string | null
  shipping_number: string | null
  field: string | null
  before_value: string | null
  after_value: string | null
  actor: string | null
  reason: string | null
}

/** Sentinel ship_key value for rows with no shipping_number (post-021). */
export const SHIP_KEY_OPEN = '__OPEN__'

/**
 * Build the canonical /api/orders/{po}/{uid} URL with optional `ship_key`
 * disambiguator. When ship_key is omitted or equals '__OPEN__' the URL is
 * the legacy 2-part form (which the backend resolves to the open row),
 * preserving today's bookmarks.
 */
export function orderUrl(
  po_number: string, uid: string, ship_key?: string, subpath?: string,
): string {
  const base = `/api/orders/${encodeURIComponent(po_number)}/${encodeURIComponent(uid)}`
  const sub = subpath ? `/${subpath}` : ''
  if (!ship_key || ship_key === SHIP_KEY_OPEN) return base + sub
  return `${base}${sub}?ship=${encodeURIComponent(ship_key)}`
}

export interface Order {
  po_number: string
  uid: string
  /** 3-part PK component (post-021). '__OPEN__' for unshipped rows. */
  ship_key: string
  status: OrderStatus
  ordered_date: string | null
  eta: string | null
  /** Derived, read-only projected arrival for lines with no real `eta` yet.
   *  Prefers the linked shipment ETA, else order_date + lead time + transit.
   *  Shown with an "est" marker only when `eta` is null. */
  eta_estimated: string | null
  /** The arrival this line can still credibly make — the stored date floored
   *  at today plus the legs it has not completed. NULL once the line has
   *  arrived or been cancelled. `eta` keeps the original promise. */
  eta_rolled?: string | null
  supplier: string | null
  split: string | null
  shipping_number: string | null
  qty: number | null
  qty_received: number | null
  item_cost_fob: number | null
  cbm_per_unit: number | null
  duty_per_unit: number | null
  invoice_po_cost: number | null
  landed_po_cost: number | null
  total_po_invoice: number | null
  /** Client-derived (not sent by the API): final all-in landed COGS per unit
   *  = landed_po_cost / qty. Computed in the Orders page so the grouped + table
   *  views can show "Landed/u" and sort/heat on it. */
  landed_per_unit?: number | null
  /** Client-derived: the effective carton count (stored, else qty ÷ U/Crtn). */
  cartons_display?: number | null
  /** Client-derived (not sent by the API): the ONE arrival date the Timeline's
   *  merged ETA column shows, and the field it sorts on. Resolution order is
   *  delivered → booked ETA → the shipment's ETA → the derived estimate.
   *  `received_at` is deliberately NOT in that chain: it is a row
   *  write-timestamp, not a receiving date (models.py:2986 and five other
   *  places say so), and rendering it as one is a known defect family. */
  arrival_date?: string | null
  /** Which link of that chain `arrival_date` came from — drives the cell's
   *  chip so a projection can never be mistaken for a recorded arrival. */
  arrival_kind?: 'delivered' | 'rolled' | 'eta' | 'ship' | 'est' | 'none'
  /** Client-derived (PR 18): `problems`' labels joined — what the Timeline's
   *  Problem column filters on. Never a second opinion: read off `problems`. */
  problem_labels?: string
  cartons: number | null
  frozen: boolean
  frozen_at: string | null
  received_at: string | null
  lock_overridden: boolean
  version: number
  // Joined from md_products
  product_name: string | null
  asin: string | null
  /** #74 — joined for SEARCH, not display (no column on the timeline): the
   *  identifiers an operator pastes from Seller Central / GoFlow. Listed in
   *  ORDER_SEARCH_KEYS (Orders.tsx). Optional so the fixtures in
   *  statusPillSelector.test.tsx / ordersBulkTransition.test.ts — which this
   *  change does not own — keep type-checking. */
  seller_sku?: string | null
  goflow_sku?: string | null
  image_url: string | null   // Phase 3C joined catalog image (read-only)
  units_per_carton: number | null
  carton_weight_kg: number | null
  carton_length_cm: number | null
  carton_width_cm: number | null
  carton_height_cm: number | null
  carton_cbm: number | null
  line_cbm: number | null
  /** DERIVED, read-only. The carton count the app works with: stored `cartons`
   *  when positive, else ceil(qty / units_per_carton). `cartons_source` says
   *  which — a 'derived' count is an estimate and must be labelled as one. */
  cartons_effective?: number | null
  cartons_source?: 'stored' | 'derived' | null
  /** DERIVED, read-only. Catalog gaps that stop the carton / CBM math. */
  catalog_missing?: boolean | null
  catalog_missing_case_pack?: boolean | null
  catalog_missing_carton_cbm?: boolean | null
  /** DERIVED, read-only. Why landed_po_cost is blank, when the server can say. */
  landed_blank_reason?:
    | 'frozen' | 'waiting_freight_invoice' | 'not_in_catalog'
    | 'needs_carton_cbm' | 'needs_case_pack' | null
  /** Linked shipment header (via shipping_number). null when no shipment /
   *  sentinel Ship #. ship_delivered_date is the REAL arrival; received_at
   *  is a write-timestamp and is never a date to show.
   *  NOTE: optional (not required) here — making these required breaks tsc
   *  in statusPillSelector.test.tsx and ordersBulkTransition.test.ts, which
   *  this agent does not own (see needs_from_others). Flip to required once
   *  those fixtures carry the four fields. */
  ship_total_cbm?: number | null
  ship_eta?: string | null
  ship_delivered_date?: string | null
  ship_status?: string | null
  /** DERIVED SERVER-SIDE — feedback #108. What this line's row is actually
   *  showing, from ONE predicate (backend/ops/engine/line_stage.py). It folds
   *  the book's `status`, the forwarder's `ship_status`, `frozen` and whether
   *  the shipment's freight/duty are booked into a single answer, so a line is
   *  never displayed behind its own shipment.
   *
   *  NEVER RE-DERIVE THIS IN THE FRONTEND. The Timeline groups on it; a second
   *  opinion written here is exactly how the Timeline came to say "In Transit"
   *  about goods the Shipments tab called Delivered. Optional only because the
   *  hand-rolled test fixtures in this folder predate it. */
  line_stage?: LineStage
  /** DERIVED SERVER-SIDE. Units ordered vs the best receipt evidence we have.
   *  `unknown` means no count anywhere and renders as nothing, never as zero. */
  receipt_check?: ReceiptCheck
  /** Units GoFlow says landed, summed over md_goflow_po_mirror for this
   *  (po, uid). Mirrored since Build 39 and read by nothing but the Purchasing
   *  coverage report until #108 — which is why a line whose only receipt
   *  evidence was GoFlow's showed a blank Qty Rcvd and no discrepancy. */
  goflow_received?: number | null
  /** DERIVED SERVER-SIDE (PR 18, Q1; engine/line_problems.py). Why this open
   *  line is stuck, most actionable first. Empty for a healthy line, always
   *  empty for Received / Cancelled. The Timeline's chip and "Has a problem"
   *  filter read this — NEVER re-derive it here. */
  problems?: LineProblem[]
}

/** Mirrors backend/ops/models.py LineProblemOut. */
export type LineProblemCode = 'nothing_left' | 'no_shipment' | 'eta_passed'
export interface LineProblem {
  code: LineProblemCode | (string & {})
  /** Chip text. */
  label: string
  /** One plain-English sentence naming the numbers and the fix. */
  reason: string
}

/** Mirrors backend/ops/engine/line_stage.py's `LineStage`. The five
 *  `received_*` members are sub-stages of one Received lane: the owner's
 *  mark-Received-on-delivery decision (2026-09-17) creates states a single
 *  "Received" would hide — a provisional landed cost, and a locked line the
 *  delivery could not move. */
export type LineStage =
  | 'received_verified' | 'received_short' | 'received_over'
  | 'received_uncosted' | 'received_locked'
  | 'in_transit' | 'customs' | 'scheduled' | 'ordered' | 'production'
  | 'ready' | 'cancelled'

/** Mirrors backend/ops/engine/line_stage.py's `ReceiptCheck`. */
export type ReceiptCheck = 'verified' | 'short' | 'over' | 'unknown'

/**
 * Per-status transition dates for one order line. See backend StatusDates
 * model for the source-of-truth-per-status semantics.
 */
export interface OrderStatusDates {
  Ordered: string | null
  Production: string | null
  Ready: string | null
  Scheduled: string | null
  'In Transit': string | null
  Customs: string | null
  Received: string | null
  Cancelled: string | null
}

export function getOrderStatusDates(
  po_number: string, uid: string, opts: { signal?: AbortSignal } = {},
): Promise<OrderStatusDates> {
  return opsFetch<OrderStatusDates>(
    `/api/orders/${encodeURIComponent(po_number)}/${encodeURIComponent(uid)}/status-dates`,
    { signal: opts.signal },
  )
}

// --- Item performance (the order row's right-hand question) -------------
//
// The status track says WHEN a line lands. This says whether landing it
// matters. Served from md_pnl_daily, not md_monthly_history: that table
// returns NULL for the whole P&L split on every month, so it cannot carry a
// stacked composition at all.

export interface ItemMonth {
  year_month: string
  units: number
  revenue: number
  net_profit: number
  cogs: number
  fees: number
  ads: number
  /** The month is still running — never compare it with a whole one. */
  partial: boolean
}

export interface ItemPeriod {
  label: string
  days: number
  units: number
  sales: number
  refunds: number
  fees: number
  cogs: number
  ads: number
  net_profit: number
  margin_pct: number | null
  real_acos: number | null
  page_views: number | null
  /** The traffic feed does not cover this whole window, so its page views
   *  span fewer days than the label says and conversion is not computed. */
  traffic_incomplete: boolean
}

export interface ItemPerformance {
  uid: string
  months: ItemMonth[]
  periods: ItemPeriod[]
  traffic_from: string | null
  /** Never sold a unit — velocity, cover and margin are unknown, not zero. */
  never_sold: boolean
  /** Has an ASIN. False means there is nothing to sell this on yet. */
  has_listing: boolean
}

export function getItemPerformance(
  po_number: string, uid: string, signal?: AbortSignal,
): Promise<ItemPerformance> {
  return opsFetch<ItemPerformance>(
    `/api/orders/${encodeURIComponent(po_number)}/${encodeURIComponent(uid)}/item-performance`,
    { signal },
  )
}

// --- Status track (forecast-aware) --------------------------------------
//
// The forward-looking half of the status dates: what the stages this line has
// NOT reached are expected to happen, and how much of that is measured.
// `kind` is the load-bearing field — it is what stops a projection from being
// read as a recorded fact.

export type StatusTrackKind = 'actual' | 'forecast' | 'unknown'

export interface OrderStatusTrackNode {
  status: string
  /** Stage date; for `Production` the EARLY edge of the window. */
  date: string | null
  /** Late edge of the production window. Null on every other stage. */
  date_to: string | null
  kind: StatusTrackKind
  /** Plain-English provenance ("the PO ordered date", "from shipment SH-12"). */
  source: string | null
  /** A forecast the calendar has already passed, on a stage not yet done.
   *  Measured against `date_original`, never against the rolled `date`. */
  late: boolean
  /** What this stage said BEFORE it was rolled forward — set only when the
   *  roll actually moved it, so "was …" never shows the same date twice. */
  date_original: string | null
  /** Whole days past `date_original`; 0 when not late. Drives the badge tier. */
  days_late: number
}

export interface OrderStatusTrack {
  nodes: OrderStatusTrackNode[]
  current: string
  lead_days: number | null
  lead_variability_d: number | null
  lead_source: string | null
  /** Forecast end of production falls after the stored arrival — two stored
   *  facts that cannot both be true. Surfaced, never silently reconciled. */
  impossible: boolean
  transit_days: number | null
  port_days: number | null
  /** Shipments carrying etd+eta+delivered_date that the legs were measured on.
   *  0 means the documented constants stood in. */
  sample_size: number
}

export function getOrderStatusTrack(
  po_number: string, uid: string,
  opts: { ship?: string; signal?: AbortSignal } = {},
): Promise<OrderStatusTrack> {
  const q = opts.ship ? `?ship=${encodeURIComponent(opts.ship)}` : ''
  return opsFetch<OrderStatusTrack>(
    `/api/orders/${encodeURIComponent(po_number)}/${encodeURIComponent(uid)}/status-track${q}`,
    { signal: opts.signal },
  )
}

// ─── Order variants (split-aware) ───────────────────────────────────────
export interface OrderVariantsResponse {
  po_number: string
  uid: string
  variants: Order[]
}
/**
 * Fetch every md_orders row for (po, uid) — one per ship_key. Used by the
 * Receive Documents v2 UID autocomplete to disambiguate the open row from
 * shipped split children.
 */
export function getOrderVariants(
  po_number: string, uid: string, opts: { signal?: AbortSignal } = {},
): Promise<OrderVariantsResponse> {
  return opsFetch<OrderVariantsResponse>(
    `/api/orders/${encodeURIComponent(po_number)}/${encodeURIComponent(uid)}/variants`,
    { signal: opts.signal },
  )
}

// ─── Split an open row into parent (remainder) + child (ship) ───────────
export interface SplitOrderResponse {
  parent: Order
  child: Order
}
export function splitOrder(
  po_number: string, uid: string,
  body: { shipping_number: string; qty: number; cartons?: number | null; reason?: string },
  if_match_version?: number,
): Promise<SplitOrderResponse> {
  const headers: Record<string, string> = {}
  if (if_match_version != null) headers['If-Match'] = String(if_match_version)
  return opsFetch<SplitOrderResponse>(
    `/api/orders/${encodeURIComponent(po_number)}/${encodeURIComponent(uid)}/split`,
    { method: 'POST', body, headers },
  )
}

// ─── Manually create a brand-new order line ─────────────────────────────
// For lines the sync can't produce (a PO whose qty grew after a batch already
// shipped, or a product not yet in the feed). Cost/supplier/dates default off
// an existing (po, uid) row, then the product catalog.
export interface CreateOrderInput {
  po_number: string
  uid: string
  qty: number
  shipping_number?: string | null
  status?: OrderStatus | null
  supplier?: string | null
  item_cost_fob?: number | null
  reason?: string
}
export function createOrder(body: CreateOrderInput): Promise<Order> {
  return opsFetch<Order>('/api/orders', { method: 'POST', body })
}

// ─── Soft-delete an order line (cancel-then-remove / abandoned PO) ───────
// Removes the line from the timeline (recoverable in DB). Optional ship_key
// targets a split child; omit for the open row.
export function deleteOrder(
  po_number: string, uid: string, ship_key?: string, if_match_version?: number,
): Promise<void> {
  const headers: Record<string, string> = {}
  if (if_match_version != null) headers['If-Match'] = String(if_match_version)
  return opsFetch<void>(orderUrl(po_number, uid, ship_key), { method: 'DELETE', headers })
}

// ─── Bulk unit economics for open order lines (Orders timeline PR-10) ────
// GET /api/ops/orders/economics — one entry per uid appearing on a
// non-Received/non-Cancelled md_orders line. Powers the "Potential Sales" /
// "Potential Profit" header chips. `total_cogs_unit` is the forward-looking
// landed+warehouse estimate (unit_economics.total_unit_bulk), not a P&L
// figure. `price_source === 'none'` means there's no price data at all for
// that uid — exclude it from any Potential Sales/Profit sum.
export interface OrderEconomics {
  expected_price: number | null
  price_source: 'history' | 'listed' | 'none' | string
  total_cogs_unit: number | null
  est_fees_unit: number | null
  /** PR 2 — the basis of est_fees_unit's fee legs ('sp_api' | 'manual' |
   *  'catalog' | 'default' | 'mixed'); absent on a pre-PR-2 backend. */
  fees_basis?: string | null
  /** True when either fee leg is the 15% / $3.50 default (an estimate). */
  fees_are_estimate?: boolean
}
export function getOrdersEconomics(
  opts: { signal?: AbortSignal } = {},
): Promise<Record<string, OrderEconomics>> {
  return opsFetch<Record<string, OrderEconomics>>('/api/orders/economics', {
    signal: opts.signal,
  })
}

// ─── UID options for a PO (Receive v2 autocomplete) ─────────────────────
export interface ReceiveUidOptionsResponse {
  po: string
  candidates: ReceiveUidCandidate[]
}
export function getReceiveUidOptions(
  po: string, opts: { signal?: AbortSignal } = {},
): Promise<ReceiveUidOptionsResponse> {
  return opsFetch<ReceiveUidOptionsResponse>(
    `/api/documents/receive/uid-options?po=${encodeURIComponent(po)}`,
    { signal: opts.signal },
  )
}

// Amazon US/CA split (2026-05-28): backend's md_mpl_inventory now stores
// "Amazon US" and "Amazon CA" as separate marketplace keys. The bare
// "Amazon" string is kept as a legacy alias for old links; mpl_restock
// route maps it to "Amazon US" so existing bookmarks keep working.
export type Marketplace =
  | 'Amazon US' | 'Amazon CA' | 'Amazon'
  | 'Walmart' | 'Temu' | 'Ebay' | 'Target' | 'TikTok'

/** Real 3-state listing status, sourced from md_goflow_listings.status
 *  (replaces the old always-true/always-null listing_active signal —
 *  2026-07-09 active-status fix). See components/StatusBadge.tsx. */
export type ListingStatus = 'active' | 'in_review' | 'inactive'

export interface RestockRow {
  uid: string
  product_name: string | null
  asin: string | null
  pack_family: string | null
  case_pack_qty: number | null
  primary_supplier?: string | null
  // FNSKU pool identity (md_sku_aliases). primary_sku is the canonical
  // seller_sku; alias_skus the rest. Empty/null when no aliases known.
  primary_sku?: string | null
  alias_skus?: string[]
  alias_count?: number
  listing_active: boolean | null
  /** Real 3-state listing status from md_goflow_listings.status — replaces
   *  the old always-true listing_active source (2026-07-09 fix). null when
   *  GoFlow has no listing row here (unknown, never guessed as active). */
  listing_status?: ListingStatus | null
  /** Per-marketplace listing identifier (2026-09-07) — the handle you paste
   *  into THAT marketplace's seller console. Kind is 'asin' | 'tcin' |
   *  'walmart_item' | 'ebay_item' | 'temu_goods_id' | 'sku_only'; 'sku_only'
   *  means the marketplace exposes no distinct id and this is our own SKU. */
  listing_id?: string | null
  listing_id_kind?: string | null
  listing_url?: string | null
  fba_on_hand: number | null
  // 2026-06-03 SC parity split: fba_on_hand = fba_available + fba_xfer
  fba_available: number | null
  fba_xfer: number | null
  fba_inbound: number | null
  fba_total: number | null
  fba_reserved: number | null
  fba_unfulfillable: number | null
  warehouse_qty: number | null
  pooled_warehouse_qty: number | null
  on_order: number | null
  /** Build 45 — the slice of `on_order` sitting on a PO line that records a
   *  received quantity but was never moved to Received. Those units reached
   *  warehouse stock too, so while they sit in `on_order` they are counted
   *  twice. Still IN `on_order` (Build 45 moves no number; Build 44 owns the
   *  netting call), so this is a flag, not a deduction. Rendered as the ⚠ on
   *  the On Order column of both the all-marketplaces and per-marketplace
   *  tabs. See backend/ops/engine/inbound_supply.py. */
  on_order_received_not_stocked: number | null
  /** fix-stale-eta-integrity — units the supplier still owes on a PO line
   *  whose ETA is more than 30 days in the past. NOT inside `on_order` —
   *  see backend/ops/engine/inbound_supply.py DECISION 3. > 0 means "this
   *  PO looks abandoned"; triage it in Orders (close it or set a new ETA). */
  overdue_units: number | null
  velocity_7d: number | null
  velocity_30d: number | null
  velocity_90d: number | null
  blended_velocity: number | null
  adjusted_velocity: number | null
  units_30d: number | null
  prior_30d: number | null
  mom_pct: number | null
  days_until_stockout: number | null
  suggest_qty: number | null
  suggest_cases: number | null
  // AI Restock = units to send into the marketplace network (FBA for Amazon).
  // forecast − (fba_on_hand + fba_inbound), floored at 0, case-pack-rounded.
  // Distinct from suggest_qty (which subtracts WH + on-order too).
  ai_restock_qty: number | null
  ai_restock_cases: number | null
  // Uncapped 'ideal' — target demand only, ignores warehouse availability.
  // Surfaced in the AI Restock tooltip alongside the capped actual qty.
  ai_restock_qty_ideal: number | null
  // Why actual differs from ideal: 'covered' | 'warehouse' | 'partial-case' | null.
  ai_restock_cap_reason: 'covered' | 'warehouse' | 'partial-case' | 'pack-family-share' | null
  send_cases: number | null
  // Operator-entered units to send; UI shows a "X cases" subscript
  // derived from case_pack_qty.
  send_qty: number | null
  override_notes: string | null
  override_version: number | null
  is_new_sku: boolean
  /** PR 11 (#94, OD-2) — where adjusted_velocity's trend multiplier came
   *  from. The per-marketplace send suggestion uses the SAME override as the
   *  supplier reorder, so the "Adjusted" header says when one applies. */
  trend_factor_source?: TrendFactorSource | null
}

export type TrendSignal = 'NEW' | 'GROW' | 'FLAT' | 'DROP'

/** PR 11 (#94) — where a row's trend multiplier came from: 'auto' = each
 *  product's own month-over-month (engine/mpl_restock.adj_factor), or the
 *  owner's override for every product / for this product. Mirrors backend
 *  models.TrendFactorSource. */
export type TrendFactorSource = 'auto' | 'all_products' | 'this_product'

export interface SupplierReorderRow {
  uid: string
  product_name: string | null
  asin: string | null
  supplier_code: string | null
  supplier_name: string | null
  case_pack_qty: number | null
  moq: number | null
  unit_fob: number | null
  ai_order_qty: number | null
  manual_order_qty: number | null
  order_qty_effective: number | null
  order_cases: number | null
  order_value: number | null
  order_cbm: number | null
  forecast_lead_60d: number | null
  days_until_stockout: number | null
  total_stock_mpl: number | null
  warehouse_qty: number | null
  on_order: number | null
  /** Build 45 — see RestockRow.on_order_received_not_stocked. */
  on_order_received_not_stocked: number | null
  /** fix-stale-eta-integrity — see RestockRow.overdue_units. */
  overdue_units: number | null
  shipping_units: number | null
  total_units: number | null
  next_eta: string | null
  top_marketplace: string | null
  blended_velocity: number | null
  units_30d: number | null
  prior_30d: number | null
  mtd_units: number | null
  mom_pct: number | null
  yoy_pct: number | null
  avg_12mo: number | null
  trend_signal: TrendSignal | null
  monthly_history: number[] | null
  override_version: number | null
  /** Who last touched the override ROW, and when. A notes-only save moves
   *  this, so never present it as the author or date of the quantity. */
  override_updated_by: string | null
  override_updated_at: string | null
  /** Who set the manual quantity, and when. From the audit chain, so a later
   *  notes edit cannot rewrite it. */
  manual_order_qty_set_by: string | null
  manual_order_qty_set_at: string | null
  /** Whole days since the manual quantity was set. Null when there is no
   *  manual quantity — an age for a number nobody entered would be a lie. */
  manual_order_qty_age_days: number | null
  /** Backend-owned staleness verdict. The server holds PO drafts on exactly
   *  this flag, so the UI must not re-derive it from a duplicated threshold. */
  manual_order_qty_stale: boolean
  /** PR 11 (#94) — the multiplier behind adjusted_velocity and where it came
   *  from. Optional: older payloads predate it. */
  trend_factor?: number | null
  trend_factor_source?: TrendFactorSource | null
  /** PR 11 (I2) — catalog production_lead_days, else the supplier link's.
   *  Null when neither has one (no made-up 30). */
  lead_days?: number | null
}

export interface CreatePODraftResponse {
  created_pos: string[]
  /** `needs_acknowledgement` marks a row skipped only because its manual
   *  quantity is stale; re-sending it in `acknowledge_stale_uids` drafts it. */
  skipped_uids: { uid: string; reason: string; needs_acknowledgement?: boolean }[]
}

export interface DriveFolderRef {
  kind: 'uid' | 'po' | 'shipment' | 'asin'
  key: string
  folder_id: string
  folder_url: string
}

// ---------------- Suppliers ----------------
export interface Supplier {
  supplier_code: string
  company_name: string | null
  primary_contact: string | null
  email: string | null
  phone_wechat: string | null
  payment_terms: string | null
  payment_method: string | null
  production_lead_days: number | null
  lead_time_variability_d: number | null
  transit_days: number | null
  shipping_method: string | null
  freight_forwarder: string | null
  annual_volume_usd: number | null
  products_supplied: string | null
  notes: string | null
  version: number
}

// ---------------- Shipments ----------------
export type ShipmentStatus = 'Booked' | 'In Transit' | 'At Port' | 'Delivered'

/**
 * Which rung of OWNER DECISION 3's carton-CBM cascade produced a line's
 * carton volume, most trustworthy first. Recorded per line so the UI can show
 * provenance instead of implying every volume was measured off a packing list.
 *
 *   own          — the item's own `carton_cbm` (measured, best)
 *   prior_ship   — the same item's `carton_cbm` from a previous shipment
 *   line_cbm     — this line's `line_cbm / cartons`
 *   ship_average — the shipment's `total_cbm / total_cartons` (weakest)
 *   missing      — no rung produced a value; the line contributes nothing
 */
export type CartonCbmRung = 'own' | 'prior_ship' | 'line_cbm' | 'ship_average' | 'missing'

/** Defect classes B1 reports on a shipment. Open-ended on purpose (the string
 *  union is the known set; consumers must tolerate an unknown `kind`). */
export type ShipmentDiscrepancyKind =
  | 'cartons_mismatch'
  | 'units_mismatch'
  | 'cbm_mismatch'
  | 'invoice_mismatch'
  | 'implausible_density'
  | 'missing_carton_cbm'
  | 'unallocatable_freight'
  | 'component_sum_mismatch'

/**
 * ONE surfaced defect on a shipment. Per OWNER DECISION 2 this is never a
 * "which number won" record — it is a statement that two things that should
 * agree do not, naming the datapoint an employee should go fix.
 */
export interface ShipmentDiscrepancy {
  kind: ShipmentDiscrepancyKind | (string & {})
  /** 'error' = the figure is unusable as-is; 'warn' = probably incomplete. */
  severity: 'error' | 'warn'
  /** Plain-English, already names the suspect datapoint. Rendered verbatim. */
  message: string
  /** The md_shipments / md_orders column an employee should go look at. */
  field?: string | null
  /** The figure typed into md_shipments, when this defect compares two. */
  stored?: number | null
  /** The figure derived from the shipment's own Timeline lines. */
  derived?: number | null
}

export interface Shipment {
  shipping_number: string
  status: string | null
  docs_link: string | null
  etd: string | null
  eta: string | null
  delivered_date: string | null
  total_cartons: number | null
  total_units: number | null
  total_cbm: number | null
  container_type: string | null
  container_number: string | null
  origin: string | null
  dest: string | null
  duty_pct: number | null
  cbm_dollar: number | null
  total_shipping_usd: number | null
  ocean_freight_usd: number | null
  brokerage_usd: number | null
  delivery_chassis_usd: number | null
  customs_duty_usd: number | null
  total_invoice_usd: number | null
  total_declared_usd: number | null
  carrier: string | null
  supplier_code: string | null
  notes: string | null
  // Comma-separated cost column names whose current values are temporary
  // placeholders (e.g. the flat $1,000 trucking fee entered while awaiting
  // the real invoice). Flagged cells render amber in the grid; the backend
  // clears a field's flag when a different value is saved into it.
  placeholder_costs?: string | null
  // DERIVED, read-only cargo rollups computed at read time from linked
  // md_orders lines (never written back to md_shipments). Stored
  // total_cartons/total_units/total_cbm above win when present; these fill
  // the blanks on Booked/In-Transit rows and render with an "est" badge.
  // cbm_lines_total/cbm_lines_with_cbm are the coverage denominator/
  // numerator for the partial-CBM badge.
  units_computed?: number | null
  cartons_computed?: number | null
  cbm_computed?: number | null
  /** SUM(qty × FOB) over the shipment's live, non-Cancelled lines. */
  invoice_computed?: number | null
  /** The cost engine's freight pool: stored total_shipping_usd, else the
   *  components (ocean + broker + drayage + other) when an ocean invoice is on
   *  file. NULL = no freight invoice yet, so its lines' landed cost is blank. */
  freight_pool_usd?: number | null
  cbm_lines_total?: number
  cbm_lines_with_cbm?: number
  // ── Cost-derivation program, B1 additions (frozen contract) ─────────────
  // The same four cargo/money roll-ups as `*_computed` above, but named for
  // what they ARE — figures DERIVED from the shipment's own linked Timeline
  // lines rather than typed into md_shipments by the nightly sheet sync.
  //
  // OWNER DECISION 2 (2026-08-05) is binding on every consumer of these:
  // "There should really never be a mismatch. If there is a mismatch, that
  // means ClickPay has some datapoint that is wrong." A stored/derived
  // disagreement is a DEFECT TO SURFACE, never a precedence rule — the UI
  // must show both figures and name the suspect datapoint, and must NEVER
  // silently present one of them as the winner. See
  // `ops/lib/shipmentIntegrity.ts` for the model that enforces this.
  //
  // `derived_*` supersede `*_computed` (kept above so the pre-B1 build keeps
  // rendering); once B1 has shipped everywhere the older names can go.
  derived_cartons?: number | null
  derived_units?: number | null
  derived_cbm?: number | null
  derived_invoice_total?: number | null
  // Fraction 0..1 of this shipment's linked lines that have a usable
  // carton_cbm from ANY rung of the OWNER-DECISION-3 cascade (own carton_cbm
  // → same item on a previous shipment → line_cbm/cartons → shipment
  // total_cbm/total_cartons). 1 = every line's volume is known; anything
  // below 1 means `derived_cbm` is a partial sum and must be badged as such.
  carton_cbm_coverage?: number | null
  // How many of this shipment's lines got their carton_cbm from each rung of
  // that cascade, keyed by rung id. Lets the UI say WHERE a derived volume
  // came from instead of presenting it as if it were measured.
  carton_cbm_rungs?: Partial<Record<CartonCbmRung, number>> | null
  // Machine-readable defects B1 found on this shipment. Rendered verbatim as
  // badges; `message` is already plain English and already names the suspect
  // datapoint, so the UI must not re-word it.
  discrepancies?: ShipmentDiscrepancy[] | null
  version: number
  // Count of linked md_orders rows. >0 means POs are pointing at this ship #;
  // 0 on a non-Booked shipment is a data-integrity warning surfaced in the UI.
  linked_order_count: number
  /** Vessel identity (migration 035) — the backend has always sent these on
   *  ShipmentOut; this interface simply never declared them. Undeclared
   *  fields are not merely undocumented: `searchKeys` is `keyof T`, so an
   *  identifier missing from the type cannot be searched (review MINOR-5). */
  bill_of_lading?: string | null
  vessel_name?: string | null
  voyage_number?: string | null
  /** Typed by a person (Shipments drawer / MMSI column) or backfilled from
   *  AIS. The backend validates both on PATCH (9-digit MMSI, 7-digit IMO with
   *  a check digit) and the Shifl sync never overwrites them. The MMSI is what
   *  live AIS tracking subscribes to (PR 20). */
  vessel_imo?: string | null
  vessel_mmsi?: string | null
  /** #74 — identifiers a shipment can be FOUND by, from its linked md_orders
   *  lines. A shipment header carries no PO number, UID or ASIN, so "which
   *  shipment is PO 1743 on?" had no answer on this page. Not columns —
   *  SHIPMENT_SEARCH_KEYS (Shipments.tsx) feeds them to the search.
   *  Optional: an older cached payload predates them, and an empty list is
   *  the honest value for a shipment with no linked lines. */
  linked_po_numbers?: string[]
  linked_uids?: string[]
  linked_asins?: string[]
  /** PR 18 (Q4): DISTINCT purchase orders among the live linked lines — what
   *  the "POs" column shows. `linked_order_count` is a LINE count. */
  linked_po_count?: number
  /** PR 18 (Q1): why this shipment is stuck (past its ETA while Shifl still
   *  says In Transit / Booked, or past Booked with no live PO line). */
  problems?: ShipmentProblem[]
  /** PR 19 (Q3): the link matcher's open suggestions for this shipment (not
   *  the blocked ones) and its links applied in the last 14 days. 0 when the
   *  matcher is off; absent on an older backend. */
  link_suggestions?: number
  links_recent?: number
  // Provenance fields (surfaced on the drawer): _source identifies the
  // writer (e.g. "shifl_sync", "manual"), _synced_at is when this row was
  // last touched by an ETL/sync, created_at is the original insert time.
  _source?: string | null
  _synced_at?: string | null
  created_at?: string | null
}

/** Mirrors backend/ops/models.py ShipmentProblemOut (PR 18, Q1). */
export interface ShipmentProblem {
  code: 'past_eta' | 'no_po_lines' | (string & {})
  label: string
  reason: string
  severity: 'error' | 'warn' | (string & {})
}
/** GET /api/shipments/problems — only shipments with >= 1 problem. */
export interface ShipmentProblemRow {
  shipping_number: string
  status: string | null
  eta: string | null
  vessel_name: string | null
  problems: ShipmentProblem[]
}
export function getShipmentProblems(
  opts: { signal?: AbortSignal } = {},
): Promise<ShipmentProblemRow[]> {
  return opsFetch<ShipmentProblemRow[]>('/api/shipments/problems', { signal: opts.signal })
}

/** One live shipment the Ship # picker offers (PR 18, Q4). */
export interface ShipmentOption {
  shipping_number: string
  status: string | null
  eta: string | null
  vessel_name: string | null
  origin?: string | null
  dest?: string | null
  container_number?: string | null
}
export interface ShipmentOptions {
  shipments: ShipmentOption[]
  /** The historical placeholders (GOFLOW-HIST, DOMESTIC). Offered only on a
   *  Received / Cancelled line; the server refuses them on an open one. */
  placeholders: { value: string; label: string }[]
}
/** GET /api/shipments/options — live shipments + placeholders, one query. */
export function getShipmentOptions(
  opts: { signal?: AbortSignal } = {},
): Promise<ShipmentOptions> {
  return opsFetch<ShipmentOptions>('/api/shipments/options', { signal: opts.signal })
}

/** Live lines sharing one Ship # that is not a live shipment (admin listing). */
export interface UnknownShipNumberGroup {
  shipping_number: string
  kind: 'placeholder' | 'unknown' | 'deleted_shipment' | (string & {})
  label: string
  lines: number
  pos: number
  units: number
  statuses: Record<string, number>
  /** First 20, sorted. */
  po_numbers: string[]
  po_numbers_truncated: boolean
}
export interface UnknownShipNumbers {
  total_lines: number
  groups: UnknownShipNumberGroup[]
}
/** GET /api/shipments/unknown-ship-numbers — ADMIN, read-only. Lists; never
 *  fixes. (X-Admin only matters in dev mode; under real auth the signed-in
 *  identity decides.) */
export function getUnknownShipNumbers(
  opts: { signal?: AbortSignal } = {},
): Promise<UnknownShipNumbers> {
  return opsFetch<UnknownShipNumbers>('/api/shipments/unknown-ship-numbers', {
    signal: opts.signal, isAdmin: true,
  })
}

// ─── Shipment log ───────────────────────────────────────────────────────
export interface ShipmentLogEvent {
  id: number
  ts: string
  actor: string | null
  action: string
  detail: string | null
}

export async function getShipmentLog(
  ship: string,
  opts: { limit?: number; signal?: AbortSignal } = {},
): Promise<ShipmentLogEvent[]> {
  const limit = opts.limit ?? 50
  const r = await opsFetch<ListResponse<ShipmentLogEvent> | ShipmentLogEvent[]>(
    `/api/shipments/${encodeURIComponent(ship)}/log?limit=${limit}`,
    { signal: opts.signal },
  )
  // Backend may return either a bare array or a {count, items} envelope.
  return Array.isArray(r) ? r : r.items
}

// ─── Vessels (PR 20 — the Shipments tab's Map view) ─────────────────────
// Mirrors backend/ops/models.py VesselOut & friends (routes/vessels.py).
// Read-only. Timestamps are naive UTC strings (the backend convention);
// coordinates are [lon, lat]. Every field after `last_position` is additive
// and null/empty when the backend could not compute it — the endpoint never
// fails because one enrichment did, so the UI must tolerate any of them
// missing.

/** A recognised port (backend engine/ports.py), keyed by UN/LOCODE. */
export interface Port {
  code: string
  name: string
  lat: number
  lon: number
}

/** The origin → destination line the map draws. `kind` 'searoute' is real
 *  shipping lanes (the optional searoute package); 'sea_lanes' is the bundled
 *  lane graph used without it (backend engine/sea_lanes.py). Neither crosses a
 *  continent; a port pair with no lane has no route. Longitudes are UNWRAPPED
 *  (continuous across the antimeridian: a Pacific crossing runs 114 → 241);
 *  `toMapLines` splits them for drawing. `split_index` is the vertex nearest
 *  the ship: coordinates[0..split_index] travelled, the rest still to go. */
export interface VesselRoute {
  kind: 'searoute' | 'sea_lanes'
  coordinates: [number, number][]
  length_nm: number
  split_index: number | null
}

/** Where the map draws the ship. 'ais' = a fix under 6 h old (solid icon);
 *  'estimated' = placed on the route (hollow + "est."), see `estimate_basis`;
 *  'ais_stale' = the last fix, older than 6 h, with no route to estimate on,
 *  or one that is not following the route (it already left the destination
 *  port, or was sailing away from it) — shown where it was last seen.
 *  `heading` is the DISPLAY heading (AIS heading, else course, else the route
 *  bearing). `route_fraction` is 0..1. */
export interface VesselMapPosition {
  lat: number
  lon: number
  source: 'ais' | 'ais_stale' | 'estimated'
  ts: string | null
  age_seconds: number | null
  sog: number | null
  cog: number | null
  heading: number | null
  nav_status: number | null
  route_fraction: number | null
  /** In Transit and the carrier's ETA date is over in the account's day
   *  (America/Los_Angeles) — the same for every `source`. */
  overdue: boolean
  /** source 'estimated' only (PR 20b): 'schedule' = the ETD → ETA
   *  elapsed-time share of the route; 'last_fix' = moved forward from the
   *  last real fix (`basis_fix_ts`, `basis_age_seconds` old) along the route
   *  at `assumed_speed_kn` (its last speed, capped at 18 kn; 0 = it was
   *  stopped there), never past the destination. Absent on an old backend. */
  estimate_basis?: 'schedule' | 'last_fix' | null
  basis_fix_ts?: string | null
  basis_age_seconds?: number | null
  assumed_speed_kn?: number | null
}

/** AIS shows the ship stopped in its destination port area since the sailing
 *  (backend vessel_routes.port_call). Display only — the shipment status is
 *  never written from it. 'at_port' = the newest fix is in the port area;
 *  'left_port' = it stopped there and has since left (`left_at`). `how` is
 *  what the newest stopped in-area fix says. Times naive UTC. */
export interface VesselPortCall {
  state: 'at_port' | 'left_port'
  port_code: string
  port_name: string
  arrived_at: string
  left_at: string | null
  last_fix_at: string
  how: 'moored' | 'at_anchor' | 'stopped'
}

/** The last stored AIS fix (kept for back-compat; the map reads `position`). */
export interface VesselLastPosition {
  ts: string
  lat: number
  lon: number
  sog: number | null
  cog: number | null
  source: string
  age_seconds: number
}

/** The ship's own latest broadcast. `destination` is free text as sent
 *  ('USNYC', 'CNYTN>USNYC'); `ais_eta` is its ETA to THAT port. */
export interface VesselAisStatic {
  destination: string | null
  ais_eta: string | null
  call_sign: string | null
  ship_type: number | null
  updated_at: string | null
}

/** Carrier ETA vs the ship's AIS ETA. `comparable` only when the ship is
 *  headed for this shipment's discharge port; `differs` when comparable and
 *  on different calendar days. diff_hours = (AIS − carrier) / 3600.
 *  `reason` (null when comparable) says why not — only 'different_port'
 *  means the ship is really calling somewhere else first. */
export interface EtaCheck {
  carrier_eta: string | null
  ais_eta: string | null
  comparable: boolean
  differs: boolean
  diff_hours: number | null
  reason?: 'different_port' | 'unknown_destination' | 'unknown_discharge_port'
    | 'no_ais_eta' | 'no_carrier_eta' | null
}

/** One PO line on board (md_orders on the shipment's ship_key). */
export interface VesselCargoLine {
  po_number: string | null
  uid: string | null
  product_name: string | null
  units: number
}

/** A ship the MMSI auto-resolver saw near the port. */
export interface VesselCandidate {
  mmsi?: string | null
  imo?: string | null
  name?: string | null
  ship_type?: number | null
  seen?: number | null
}

/** Latest MMSI auto-resolution outcome for a shipment (off by default). */
export interface VesselResolution {
  outcome: 'resolved' | 'ambiguous' | 'not_seen' | 'no_port' | (string & {})
  checked_at: string
  candidates: VesselCandidate[]
}

export interface Vessel {
  shipping_number: string
  status: string | null
  vessel_name: string | null
  voyage_number: string | null
  vessel_imo: string | null
  vessel_mmsi: string | null
  bill_of_lading: string | null
  carrier: string | null
  origin: string | null
  dest: string | null
  etd: string | null
  eta: string | null
  last_position?: VesselLastPosition | null
  origin_port?: Port | null
  dest_port?: Port | null
  route?: VesselRoute | null
  position?: VesselMapPosition | null
  ais_static?: VesselAisStatic | null
  eta_check?: EtaCheck | null
  cargo?: VesselCargoLine[]
  cargo_units?: number
  resolution?: VesselResolution | null
  /** PR 20b: null unless the shipment is not yet At Port / Delivered and
   *  AIS shows the ship stopped at its destination port. */
  ais_port_call?: VesselPortCall | null
}

/** GET /api/ops/vessels/health — is live AIS tracking working? `summary` is
 *  one plain-English line written by the backend for the map header.
 *  `last_message_at` is the newest AIS data this database has heard (the
 *  running consumer or the stored rows), so it survives a restart. */
export interface VesselHealth {
  enabled: boolean
  resolver_enabled: boolean
  running_here: boolean
  connected: boolean
  subscribed: number | null
  last_message_at: string | null
  last_position_at: string | null
  heartbeat_at: string | null
  ships_with_live_fix: number
  ships_tracked: number
  status: 'ok' | 'off' | 'unknown' | 'error' | 'degraded' | 'skipped' | (string & {})
  summary: string
}

/** Active shipments (Booked / In Transit / At Port) with a vessel. */
export function getVessels(): Promise<ListResponse<Vessel>> {
  return opsFetch<ListResponse<Vessel>>('/api/vessels')
}

/** One shipment's vessel row, any status (the Shipments drawer). 404 when
 *  the shipment does not exist. */
export function getVessel(ship: string): Promise<Vessel> {
  return opsFetch<Vessel>(`/api/vessels/${encodeURIComponent(ship)}`)
}

export function getVesselHealth(): Promise<VesselHealth> {
  return opsFetch<VesselHealth>('/api/vessels/health')
}

// ─── Per-line cost breakdown (cost-derivation program, B2 frozen contract) ──
//
// The owner's requirement, verbatim (2026-08-05): "I want the columns and
// datapoints to make sense and be connected like a google sheet would be. I
// should be able to see math and have it make sense." This endpoint answers
// "how did this line's landed cost get to that number?" by returning every
// leg of FOB → freight → duty → landed WITH the inputs that produced it, so
// the UI renders the actual arithmetic instead of a final figure the employee
// has to take on faith.
//
// Nothing here is a second cost engine: the backend returns what
// `engine/cost.py` actually computed, plus the inputs it used. When an input
// had to be substituted (an estimated duty %, a carton volume borrowed from a
// previous shipment) the corresponding leg carries `estimated: true` and the
// substitution shows up in `inputs.carton_cbm.rung` / `missing[]`.

/** One leg's contribution, in $/unit and in total. */
export interface CostLegFob {
  per_unit: number | null
  qty: number | null
  total: number | null
}

/** Freight leg — allocated by this line's share of the shipment's CBM. */
export interface CostLegFreight {
  /** Invoiced freight for the whole shipment (the pool being divided). */
  pool: number | null
  /** This line's cubic meters. */
  line_cbm: number | null
  /** The denominator: summed CBM across every line on the shipment. */
  basis_cbm_total: number | null
  /** line_cbm / basis_cbm_total, 0..1. */
  share: number | null
  per_unit: number | null
  total: number | null
  /** True when any input was substituted rather than measured/invoiced. */
  estimated: boolean
}

/** Duty leg — either a share of the shipment's invoiced customs duty
 *  ('fob_share') or the legacy `item_cost_fob × duty_pct` ('pct_estimate'). */
export interface CostLegDuty {
  pool: number | null
  basis: 'fob_share' | 'pct_estimate'
  pct: number | null
  per_unit: number | null
  total: number | null
  estimated: boolean
}

export interface CostLegLanded {
  per_unit: number | null
  total: number | null
}

export interface CostBreakdownLegs {
  fob: CostLegFob
  freight: CostLegFreight
  duty: CostLegDuty
  landed: CostLegLanded
}

/** The carton volume used for this line, and WHICH rung of OWNER DECISION 3's
 *  cascade produced it (see `CartonCbmRung`). `inputs` carries the raw numbers
 *  that rung consumed, so the UI can show e.g. "10 CBM ÷ 10 cartons". */
export interface CostBreakdownCartonCbm {
  value: number | null
  rung: CartonCbmRung
  inputs: Record<string, number | null>
}

export interface CostBreakdownInputs {
  carton_cbm: CostBreakdownCartonCbm
  cartons: number | null
  line_cbm: number | null
  /** The $/CBM figure the Shipments tab displays. DISPLAY ONLY — it is itself
   *  derived (total_shipping_usd / total_cbm) and is not an input to any leg
   *  above; shown so an employee can reconcile the two screens. */
  cbm_dollar_display: number | null
}

/** Which storage layer each figure came from, so the UI can say whether it is
 *  reading the live columns (what the P&L books) or the computed shadow
 *  (what COGS-hub / Restock / the SellerLegend push read). */
export interface CostBreakdownSources {
  live: boolean
  computed: boolean
  frozen: boolean
  computed_at: string | null
}

export interface CostBreakdown {
  legs: CostBreakdownLegs
  inputs: CostBreakdownInputs
  sources: CostBreakdownSources
  /** Human-readable arithmetic, one line per step, already formatted — e.g.
   *  "freight = 1.83 CBM ÷ 67.76 CBM × $4,890.00 = $132.05". Rendered as-is. */
  arithmetic: string[]
  /** Inputs that were absent, named in plain English. Non-empty means the
   *  landed figure above is incomplete and the UI must say so. */
  missing: string[]
}

/**
 * GET /api/ops/cost-breakdown/{po}/{uid}/{ship_key} — the FOB → freight →
 * duty → landed derivation for ONE Timeline line.
 *
 * `shipKey` is the line's `ship_key`, NOT its `shipping_number`: an unshipped
 * remainder line lives under the `__OPEN__` sentinel and still has a cost
 * breakdown (FOB only, no freight pool to divide). Pass the raw value from
 * the row; encoding is handled here.
 */
export async function getCostBreakdown(
  po: string,
  uid: string,
  shipKey: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CostBreakdown> {
  const p = encodeURIComponent(po)
  const u = encodeURIComponent(uid)
  const s = encodeURIComponent(shipKey)
  return opsFetch<CostBreakdown>(`/api/cost-breakdown/${p}/${u}/${s}`, {
    signal: opts.signal,
  })
}

// ---------------- FBA Shipments (Local warehouse -> FBA) ----------------
export type FbaShipmentStatus = 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'

export interface FbaShipment {
  fba_shipment_id: string
  name: string | null
  status: string | null
  destination_fc: string | null
  created_date: string | null
  shipped_date: string | null
  delivered_date: string | null
  cartons: number | null
  pallets: number | null
  units: number | null
  carrier: string | null
  // TRUE = Amazon's partnered-carrier program booked the truck; that cost
  // already flows through Amazon's FBA fees. amazon_trucking_cost is
  // DISPLAY-ONLY in that case — never summed into any total.
  amazon_partnered: boolean
  amazon_trucking_cost: number | null
  // Real cost when WE booked the carrier — feeds the P&L's own
  // "Seller-Purchased Trucking" fee category. NEVER landed COGS. The
  // manual-entry / attach-invoice path only applies when amazon_partnered
  // is false (owner rule 3).
  seller_trucking_cost: number | null
  seller_trucking_invoice_ref: string | null
  invoice_doc_path: string | null
  notes: string | null
  _source?: string | null
  _synced_at?: string | null
  created_at?: string | null
  created_by?: string | null
  version: number
}

export interface FbaInvoiceAttachResult {
  fba_shipment_id: string
  extracted_fba_shipment_id: string | null
  extracted_total: number | null
  extracted_carrier: string | null
  extracted_pickup_date: string | null
  extracted_delivery_date: string | null
  seller_trucking_cost: number
  invoice_doc_path: string | null
  version: number
}

export async function attachFbaInvoice(
  fbaShipmentId: string,
  file: File,
  opts: { ifMatch?: number } = {},
): Promise<FbaInvoiceAttachResult> {
  const fd = new FormData()
  fd.append('upload', file)
  // NOTE: pass the path WITHOUT the /ops segment — withApiBase() inserts it
  // (`/api/X` → `/api/ops/X`). Writing `/api/ops/...` here double-prefixes to
  // `/api/ops/ops/...` and 404s (the launch-day FBA-tab bug, 2026-07-08).
  return opsFetch<FbaInvoiceAttachResult>(
    `/api/fba-shipments/${encodeURIComponent(fbaShipmentId)}/attach-invoice`,
    { method: 'POST', body: fd, isMultipart: true, ifMatch: opts.ifMatch },
  )
}

// ---------------- Payments ----------------
export type PaymentStatus = 'Pending' | 'Paid' | 'Partial'

export interface Payment {
  po_number: string
  supplier_code: string | null
  supplier_name: string | null
  deposit_status: PaymentStatus | null
  balance_status: PaymentStatus | null
  total_po_value: number | null
  deposit_pct: number | null
  deposit_amount: number | null
  deposit_date: string | null
  balance_amount: number | null
  balance_due_date: string | null
  payment_method: string | null
  docs_link: string | null
  notes: string | null
  // PO cost rollups (joined from md_orders, 2026-05-27).
  // NULL when no orders match po_number (orphan payment) or when the cost
  // engine hasn't populated landed/invoice yet on a brand-new PO.
  invoice_total: number | null
  landed_total: number | null
  total_qty: number | null
  total_cartons: number | null
  version: number
}

/** PATCH a PO payment record (optimistic concurrency via `ifMatch`).
 *  PO numbers can contain spaces and parens (e.g. "5488-A (NEW)"), so the
 *  path segment MUST be percent-encoded — an unencoded PO silently 404s /
 *  matches the wrong row (2026-08-14 Payments URL-encoding defect). */
export function patchPayment(
  po: string, body: Record<string, unknown>, ifMatch: number,
): Promise<Payment> {
  return opsFetch<Payment>(`/api/payments/${encodeURIComponent(po)}`, {
    method: 'PATCH', body, ifMatch,
  })
}

/** Mark a PO payment leg (deposit/balance) Paid. See `patchPayment` for why
 *  the PO number must be encoded. */
export function markPoPaid(po: string, leg: 'deposit' | 'balance'): Promise<Payment> {
  return opsFetch<Payment>(`/api/payments/${encodeURIComponent(po)}/mark-paid`, {
    method: 'POST', body: { leg },
  })
}

// ---------------- Logistics ----------------
export interface LogisticsRoute {
  id: number
  route: string | null
  mode: string | null
  transit_days: string | null
  cost_per_container: string | null
  cost_per_cbm_lcl: string | null
  us_drayage: string | null
  customs_broker: string | null
  total_port_to_warehouse: string | null
  version: number
}

// ---------------- Product Managers ----------------
export interface ProductManager {
  asin: string
  manager: string | null
  version: number
}

// ---------------- Folder Map ----------------
export type FolderKind = 'uid' | 'po' | 'shipment' | 'asin'

export interface FolderMapping {
  kind: string
  key: string
  folder_id: string | null
  folder_url: string | null
  notes: string | null
  version: number
}

// ---------------- Restock Hub ----------------
export type HubMarketplace =
  | 'Amazon US' | 'Amazon CA' | 'Walmart' | 'Target+'
  | 'Temu' | 'eBay' | 'TikTok' | 'Shopify'

export type ForecastKind = 'holtwinters' | 'ets_no_season' | 'formula' | 'damped_seasonal'

export interface ForecastPoint {
  year_month: string
  units: number
  revenue_est: number
}

export interface MonthlyHistoryPoint {
  year_month: string
  units: number
  /** From md_monthly_history.revenue. Null when not populated by the sync job. */
  revenue?: number | null
  /** revenue × current net_margin_pct_blended (snapshot approximation;
   *  doesn't reflect price/cost drift over the 12 months). */
  net_margin_usd_approx?: number | null
  /** Monthly P&L composition from md_pnl_daily (owner 2026-06-24) — powers the
   *  stacked Revenue bars (net_profit + cogs + amazon_fees + ad_spend ≈
   *  revenue). Historical months only; null on forecasts + uncovered months. */
  net_profit?: number | null
  cogs?: number | null
  amazon_fees?: number | null
  ad_spend?: number | null
  /** Forward-looking projection rather than actual history. Enriched
   *  client-side in RestockHub.tsx using adjusted_velocity × days/month
   *  × last-3-month avg revenue/unit. Rendered with a distinct visual
   *  treatment by HistoryChartCard (lighter fill + striped pattern). */
  is_forecast?: boolean
  /** Lower bound of the 80% confidence interval for this forecast row
   *  (units). units_lower = max(0, units - Z·σ_d·√months_ahead). Only
   *  present when is_forecast=true. Shaded as a band by the chart. */
  units_lower?: number | null
  /** Upper bound of the 80% confidence interval. units_upper = units +
   *  Z·σ_d·√months_ahead. Only present when is_forecast=true. */
  units_upper?: number | null
  /** CLIENT-COMPUTED in buildChartSeries (RestockHub.tsx): for the in-progress
   *  calendar month only, the units we still expect to sell this month =
   *  max(0, forecast_total_this_month − units_sold_so_far). HistoryChartCard
   *  renders it as a striped segment STACKED on top of the solid (confirmed
   *  MTD) bar, so the two together reach the month's forecast total. Absent on
   *  every other row. */
  forecast_remainder?: number | null
  /** CLIENT-COMPUTED companion to forecast_remainder: the full forecast total
   *  for the in-progress month (units_sold_so_far + forecast_remainder). Used
   *  for the stacked bar's tooltip. */
  forecast_total?: number | null
}

/** One day of FBA stock from md_inventory_daily (Group C #8). Two of these
 *  series (Available + Total) are overlaid as lines on the monthly bars. */
export interface DailyStockPoint {
  day: string                // 'YYYY-MM-DD'
  fba_available: number
  fba_total: number
  /** Local warehouse on-hand that day (pooled). The aggregate chart burns this
   *  down to its OOS date; marketplace tabs use it only as context. */
  warehouse_qty?: number
}

/** One day of units sold from md_orders_daily (Amazon US/CA). Daily-grain
 *  companion to the monthly history; drawn as faint per-day bars under the
 *  stock curve in the chart's Daily view. */
export interface DailyUnitsPoint {
  day: string                // 'YYYY-MM-DD'
  units: number
}

/** Out-of-stock projection — the forward extension of the Available line.
 *  oos_date = today + floor(available_now / velocity_per_day); null when
 *  velocity_per_day <= 0 (chart renders the line flat, no projection). */
export interface InventoryDailyOOS {
  available_now: number
  velocity_per_day: number
  oos_date: string | null
  /** 'warehouse' on the aggregate view (OOS = local warehouse runout),
   *  'marketplace' on a per-MP view (OOS = that MP's sellable FBA runout). */
  basis?: string
}

/** One day of PPC ad spend + ad sales for a UID (chart cross-overlay). Sourced
 *  from ppc_asin_daily (+ SD) via the cp_item_identity bridge, aligned to the
 *  same day axis as the stock `points`. Optional (default-off) overlay lines on
 *  the Restock chart's money axis. Empty when the local ad tables are absent. */
export interface DailyPpcPoint {
  day: string                // 'YYYY-MM-DD'
  ad_spend: number
  ad_sales: number
}

export interface InventoryDailyResponse {
  points: DailyStockPoint[]
  /** Daily units sold, aligned to the same day axis as `points`. Empty until
   *  the first sync_orders_daily run. */
  sales?: DailyUnitsPoint[]
  /** Daily PPC ad spend + ad sales, aligned to the same day axis as `points`.
   *  Optional (default-off) overlay on the chart. Empty until the first ppc
   *  mirror sync / when the local ad tables are absent (split mode). */
  ppc?: DailyPpcPoint[]
  oos: InventoryDailyOOS
}

export interface MarketplaceBreakdown {
  marketplace: string
  // FNSKU pool identity (md_sku_aliases). primary_sku is the canonical
  // seller_sku; alias_skus the rest. Empty/null when no aliases known.
  primary_sku?: string | null
  alias_skus?: string[]
  alias_count?: number
  listing_active: boolean | null
  /** Real 3-state listing status from md_goflow_listings.status — replaces
   *  the old always-true listing_active source (2026-07-09 fix). null when
   *  GoFlow has no listing row here (unknown, never guessed as active). */
  listing_status?: ListingStatus | null
  /** Per-marketplace listing identifier (2026-09-07) — the handle you paste
   *  into THAT marketplace's seller console. `listing_id_kind` names what it
   *  is ('asin' | 'tcin' | 'walmart_item' | 'ebay_item' | 'temu_goods_id' |
   *  'sku_only') so the UI can label it instead of showing a bare number.
   *  'sku_only' means the marketplace exposes no distinct id and this is our
   *  own SKU. null when GoFlow has no listing row for the pair. */
  listing_id?: string | null
  listing_id_kind?: string | null
  listing_url?: string | null
  sale_price: number | null
  fba_on_hand: number | null
  fba_inbound: number | null
  fba_reserved: number | null
  /** PR 24: FBA stock = available + reserved + in transfer (inbound apart). */
  fba_stock?: number | null
  /** Units moving between Amazon warehouses (on hand less available). */
  fba_transfer?: number | null
  fba_unfulfillable: number | null
  fba_total: number | null
  /** Walmart-only WFS on-hand/inbound (#252). null/0 on every non-Walmart
   *  marketplace. Folded into fba_total for Walmart already server-side. */
  wfs_on_hand?: number | null
  wfs_inbound?: number | null
  velocity_7d: number | null
  velocity_30d: number | null
  velocity_90d: number | null
  blended_velocity: number | null
  /** Trend-adjusted blended velocity for THIS marketplace alone. Mirrors
   *  RestockHubRow.adjusted_velocity but per-MP. Surface in any drawer-
   *  matrix row labelled 'Adjusted' so the same label = same recipe at
   *  both scopes. */
  adjusted_velocity: number | null
  units_30d: number | null
  prior_30d: number | null
  mom_pct: number | null
  gross_margin_pct: number | null
  net_margin_pct: number | null                     // current snapshot, NO ads
  net_margin_pct_after_ppc_30d: number | null       // period-weighted, WITH ads
  /** ALWAYS `[]` on the Restock Hub LIST response as of Build 48 — this
   *  field (× 12 points × 8 marketplaces × ~500 rows) was the dominant
   *  share of the list payload's 6.67MB. Fetch the real values lazily via
   *  `getRestockHubHistory(uid)` when a row's drawer expands; never read
   *  this field off a `getRestockHub()` row directly. */
  history: MonthlyHistoryPoint[]
  /** ALWAYS `[]` on the list response — see `history` above. */
  forecast: ForecastPoint[]
  /** ALWAYS `null` on the list response — see `history` above. */
  forecast_kind: ForecastKind | null
}

/** One open purchase order behind a UID's `on_order`.
 *
 * Grain is (uid, po_number) — a split PO's shipped children are summed
 * server-side, so a PO appears exactly once. Sourced from the same engine
 * pass that produces `on_order`, so `sum(on_order)` over these equals
 * `SupplierReorderDetails.on_order` by construction. */
export interface OpenPoLine {
  po_number: string
  /** Units the supplier still owes, net of the GoFlow mirror's receipt. */
  open_units: number
  /** Units a line records as received on a PO nobody closed. Counted INSIDE
   *  on_order and double-counted against warehouse stock while it sits
   *  there — see backend engine/inbound_supply.py DECISION 2. */
  received_not_stocked: number
  /** open_units + received_not_stocked. */
  on_order: number
  /** Units on a line whose ETA has gone stale. A SUBSET of open_units and a
   *  FLAG — never subtract it (DECISION 3, owner instruction 2026-08-17). */
  overdue_units: number
  /** Earliest stage across this PO's lines: what still gates arrival. */
  status: string
  /** Earliest ETA across this PO's lines, 'YYYY-MM-DD', or null. */
  eta?: string | null
  shipping_number?: string | null
}

export interface SupplierReorderDetails {
  supplier_code: string | null
  supplier_name: string | null
  case_pack_qty: number | null
  moq: number | null
  unit_fob: number | null
  lead_days: number | null
  ai_order_qty: number | null
  manual_order_qty: number | null
  order_qty_effective: number | null
  order_cases: number | null
  order_value: number | null
  order_cbm: number | null
  forecast_lead_60d: number | null
  /** The seasonal demand forecast over `cover_target_days`, in units, AS THE
   *  ENGINE COMPUTED IT (backend engine/supplier_reorder.
   *  forecast_seasonal_window). NOT `adjusted_velocity × cover_target_days`:
   *  the engine walks the window month by month applying a per-calendar-month
   *  seasonality lift, so the flat product is wrong on every seasonal SKU.
   *  Never re-derive it here — that re-derivation is what made the AI Order
   *  Qty card explain arithmetic nobody ran. */
  forecast_window_units?: number | null
  /** Total Stock as the engine SUBTRACTED it: warehouse_qty + other_wh_qty +
   *  wfs_on_hand + on_order + FBA (on-hand + inbound). The drawer used to
   *  rebuild this as `fba + warehouse_qty + on_order`, dropping the
   *  non-Default warehouses and Walmart WFS — so the card showed less supply
   *  than the engine had, and advertised a bigger shortfall than the one it
   *  ordered against. */
  existing_supply?: number | null
  /** Units owned but not yet in our warehouse / FBA — POs in any status
   *  except Received or Cancelled (Ordered + Production + Ready + Scheduled
   *  + In Transit + Customs). Required for an honest "Total Stock" count: a
   *  SKU with 13,500 on a PO in Production owns those units; just hasn't
   *  received them yet. */
  on_order: number | null
  /** Build 45 — the slice of on_order whose PO line records a received
   *  quantity but was never moved to Received, so those units are counted
   *  both here and in warehouse stock. Counted INSIDE on_order, so this
   *  flags a stuck PO rather than reducing supply.
   *  See backend/ops/engine/inbound_supply.py. */
  on_order_received_not_stocked: number | null
  /** fix-stale-eta-integrity — units the supplier still owes on a PO line
   *  whose ETA is more than 30 days in the past. NOT counted inside
   *  `on_order` (a third bucket, not a deduction) — see
   *  backend/ops/engine/inbound_supply.py DECISION 3. > 0 means "this PO
   *  looks abandoned"; triage it in Orders (close it or set a new ETA). */
  overdue_units: number | null
  /** Subset of on_order that's already left the supplier (In Transit +
   *  Customs only). Useful for ETA-aware urgency. */
  shipping_units: number | null
  monthly_history: number[] | null
  avg_12mo: number | null
  /** Rich per-month history (units + revenue + margin $ approx) for the
   *  full-featured HistoryChartCard. Oldest→newest, ≤13 entries — 13 so the
   *  same calendar month one year ago is always the first bar. */
  monthly_points?: MonthlyHistoryPoint[] | null
  /** Forward forecast points (is_forecast=true, oldest→newest) from the
   *  backend engine (forecast.py). The chart appends these after the actuals
   *  instead of computing its own client-side projection. forecast_kind names
   *  the model that produced them (e.g. 'damped_seasonal'). */
  forecast_points?: MonthlyHistoryPoint[] | null
  forecast_kind?: ForecastKind | null
  /** The cover window (days) the AI order qty was actually computed against
   *  — engine/supplier_reorder.cover_target_days(), owner-settable since
   *  ops/095, default 270. The drawer's math chain and the chart's forecast
   *  horizon read THIS, not a client-side literal: a setting that reached
   *  the order math but not the screen would leave the card showing one
   *  number while the engine bought another. */
  cover_target_days?: number | null
  /** The POs behind `on_order`, soonest ETA first (undated last). Null — not
   *  [] — when nothing is open, so the UI renders no block rather than an
   *  empty one. Always sums to `on_order`. */
  open_po_lines?: OpenPoLine[] | null
}

export interface RestockPoProblem {
  po_number: string
  qty: number
  eta: string | null
  codes: string[]
  label: string
  reason: string
}

export interface RestockHubRow {
  key: string
  uid: string | null
  name: string | null
  asin: string | null
  image_url: string | null
  pack_family: string | null
  variation_family: string | null
  case_pack: number | null
  lead_days: number | null
  landed_unit_cost: number | null
  /** True when landed_unit_cost is MODELLED (an FOB or catalog cost grossed up
   *  by a default freight+duty %), not a document-backed landed cost from a
   *  received PO. Computed in restock_hub for months and never emitted, so an
   *  estimate rendered identically to a real cost. */
  landed_is_estimate?: boolean
  /** PR 24 (K2): Restock reads the product facts' cost. The basis ('po',
   *  'fifo', 'estimate', …) and the plain-English label for it ("PO 1753
   *  landed", "FIFO seed (est.)"). */
  landed_cost_basis?: string | null
  landed_cost_label?: string | null
  primary_supplier?: string | null
  warehouse_qty: number | null
  /** Non-Default local warehouses (West Coast, 297, …). Sent by the backend
   *  (restock_hub.py:1432) but historically dropped by the client Total Stock
   *  sum. Under #252 warehouse_qty is Default-ONLY, so Total Stock must add
   *  this back or it undercounts local stock. */
  other_wh_qty: number | null
  /** Walmart WFS on-hand — its own bucket, not a local warehouse
   *  (restock_hub.py:1433). Same #252 undercount fix: include in Total Stock. */
  wfs_on_hand: number | null
  fba_on_hand_all_mp: number | null
  fba_inbound_all_mp: number | null
  /** PR 24: reserved units, and FBA stock (available + reserved + in transfer),
   *  every marketplace. */
  fba_reserved_all_mp?: number | null
  fba_stock_all_mp?: number | null
  blended_velocity: number | null
  /** Trend-adjusted blended velocity (= blended × adj_factor(mom)). This is
   *  the value that actually drives the AI Order suggestion + days_until_stockout.
   *  Surface this in any table cell labelled "Blended" so the column matches
   *  the drawer's "Adj blended" + the AI math. Falls back to blended_velocity
   *  when null (group rows). */
  adjusted_velocity?: number | null
  days_until_stockout: number | null
  suggest_send_qty: number | null
  suggest_send_cases: number | null
  manual_send_qty: number | null
  ai_order_qty: number | null
  manual_order_qty: number | null
  /** Who typed `manual_order_qty`, and how many whole days ago. From the
   *  audit chain, so a later notes-only save cannot rewrite them. Null when
   *  nobody entered a quantity — an age for a number nobody typed is a lie. */
  manual_order_qty_set_by: string | null
  manual_order_qty_age_days: number | null
  /** Backend-owned staleness verdict, and the SAME flag that holds a PO
   *  draft (engine/supplier_reorder.py). Render it, never re-derive it — a
   *  second day threshold on the client is how the warning and the refusal
   *  drift apart. Always false on group rollup rows. */
  manual_order_qty_stale: boolean
  order_cases: number | null
  order_value: number | null
  gross_margin_pct_blended: number | null
  net_margin_pct_blended: number | null
  /** Period-aggregate units from md_monthly_history. 30d/120d are the last
   *  1/4 monthly history rows summed; 12mo is the full window. */
  units_30d_history?: number | null
  units_120d_history?: number | null
  units_12mo?: number | null
  /** Net margin $ approximation per period — monthly revenue × current
   *  net_margin_pct_blended. Snapshot, not historical. See backend model
   *  docstring for the caveat. */
  net_margin_usd_30d_approx?: number | null
  net_margin_usd_120d_approx?: number | null
  net_margin_usd_365d_approx?: number | null
  net_margin_pct_30d_approx?: number | null
  net_margin_pct_120d_approx?: number | null
  net_margin_pct_365d_approx?: number | null
  net_margin_pct_after_ppc_30d_approx?: number | null
  net_margin_pct_after_ppc_120d_approx?: number | null
  net_margin_pct_after_ppc_365d_approx?: number | null
  trend_signal: TrendSignal | null
  primary_marketplace: string | null
  override_version: number | null
  active_marketplaces: string[] | null
  supplier_reorder_details: SupplierReorderDetails | null
  marketplaces: MarketplaceBreakdown[]
  /** Client-derived: warehouse_qty + fba_on_hand_all_mp + fba_inbound_all_mp
   *  + supplier_reorder_details.on_order. Mirrors what the AI Order math
   *  treats as "existing supply" so the column and the recommendation are
   *  denominated in the same universe. Null when no source field is set. */
  total_stock?: number | null
  /** Client-flat mirror of `supplier_reorder_details.on_order` so an
   *  EntityTable col can sort/filter/heat it via `key: 'on_order_qty'`.
   *  Set in RestockHub.tsx's `filtered` memo. */
  on_order_qty?: number | null
  /** PR-14 — populated only on a grouped row (key != uid): the member UIDs
   *  rolled into this group. Null on an ungrouped row. */
  member_uids?: string[] | null
  /** Client-flat "Order qty" — `manual_order_qty ?? ai_order_qty`, the number
   *  a PO draft actually uses. Set in RestockHub.tsx's `filtered` memo so the
   *  one Order qty column can sort, filter and total on it. */
  order_qty_effective?: number | null
  /** Client-only: the Order qty this row had before an inline edit since the
   *  last refresh, so the default sort does not move a row under the cursor
   *  (restockHub/orderSort.ts). Undefined = no edit since the last load. */
  order_qty_sort_hold?: number | null
  /** Fam Stock / Fam On-Order (PR 11, plan I6 / I6b, feedback #95) — packs of
   *  THIS item that could be made by repacking its family siblings
   *  (backend engine/product_components.compute_family_packs). Only
   *  single-component sibling packs are sources (mixed sets are never broken
   *  down), this item's own stock never counts, and only warehouse + other
   *  warehouses can be repacked (FBA and WFS cannot). Fam On-Order converts
   *  the siblings' open POs the same way. Null (not 0) when no sibling could
   *  ever feed the item, and on grouped rollup rows. Visibility only — the AI
   *  order / days-of-cover math is unchanged.
   *
   *  The 2026-07-17 fields (family_on_order, family_on_order_elsewhere,
   *  family_total_stock, family_member_count) summed raw counts across pack
   *  sizes, self and FBA included; the backend removed them. All optional
   *  here so an older payload still type-checks. */
  family_stock_packs?: number | null
  family_on_order_packs?: number | null
  /** The working behind both numbers, for the cell tooltip. Null when the
   *  product has no recipe (no pack family, or no usable pack size). */
  family_breakdown?: FamilyBreakdown | null
  /** Plain-English warning when pack_size disagrees with the "(N)PK" in the
   *  product's own name or SKU. Fam Stock divides by pack_size, so a wrong
   *  one multiplies every sibling's number. */
  pack_size_conflict?: string | null
  /** The source sibling UIDs (excludes self), sorted; null when none. */
  family_sibling_uids?: string[] | null
  /** PR 11 (#94) — the multiplier behind adjusted_velocity, where it came
   *  from, and this product's own stored override (null = not set). */
  trend_factor?: number | null
  trend_factor_source?: TrendFactorSource | null
  trend_multiplier_product?: number | null
  /** PR 11 (I2) — today (Pacific) + days of cover, 'YYYY-MM-DD'. Null with no
   *  velocity, or past ten years. */
  stockout_date?: string | null
  /** Earliest ETA across this UID's open POs, overdue ones included,
   *  'YYYY-MM-DD'; `next_po_eta_overdue` says that date has already passed. */
  next_po_eta?: string | null
  next_po_eta_overdue?: boolean
  /** PR 22 (B9): open PO lines with a problem (the Orders page's predicate),
   *  and the one whose date next_po_eta would have shown when GoFlow says it is
   *  fully received. Flags only: on_order is unchanged. */
  po_problems?: RestockPoProblem[]
  next_po_eta_problem?: RestockPoProblem | null
  /** D1 — rolling 30-day Amazon sales (US + CA) the 30d after-ads margin
   *  divides ad spend by. The margin column's totals weight by it. */
  revenue_30d_rolling?: number | null
  /** PR 10 (#110) — what the stock we OWN is worth at landed cost:
   *  (warehouse + other warehouses + WFS + FBA on hand + FBA inbound) ×
   *  landed_unit_cost (backend restock_hub.inventory_value — the product
   *  snapshot uses the same helper). On-order stock is NOT included. null
   *  when the row has no landed cost. Breakdown keys: warehouse / other_wh /
   *  wfs / fba_on_hand / fba_inbound. */
  inventory_value?: number | null
  inventory_value_breakdown?: Record<string, number> | null
  inventory_value_is_estimate?: boolean
}

/** One sibling pack feeding one component of a Fam Stock number. */
export interface FamilySourceLine {
  uid: string
  /** Packs of the sibling in the warehouse (or on order). */
  packs: number
  /** Units of the component in one sibling pack. */
  qty_per_pack: number
  /** packs × qty_per_pack. */
  units: number
  /** The sibling's own pack size disagrees with its name. */
  pack_size_in_doubt?: boolean
}

/** One component of THIS row's recipe and what its siblings hold. */
export interface FamilyComponentLine {
  component_key: string
  label: string
  /** Units of the component in one pack of THIS item. */
  qty_per_pack: number
  /** Sum of the sources' units. */
  units: number
  /** units ÷ qty_per_pack, rounded down. */
  packs: number
  sources: FamilySourceLine[]
}

export interface FamilyBreakdown {
  recipe_source: 'custom' | 'default' | 'none'
  components: FamilyComponentLine[]
  on_order_components: FamilyComponentLine[]
  /** Component keys that set the minimum — only when the recipe has more
   *  than one component and they disagree ("limited by blue"). */
  limited_by: string[]
  on_order_limited_by: string[]
  /** Same-family UIDs left out of the sum, with why. */
  skipped: { uid: string; reason: string }[]
}

// PR-14 — GroupingPicker levels this hub's server-side rollup supports.
// 'variation' matches the GroupingLevel 'variation' (variation_family);
// the rest align 1:1 with GroupingLevel values.
export type RestockHubGroupBy =
  | 'uid' | 'pack_family' | 'variation' | 'asin' | 'brand' | 'manager' | 'category'

// ---- Restock Hub helpers ----

export function getRestockHub(params: {
  group_by?: RestockHubGroupBy
  marketplace?: HubMarketplace | 'all'
  search?: string
  signal?: AbortSignal
}): Promise<CachedListResponse<RestockHubRow>> {
  const q = new URLSearchParams()
  q.set('group_by', params.group_by ?? 'uid')
  q.set('marketplace', params.marketplace ?? 'all')
  if (params.search) q.set('search', params.search)
  return opsFetch<CachedListResponse<RestockHubRow>>(`/api/restock-hub?${q}`, {
    signal: params.signal,
  })
}

/** One uid's grouping dimensions (PR-14) — mirrors backend GroupKeyInfo. */
export interface GroupKeyInfo {
  asin: string | null
  variation_family: string | null
  pack_family: string | null
  brand: string | null
  manager: string | null
  category_l1: string | null
  category_l2: string | null
  category_l3: string | null
  category_path: string | null
}

/** GET /api/ops/products/group-keys — uid -> GroupKeyInfo. Backs every
 *  client-side GroupingPicker wire-up that doesn't already carry the field
 *  on its own rows (asin/variation/pack live on md_products already; brand/
 *  manager/category need this join). `marketplace='all'` (default) prefers
 *  the Amazon US category tree per uid. */
export function getGroupKeys(
  marketplace: HubMarketplace | 'all' = 'all',
  signal?: AbortSignal,
): Promise<Record<string, GroupKeyInfo>> {
  const q = new URLSearchParams({ marketplace })
  return opsFetch<Record<string, GroupKeyInfo>>(`/api/products/group-keys?${q}`, { signal })
}

/** Per-day FBA Available + Total stock history for one UID (Group C #8).
 *  Lazy — fetched only when a Restock row expands. marketplace='all' sums the
 *  daily series across active marketplaces; a specific MP returns that MP. */
export function getInventoryDaily(
  uid: string,
  params: { marketplace?: HubMarketplace | 'all'; days?: number; signal?: AbortSignal } = {},
): Promise<InventoryDailyResponse> {
  const q = new URLSearchParams()
  q.set('marketplace', params.marketplace ?? 'all')
  q.set('days', String(params.days ?? 365))
  return opsFetch<InventoryDailyResponse>(
    `/api/restock-hub/${encodeURIComponent(uid)}/inventory-daily?${q}`,
    { signal: params.signal },
  )
}

/** One marketplace's history + forecast from GET /restock-hub/{uid}/history
 *  (Build 48). Only marketplaces with at least one history OR forecast
 *  point are present — a marketplace absent from `marketplaces` has
 *  nothing to chart, same as an empty array would have meant before. */
export interface MarketplaceHistoryEntry {
  marketplace: string
  history: MonthlyHistoryPoint[]
  forecast: ForecastPoint[]
  forecast_kind: ForecastKind | null
}

export interface RestockHubHistoryResponse {
  uid: string
  marketplaces: MarketplaceHistoryEntry[]
}

/** Per-UID history + forecast for every marketplace with data (Build 48).
 *  Lazy — fetched only when a Restock Hub row's drawer expands. Split out
 *  of the list payload because MarketplaceBreakdown.history/.forecast × 8
 *  marketplaces × ~500 rows was the dominant share of the list response;
 *  the list's `marketplaces[].history`/`.forecast` are now always `[]` and
 *  this is where that detail actually lives. */
export function getRestockHubHistory(
  uid: string,
  signal?: AbortSignal,
): Promise<RestockHubHistoryResponse> {
  return opsFetch<RestockHubHistoryResponse>(
    `/api/restock-hub/${encodeURIComponent(uid)}/history`,
    { signal },
  )
}

/** One trailing 30-day bucket of GET /restock-hub/{uid}/rolling (#112). Money
 *  is USD; `by_marketplace` carries units + revenue per marketplace (the
 *  bucket totals come from the P&L's all-marketplace row). */
export interface RestockRollingBucket {
  start: string
  end: string
  units: number
  revenue: number
  net_profit: number
  cogs: number
  amazon_fees: number
  ad_spend: number
  by_marketplace: Record<string, { units: number; revenue: number }>
}

export interface RestockRollingResponse {
  uid: string
  /** The newest bucket's last day — yesterday (Pacific); today is not complete. */
  end: string
  days: number
  periods: number
  /** Oldest first. */
  buckets: RestockRollingBucket[]
}

/** PR 10 (#112) — the Restock drawer chart's "Rolling 30 days" view: 13
 *  trailing 30-day buckets ending yesterday. Lazy (fetched on first click). */
export function getRestockRolling(
  uid: string,
  opts: { periods?: number; days?: number; signal?: AbortSignal } = {},
): Promise<RestockRollingResponse> {
  const p = new URLSearchParams()
  if (opts.periods != null) p.set('periods', String(opts.periods))
  if (opts.days != null) p.set('days', String(opts.days))
  const qs = p.toString()
  return opsFetch<RestockRollingResponse>(
    `/api/restock-hub/${encodeURIComponent(uid)}/rolling${qs ? `?${qs}` : ''}`,
    { signal: opts.signal },
  )
}

// ── Product snapshot + quick view (PR 10, plan I5) ──────────────────────────
// "How is this product doing" in one place: the same stock, 30-day money and
// listing facts every product table and the app-wide quick-view drawer read
// (backend engine/product_snapshot.py). Money is the P&L's over the last 30
// COMPLETE days — the Overview's window — so no two screens disagree.

export interface ProductSnapshotWindow {
  from: string
  to: string
  days: number
}

export interface ProductSnapshot {
  uid: string
  asin: string | null
  product_name: string | null
  image_url: string | null
  stock_units: number | null
  /** warehouse / other_wh / wfs / fba_on_hand / fba_inbound */
  stock_breakdown: Record<string, number> | null
  on_order: number | null
  days_of_cover: number | null
  /** 'pooled' (everything we own ÷ every marketplace's sales) or
   *  'fba_this_marketplace' (that Amazon marketplace's FBA stock ÷ its sales). */
  days_of_cover_basis: string | null
  runs_out_on: string | null
  next_po_eta: string | null
  next_po_overdue: boolean
  inventory_value: number | null
  inventory_value_breakdown?: Record<string, number> | null
  inventory_value_is_estimate: boolean
  units_30d: number | null
  sales_30d: number | null
  ad_spend_30d: number | null
  ad_sales_30d: number | null
  acos_30d: number | null
  tacos_30d: number | null
  net_profit_30d: number | null
  margin_30d: number | null
  /** Some of the window's cost of goods came from the catalog cost sheet,
   *  not a received purchase order — the profit figures are estimates. */
  cogs_estimated: boolean
  /** One marketplace's listing status (null on the all-marketplace snapshot). */
  status: string | null
  /** marketplace → 'active' | 'in_review' | 'inactive' */
  statuses: Record<string, string>
  /** "Active on 3 of 4" */
  status_summary: string | null
  buy_box_pct: number | null
  rating: number | null
  review_count: number | null
  bsr_rank: number | null
}

export interface ProductSnapshotResponse {
  window: ProductSnapshotWindow
  marketplace: string
  computed_at: string
  count: number
  items: ProductSnapshot[]
}

/** GET /api/ops/products/snapshot — every product's snapshot for one scope
 *  ('all' or a hub marketplace). Cached server-side for 5 minutes. */
export function getProductSnapshot(
  marketplace: string,
  opts: { uids?: string[]; signal?: AbortSignal } = {},
): Promise<ProductSnapshotResponse> {
  const p = new URLSearchParams({ marketplace })
  if (opts.uids?.length) p.set('uids', opts.uids.join(','))
  return opsFetch<ProductSnapshotResponse>(`/api/products/snapshot?${p}`, { signal: opts.signal })
}

export interface QuickViewTrendPoint {
  date: string
  units: number
  sales: number
  ad_spend: number
  net_profit: number
}

export interface ProductQuickView extends ProductSnapshot {
  window: ProductSnapshotWindow
  marketplace: string
  computed_at: string
  /** One point per day in the window, zero-filled. */
  trend_30d: QuickViewTrendPoint[]
  open_po_lines: OpenPoLine[]
  unit_cost: number | null
  unit_cost_is_estimate: boolean
  unit_cost_label: string | null
  /** In-app paths (product_page, listings, restock, settings, ppc_product)
   *  plus `amazon` (an external storefront URL) — the ASIN links are absent
   *  when the product has no ASIN. */
  links: Record<string, string>
}

/** GET /api/ops/products/{uid}/quick-view — the drawer's one request. */
export function getProductQuickView(
  uid: string,
  marketplace: string,
  opts: { signal?: AbortSignal } = {},
): Promise<ProductQuickView> {
  const p = new URLSearchParams({ marketplace })
  return opsFetch<ProductQuickView>(
    `/api/products/${encodeURIComponent(uid)}/quick-view?${p}`,
    { signal: opts.signal },
  )
}

/** One ASIN (Amazon) or SKU (GoFlow) that sold in the window but is in no
 *  product row — so its sales are missing from the P&L and every product
 *  table (#96 / #97). */
export interface UnlinkedSale {
  kind: 'amazon' | 'goflow'
  asin: string | null
  sku: string | null
  marketplace: string
  skus: string[]
  title: string | null
  units: number
  sales_usd: number
  first_seen: string | null
  last_seen: string | null
  suggested_uid: string | null
  /** Plain English: why this product is suggested. */
  suggestion_reason: string | null
}

export interface UnlinkedSalesResponse {
  window: ProductSnapshotWindow
  count: number
  total_sales_usd: number
  items: UnlinkedSale[]
}

/** GET /api/ops/products/unlinked-sales — selling, not in the catalog. */
export function getUnlinkedSales(
  opts: { days?: number; signal?: AbortSignal } = {},
): Promise<UnlinkedSalesResponse> {
  const p = new URLSearchParams({ days: String(opts.days ?? 30) })
  return opsFetch<UnlinkedSalesResponse>(`/api/products/unlinked-sales?${p}`, { signal: opts.signal })
}

/** Map the HubMarketplace tab label (e.g. "Amazon US", "Target+") to the
 *  MPL backend's marketplace name (e.g. "Amazon US", "Target"). Backend at
 *  backend/ops/routes/mpl_restock.py validates against _MARKETPLACES.
 *
 *  Indexed by `string` (not the strict HubMarketplace type) because the
 *  page's HubMarketplace also carries an 'all' variant which has no MPL
 *  counterpart — the caller guards against it before lookup. Missing keys
 *  return `undefined` (Shopify is intentionally absent; 'all' returns
 *  undefined naturally).
 *
 *  Caveats:
 *   - "Amazon US" and "Amazon CA" map to their own backend keys —
 *     md_mpl_inventory stores them as separate marketplaces (2026-05-28
 *     split), and md_restock_overrides is keyed per marketplace too. The
 *     bare legacy "Amazon" name is still accepted by the backend as an
 *     alias for "Amazon US" (old links/bookmarks), but new requests must
 *     never send it for CA — that's how the CA tab ended up serving US
 *     data (finding ca-tab-serves-us-data).
 *   - "Shopify" is unsupported by MPL Restock backend. Returns undefined
 *     so the page can render a "no MPL data for Shopify" stub.
 */
export const HUB_TO_MPL_MARKETPLACE: Record<string, Marketplace | undefined> = {
  'Amazon US': 'Amazon US',
  'Amazon CA': 'Amazon CA',
  'Walmart':   'Walmart',
  'Target+':   'Target',
  'Temu':      'Temu',
  'eBay':      'Ebay',
  'TikTok':    'TikTok',
  // Shopify intentionally omitted — no MPL backend coverage.
}

/** Per-marketplace label for the "ship from warehouse INTO the channel"
 * decision. Mirrors RESTOCK_LABEL_BY_MP in restockMplCols.tsx but keyed by
 * the HUB display name (`'Amazon US'`, `'Walmart'`, etc.) rather than the
 * API Marketplace type. Use in drawer headers / cards on the SR + per-MP
 * tabs so the copy matches the column headers. */
export const HUB_RESTOCK_LABEL: Record<string, string> = {
  'Amazon US': 'FBA Restock',
  'Amazon CA': 'FBA Restock',
  'Walmart':   'WFS Restock',
  'Target+':   'TPS Restock',
  'Temu':      'SMW Restock',
  'eBay':      'eBay Restock',
  'TikTok':    'Shop Restock',
  'Shopify':   'Shopify Restock',
}

export function getMplRestock(
  marketplace: Marketplace,
  signal?: AbortSignal,
): Promise<ListResponse<RestockRow>> {
  return opsFetch<ListResponse<RestockRow>>(
    `/api/mpl-restock?marketplace=${encodeURIComponent(marketplace)}`,
    { signal },
  )
}

export function patchRestockHubOverride(
  uid: string,
  marketplace: string,
  body: {
    manual_send_qty?: number | null
    manual_order_qty?: number | null
    notes?: string | null
  },
  ifMatch: number,
): Promise<RestockHubRow> {
  return opsFetch<RestockHubRow>(
    `/api/restock-hub/${encodeURIComponent(uid)}/${encodeURIComponent(marketplace)}/override`,
    { method: 'PATCH', body, ifMatch },
  )
}

/** Supplier Reorder tab override (cross-marketplace UID rollup).
 *
 * Routes to PATCH /api/supplier-reorder/{uid} which writes
 * md_supplier_reorder_overrides (PK uid only). Use this from the Supplier
 * Reorder tab — for per-marketplace tabs, use patchRestockHubOverride above.
 *
 * Returns the refreshed SupplierReorderRow with order_value recomputed.
 * `ifMatch` is optional; omit it (or pass `null`) to skip the version check
 * (acceptable for the single-user app and the no-existing-override case).
 */
export function patchSupplierReorderOverride(
  uid: string,
  body: {
    manual_order_qty?: number | null
    notes?: string | null
  },
  ifMatch?: number | null,
): Promise<unknown> {
  return opsFetch<unknown>(
    `/api/supplier-reorder/${encodeURIComponent(uid)}`,
    {
      method: 'PATCH',
      body,
      ifMatch: ifMatch == null ? undefined : ifMatch,
    },
  )
}

/** The single md_reorder_settings row (ops/095 + ops/105) — the
 *  supplier-reorder cover target, in days, and the all-products trend
 *  multiplier. Same knobs the AI Order Qty card, the Daily Brief's
 *  "overstocked" definition and the chart's forecast horizon all read. */
export interface ReorderSettings {
  cover_target_days: number
  /** ops/105 (#94) — one multiplier for every product's trend, 0.50..2.00.
   *  Null = Automatic (each product's own month-over-month). */
  trend_multiplier: number | null
  version: number
  updated_at?: string | null
  updated_by?: string | null
}

export function getReorderSettings(): Promise<ReorderSettings> {
  return opsFetch<ReorderSettings>('/api/supplier-reorder/settings')
}

/** Send ONLY what changed — the route is partial (`model_fields_set`), and
 *  `trend_multiplier: null` means "back to Automatic", which is different
 *  from leaving the field out. Cover target is clamped 30..365 server-side;
 *  the 365 ceiling is engine/forecast.py's 12-month forecast cache, not a
 *  preference. An empty patch is a 400. */
export function patchReorderSettings(
  patch: { cover_target_days?: number; trend_multiplier?: number | null },
  ifMatch?: number | null,
): Promise<ReorderSettings> {
  return opsFetch<ReorderSettings>('/api/supplier-reorder/settings', {
    method: 'PATCH',
    body: patch,
    ifMatch: ifMatch == null ? undefined : ifMatch,
  })
}

/** One product's own trend multiplier (#94, ops/105) — GET/PUT/DELETE
 *  /api/ops/supplier-reorder/{uid}/trend-multiplier. */
export interface TrendMultiplierOut {
  uid: string
  /** This product's stored value; null when it follows the all-products
   *  setting (or Automatic). */
  trend_multiplier: number | null
  /** If-Match for the next PUT / DELETE; 0 when the product has none. */
  version: number
  /** Which setting applies to this product now. */
  source: TrendFactorSource
  /** What "Use the all-products setting" falls back to (null = Automatic). */
  all_products_multiplier: number | null
}

export function getTrendMultiplier(uid: string): Promise<TrendMultiplierOut> {
  return opsFetch<TrendMultiplierOut>(
    `/api/supplier-reorder/${encodeURIComponent(uid)}/trend-multiplier`,
  )
}

/** 0.50..2.00 (a 422 outside it). Beats the all-products setting and the
 *  automatic one, on every tab. A stale `version` is a 409. */
export function putTrendMultiplier(
  uid: string, value: number, version: number,
): Promise<TrendMultiplierOut> {
  return opsFetch<TrendMultiplierOut>(
    `/api/supplier-reorder/${encodeURIComponent(uid)}/trend-multiplier`,
    { method: 'PUT', body: { trend_multiplier: value }, ifMatch: version },
  )
}

/** Back to the all-products setting (or Automatic). Idempotent. */
export function clearTrendMultiplier(uid: string, version: number): Promise<TrendMultiplierOut> {
  return opsFetch<TrendMultiplierOut>(
    `/api/supplier-reorder/${encodeURIComponent(uid)}/trend-multiplier`,
    { method: 'DELETE', ifMatch: version },
  )
}

// ---------------- Brand Analytics (WS4 hub surfaces) ------------------------
// Read-only Amazon-only Marketplaces-hub surfaces. The backend route registers
// at /api/ops/brand-analytics/*; we call `/api/brand-analytics/*` and let
// `withApiBase` add the single `/ops` segment (calling `/api/ops/...` here
// would double-prefix to `/api/ops/ops/...`).

/** Shared envelope for every BA surface. Rows ride under `items` as loose
 *  records because the four surfaces share the envelope but not the columns. */
export interface BAListResponse<T = Record<string, unknown>> {
  count: number
  items: T[]
  marketplace: string
  /** False when the active marketplace has no rows — drives the empty-state.
   *  Non-Amazon marketplaces and absent main-chain tables both report false. */
  available: boolean
  /** The `brand_analytics_sqp` SPAN behind these rows, when the surface reports
   *  one — every SQP figure on a row is a sum across it, so both ends matter.
   *  OPTIONAL because only the keyword bank sets them (backend
   *  `models.KeywordBankResponse`, a subclass — the shared envelope is
   *  `extra='forbid'` and every other surface's payload is unchanged). */
  data_from?: string | null
  data_through?: string | null
}

export type BASurface =
  | 'search-terms' | 'sqp' | 'sqp-view' | 'scp' | 'reviews-returns' | 'listing-health'
  | 'traffic-gap' | 'sns-gap'

/** The hub passes the level-1 tab marketplace down to a sub-tab; that includes
 *  the synthetic 'all' (Supplier Reorder) tab. BA surfaces are Amazon-only and
 *  empty-state everything else, so the fetcher accepts the full set and the
 *  backend maps any non-Amazon value to an honest empty-state. */
export type BAMarketplace = HubMarketplace | 'all'

export function getBrandAnalytics<T = Record<string, unknown>>(
  surface: BASurface,
  marketplace: BAMarketplace,
  opts?: { from?: string; to?: string; signal?: AbortSignal },
): Promise<BAListResponse<T>> {
  const qs = new URLSearchParams({ marketplace })
  if (opts?.from) qs.set('from', opts.from)
  if (opts?.to) qs.set('to', opts.to)
  return opsFetch<BAListResponse<T>>(`/api/brand-analytics/${surface}?${qs}`, {
    signal: opts?.signal,
  })
}

/** #82 — the window (`from`/`to`) is part of the fetcher contract, not an
 *  extra only `getScpWindowed` understood. `getBrandAnalytics` has always
 *  forwarded the pair and every BA endpoint has always accepted it; the
 *  fetcher TYPE was the only thing that didn't, which is why BrandAnalyticsPanel
 *  could not be given a date range. Both fields stay optional — omitting them
 *  keeps the backend's own default lookback, the behaviour every caller had. */
export type BAFetcher<T = Record<string, unknown>> = (
  marketplace: BAMarketplace,
  opts?: { from?: string; to?: string; signal?: AbortSignal },
) => Promise<BAListResponse<T>>

export const getSearchTerms: BAFetcher = (m, o) => getBrandAnalytics('search-terms', m, o)
export const getSqp: BAFetcher = (m, o) => getBrandAnalytics('sqp', m, o)
// A5 — ScaleInsights-style SQP keyword view (per-query funnel + brand share).
export const getSqpView: BAFetcher<SqpKeywordRow> = (m, o) =>
  getBrandAnalytics<SqpKeywordRow>('sqp-view', m, o)
export const getScp: BAFetcher = (m, o) => getBrandAnalytics('scp', m, o)
/** SCP with an explicit period window (from/to) — used by the SCP sub-tab's
 *  Amazon-style period picker. The base `getScp` (BAFetcher shape) only carries
 *  a signal; this passes the selected period's [start, end] through. */
export const getScpWindowed = (
  m: BAMarketplace,
  o?: { from?: string; to?: string; signal?: AbortSignal },
): Promise<BAListResponse> => getBrandAnalytics('scp', m, o)

/** One search-query row of the ScaleInsights-style SQP keyword view (A5).
 *  Mirrors backend models.SqpKeywordRow. ``total_*`` is the whole market's
 *  funnel for the query; ``our_*`` is our brand's slice; ``*_share`` is our
 *  slice ÷ the market. Every numeric is NULL-tolerant (partial backfill). */
export interface SqpKeywordRow extends Record<string, unknown> {
  search_query: string
  asin_count: number
  search_query_volume: number | null
  total_impressions: number | null
  total_clicks: number | null
  total_cart_adds: number | null
  total_purchases: number | null
  ctr: number | null
  cvr: number | null
  our_impressions: number | null
  our_clicks: number | null
  our_cart_adds: number | null
  our_purchases: number | null
  impression_share: number | null
  click_share: number | null
  cart_add_share: number | null
  purchase_share: number | null
  our_ctr: number | null
  our_cvr: number | null
  period_count: number
  start_date: string | null
  end_date: string | null
}
// ── Unified SQP tab — Amazon Brand-Analytics SQP parity ─────────────────────
// ONE Amazon-parity Search-Query-Performance surface (replaces the two legacy
// SQP tabs). One row per query stitching the whole-market funnel + our brand's
// counts/shares (the sqp-view rollup) with the PPC side (keyword_performance)
// and an Organic = brand − PPC remainder. Brand vs ASIN view + Monthly/Weekly/
// Quarterly grain. NULL-tolerant: empty/partial windows + the not-yet-landed
// weekly backfill render the honest empty-state.

export type SqpView = 'brand' | 'asin'
export type SqpPeriod = 'month' | 'week' | 'quarter'

/** One unified-SQP row. Mirrors backend models.SqpUnifiedRow. ``total_*`` is the
 *  whole-market funnel; ``brand_*`` + ``*_share`` is our slice; ``ppc_*`` is the
 *  paid side; ``organic_*`` = brand − PPC (floored at 0). Every numeric is
 *  NULL-tolerant (partial backfill / missing side). */
export interface SqpUnifiedRow extends Record<string, unknown> {
  search_query: string
  asin_count: number
  /** Amazon's Search Query Score (1..100 relevance/quality), avg'd across the
   *  ASINs/periods folded into the row. */
  search_query_score: number | null
  search_query_volume: number | null
  total_impressions: number | null
  total_clicks: number | null
  total_cart_adds: number | null
  total_purchases: number | null
  ctr: number | null
  cvr: number | null
  brand_impressions: number | null
  brand_clicks: number | null
  brand_cart_adds: number | null
  brand_purchases: number | null
  impression_share: number | null
  click_share: number | null
  purchase_share: number | null
  bidding: boolean
  ppc_impressions: number | null
  ppc_clicks: number | null
  ppc_spend: number | null
  ppc_sales: number | null
  ppc_orders: number | null
  ppc_acos: number | null
  organic_clicks: number | null
  organic_orders: number | null
  organic_sales: number | null
  period_count: number
  start_date: string | null
  end_date: string | null
}

/** One per-ASIN summary row (feature #3 default view). Mirrors backend
 *  models.SqpAsinSummaryRow. Emitted by sqp-unified in "summary mode"
 *  (view=asin + no ASIN scoped): one row per ASIN aggregating its slice over
 *  the selected window. Deliberately uses SHORT metric names
 *  (``impressions``/``clicks``/``cart_adds``/``purchases``) — these are THIS
 *  ASIN's totals, not the per-query ``brand_*``/``total_*`` vocabulary. */
export interface SqpAsinSummaryRow extends Record<string, unknown> {
  asin: string
  product_name: string | null
  brand: string | null
  query_count: number
  /** Amazon's Search Query Score (1..100 relevance/quality), avg'd over this
   *  ASIN's queries/periods in the window. */
  search_query_score: number | null
  search_query_volume: number | null
  impressions: number | null
  clicks: number | null
  cart_adds: number | null
  purchases: number | null
  ctr: number | null
  cvr: number | null
  start_date: string | null
  end_date: string | null
}

/** Envelope for the unified SQP tab. Extends the BA empty-state contract with
 *  the Brand/ASIN + period state the tab's controls need: ``asins`` self-
 *  populates the ASIN-view picker; ``weekly_available`` is false until the
 *  parallel weekly backfill lands WEEK-grain rows. ``items`` is a union: normal
 *  per-query ``SqpUnifiedRow``s, OR per-ASIN ``SqpAsinSummaryRow``s in the
 *  feature-#3 summary mode (view=asin + no ASIN scoped). Both extend
 *  ``Record<string, unknown>`` so EntityTable's generic row type is satisfied. */
export interface SqpUnifiedResponse {
  count: number
  items: (SqpUnifiedRow | SqpAsinSummaryRow)[]
  marketplace: string
  available: boolean
  view: SqpView
  period: SqpPeriod
  asins: string[]
  weekly_available: boolean
}

/** GET /api/ops/brand-analytics/sqp-unified — the Amazon-parity SQP surface.
 *  Amazon-only + NULL-tolerant: non-Amazon / absent tables / empty windows
 *  return available=false with items=[]. */
export function getSqpUnified(
  marketplace: BAMarketplace,
  opts?: {
    view?: SqpView
    period?: SqpPeriod
    from?: string
    to?: string
    asin?: string
    q?: string
    brand?: string
    signal?: AbortSignal
  },
): Promise<SqpUnifiedResponse> {
  const qs = new URLSearchParams({ marketplace })
  if (opts?.view) qs.set('view', opts.view)
  if (opts?.period) qs.set('period', opts.period)
  if (opts?.from) qs.set('from', opts.from)
  if (opts?.to) qs.set('to', opts.to)
  if (opts?.asin) qs.set('asin', opts.asin)
  if (opts?.q) qs.set('q', opts.q)
  if (opts?.brand) qs.set('brand', opts.brand)
  return opsFetch<SqpUnifiedResponse>(`/api/brand-analytics/sqp-unified?${qs}`, {
    signal: opts?.signal,
  })
}

// ── Amazon-style period picker (shared by SQP + SCP) ────────────────────────
// One selectable Year + Month|Week|Quarter period, sourced from the spans that
// actually exist in brand_analytics_sqp (so the picker never offers an empty
// period). Selecting one drives the fetch via its [start_date, end_date].

export interface BAPeriodOption {
  grain: SqpPeriod
  year: number
  /** month-of-year (1-12) | ISO week (1-53) | quarter (1-4) per grain. */
  index: number
  /** Amazon-style human label: 'Jun 2026' | 'W23 · Jun 2–8, 2026' | 'Q2 2026'. */
  label: string
  start_date: string
  end_date: string
}

export interface BAPeriodsResponse {
  marketplace: string
  available: boolean
  months: BAPeriodOption[]
  weeks: BAPeriodOption[]
  quarters: BAPeriodOption[]
  /** Distinct ASINs present in the marketplace's SQP rows — sources the
   *  searchable ASIN input on both the SQP and SCP surfaces. */
  asins: string[]
  /** Distinct non-empty ``md_products.brand`` among the marketplace's SQP
   *  ASINs, sorted — sources the SQP Brand selector. Empty when md_products is
   *  absent / the brand column is unpopulated / non-Amazon marketplace. */
  brands: string[]
  weekly_available: boolean
}

/** GET /api/ops/brand-analytics/sqp-periods — available periods + ASIN list for
 *  the Amazon-style period picker. NULL-tolerant: non-Amazon / absent table →
 *  available=false with empty lists. */
export function getSqpPeriods(
  marketplace: BAMarketplace,
  opts?: { signal?: AbortSignal },
): Promise<BAPeriodsResponse> {
  const qs = new URLSearchParams({ marketplace })
  return opsFetch<BAPeriodsResponse>(`/api/brand-analytics/sqp-periods?${qs}`, {
    signal: opts?.signal,
  })
}

// ── SQP Wave 2: period deltas + conversion / funnel / opportunity metrics ───
// A research-grounded analytics layer STACKED on the Wave-1 sqp-unified rollup.
// Mirrors backend models.SqpDeltaRow / SqpDeltaResponse. Every numeric is
// NULL-tolerant (a query with no prior period / no clicks / no price renders
// with the affected fields null, never a fabricated zero).

/** NEW = no prior-period row; GAP = returned after a stale gap; DROPPED =
 *  present prior, absent now; STABLE = both present. */
export type SqpDeltaTag = 'NEW' | 'GAP' | 'DROPPED' | 'STABLE'

/** One metric's movement vs a prior period. Count/rate metrics carry
 *  ``abs_delta`` + ``pct_change``; SHARE metrics carry ``pp_delta`` (a
 *  percentage-POINT move) and leave ``pct_change`` null. */
export interface SqpPeriodDelta {
  current: number | null
  prior: number | null
  abs_delta: number | null
  pct_change: number | null
  pp_delta: number | null
  tag: SqpDeltaTag
}

/** The metric keys carried in each MoM/QoQ/YoY delta block. */
export type SqpDeltaMetric =
  | 'search_query_volume'
  | 'purchase_share'
  | 'click_share'
  | 'impression_share'
  | 'cvr'
  | 'our_click_cvr'

/** One search-query row of the SQP Wave 2 deltas surface. Current-period funnel
 *  + MoM/QoQ/YoY delta blocks + derived conversion / funnel / opportunity /
 *  price signals. ``opportunity_score`` is a TRANSPARENT ClickPay construct
 *  (0–100 percentile of demand × market convert-rate × our headroom), NOT an
 *  Amazon metric. */
export interface SqpDeltaRow extends Record<string, unknown> {
  search_query: string
  /** Server emits `upper(s.asin) AS asin` per row (brand_analytics.py:982);
   *  non-null by construction — the query filters `s.asin IS NOT NULL`
   *  (brand_analytics.py:1029). */
  asin: string
  asin_count: number
  /** Retained for API back-compat; at this grain (one ASIN per row) it
   *  always equals `asin` (brand_analytics.py:987-989). */
  top_asin: string | null
  search_query_score: number | null
  search_query_volume: number | null
  total_impressions: number | null
  total_clicks: number | null
  total_cart_adds: number | null
  total_purchases: number | null
  ctr: number | null
  cvr: number | null
  /** CLIENT-DERIVED in SqpSubTab's `deltaRows` memo (total_* ÷
   *  search_query_volume) — the server never sends these. Amazon's SQP
   *  "*_rate" columns divide by SEARCHES; `ctr`/`cvr` above divide by
   *  impressions/clicks instead, so this is a different figure, not a
   *  duplicate. Null when volume is null/0 or the numerator is null. */
  total_click_rate?: number | null
  total_cart_add_rate?: number | null
  total_purchase_rate?: number | null
  brand_impressions: number | null
  brand_clicks: number | null
  brand_cart_adds: number | null
  brand_purchases: number | null
  impression_share: number | null
  click_share: number | null
  cart_add_share: number | null
  purchase_share: number | null
  // ── PPC overlay (our paid side, keyword_performance) — same
  // `_sqp_ppc_overlay()` lookup the funnel row uses, applied to this row's
  // (marketplace, view, asin|brand, search_query) slice. Null-tolerant: a
  // query with no PPC match gets bidding=false and null ppc_* fields. ───────
  bidding: boolean
  ppc_impressions: number | null
  ppc_clicks: number | null
  ppc_spend: number | null
  ppc_sales: number | null
  ppc_orders: number | null
  ppc_acos: number | null
  // ── Organic = brand total − PPC (floored at 0) ────────────────────────────
  organic_clicks: number | null
  organic_orders: number | null
  organic_sales: number | null
  market_click_cvr: number | null
  our_click_cvr: number | null
  market_atc_rate: number | null
  cart_to_purchase: number | null
  conversion_delta: number | null
  ctr_delta: number | null
  funnel_leakage: number | null
  visibility_leakage: number | null
  conversion_efficiency: number | null
  opportunity_raw: number | null
  opportunity_score: number | null
  our_median_purchase_price: number | null
  market_median_purchase_price: number | null
  price_premium_purchase: number | null
  price_premium_purchase_pct: number | null
  our_median_click_price: number | null
  market_median_click_price: number | null
  price_premium_click: number | null
  price_resistance: boolean
  volume_rank: number | null
  volume_percentile: number | null
  mom: Record<string, SqpPeriodDelta>
  qoq: Record<string, SqpPeriodDelta>
  yoy: Record<string, SqpPeriodDelta>
  change_tag: SqpDeltaTag
  period_count: number
  start_date: string | null
  end_date: string | null
  prior_start_date: string | null
  prior_end_date: string | null
}

/** Opportunity preset / saved filter (P1.4), copying Helium-10's OPC/LPC/etc.
 *  over the derived signals. */
export type SqpDeltaPreset =
  | 'all'
  | 'underexposed_winner'
  | 'leaky_listing'
  | 'invisible'
  | 'losing_ground'
  | 'new'
  | 'dropped'

/** Which MoM/QoQ/YoY delta block(s) the response computes + carries per row.
 *  The merged SQP tab only ever renders MoM, so it passes `lenses: ['mom']` —
 *  QoQ/YoY are real work + real bytes (two more `Record<string,
 *  SqpPeriodDelta>` blocks per row) the merged table never reads. Omit for
 *  the pre-existing default (all three). */
export type SqpDeltaLens = 'mom' | 'qoq' | 'yoy'

/** Envelope for the SQP Wave 2 deltas surface. ``prior_*`` echoes the MoM prior
 *  window (label the delta columns "vs {month}"); ``yoy_available`` is false
 *  when the window's 12-month prior predates the ~17 months of data (grey YoY
 *  out rather than tag everything NEW). ``total_count`` is the full match
 *  count BEFORE `limit` truncates `items` — lets the merged tab render
 *  "showing top N of M queries" instead of silently dropping rows. */
export interface SqpDeltaResponse {
  count: number
  items: SqpDeltaRow[]
  marketplace: string
  available: boolean
  view: SqpView
  period: SqpPeriod
  preset: string
  asins: string[]
  prior_start_date: string | null
  prior_end_date: string | null
  yoy_available: boolean
  weekly_available: boolean
  /** Full match count before `limit` truncation. Falls back to `count`/
   *  `items.length` when the backend hasn't started sending it yet, so an
   *  older response never renders a false "showing top N of M" note. */
  total_count?: number
}

/** GET /api/ops/brand-analytics/sqp-deltas — period deltas + derived signals.
 *  Amazon-only + NULL-tolerant (non-Amazon / absent table / empty window →
 *  available=false, items=[]). */
export function getSqpDeltas(
  marketplace: BAMarketplace,
  opts?: {
    view?: SqpView
    period?: SqpPeriod
    from?: string
    to?: string
    asin?: string
    q?: string
    brand?: string
    preset?: SqpDeltaPreset
    minVolume?: number
    /** Restrict which delta block(s) compute/return. Defaults to all three
     *  server-side when omitted. */
    lenses?: SqpDeltaLens[]
    /** Cap on `items` length (server-side, sorted by the active sort before
     *  truncating). Pair with `SqpDeltaResponse.total_count` for an honest
     *  "showing top N of M" note. Omit for the pre-existing unbounded result. */
    limit?: number
    signal?: AbortSignal
  },
): Promise<SqpDeltaResponse> {
  const qs = new URLSearchParams({ marketplace })
  if (opts?.view) qs.set('view', opts.view)
  if (opts?.period) qs.set('period', opts.period)
  if (opts?.from) qs.set('from', opts.from)
  if (opts?.to) qs.set('to', opts.to)
  if (opts?.asin) qs.set('asin', opts.asin)
  if (opts?.q) qs.set('q', opts.q)
  if (opts?.brand) qs.set('brand', opts.brand)
  if (opts?.preset) qs.set('preset', opts.preset)
  if (opts?.minVolume != null) qs.set('min_volume', String(opts.minVolume))
  if (opts?.lenses?.length) qs.set('lenses', opts.lenses.join(','))
  if (opts?.limit != null) qs.set('limit', String(opts.limit))
  return opsFetch<SqpDeltaResponse>(`/api/brand-analytics/sqp-deltas?${qs}`, {
    signal: opts?.signal,
  })
}

/** One month of a per-query trend sparkline (P1.2). */
export interface SqpTrendPoint {
  start_date: string
  end_date: string
  search_query_volume: number | null
  impression_share: number | null
  click_share: number | null
  purchase_share: number | null
}

/** Per-query trend series (P1.2) — full MONTH-grain history of volume + our
 *  shares. Honest-empty for non-Amazon / absent table / unknown query. */
export interface SqpTrendResponse {
  search_query: string
  marketplace: string
  available: boolean
  months: number
  points: SqpTrendPoint[]
}

/** GET /api/ops/brand-analytics/sqp-trend — per-query 17-month sparkline series. */
export function getSqpTrend(
  marketplace: BAMarketplace,
  searchQuery: string,
  opts?: { asin?: string; brand?: string; signal?: AbortSignal },
): Promise<SqpTrendResponse> {
  const qs = new URLSearchParams({ marketplace, search_query: searchQuery })
  if (opts?.asin) qs.set('asin', opts.asin)
  if (opts?.brand) qs.set('brand', opts.brand)
  return opsFetch<SqpTrendResponse>(`/api/brand-analytics/sqp-trend?${qs}`, {
    signal: opts?.signal,
  })
}

export const getReviewsReturns = (
  m: BAMarketplace, o?: { from?: string; to?: string; signal?: AbortSignal },
): Promise<BAListResponse> => getBrandAnalytics('reviews-returns', m, o)

// ── Listing Health (Marketplaces hub #1) — reviews/returns + conversion ─────
export const getListingHealth = (
  m: BAMarketplace, o?: { from?: string; to?: string; signal?: AbortSignal },
): Promise<BAListResponse> => getBrandAnalytics('listing-health', m, o)

/** Aggregate header for the Listing Health surface. */
export interface ListingHealthSummary {
  avg_star_rating: number | null
  total_review_count: number | null
  rated_asin_count: number
  total_returns: number
  returned_units: number
  distinct_returned_asins: number
  total_units: number
  total_page_views: number
  total_sessions: number
  avg_conv_rate: number | null
  avg_session_conv_rate: number | null
  return_rate: number | null
  window_start: string | null
  window_end: string | null
  /** False until the upstream sessions feed populates (page-view conv only). */
  sessions_available: boolean
  top_return_reasons: ReturnReasonCount[]
}

/** Per-ASIN Listing Health row (everything after the `__summary__` element). */
export interface ListingHealthRow {
  asin: string
  uid: string | null
  product_name: string | null
  image_url: string | null
  star_rating: number | null
  review_count: number | null
  return_count: number
  returned_units: number
  top_return_reason: string | null
  units: number | null
  page_views: number | null
  sessions: number | null
  conv_rate: number | null
  session_conv_rate: number | null
  return_rate: number | null
  revenue: number | null
  buy_box_pct: number | null
}

// ── Listing detail + keyword bank (A6) — per-ASIN Helium-10-style drill ──────
// Two READ surfaces backing the listing-detail view you reach by clicking a
// Listings-tab row. Both Amazon-only + NULL-tolerant: empty/partial data
// renders cleanly and lights up as the SP-API pull + SQP backfill land.

/** Live SP-API listing content for one (asin, marketplace) + Keepa snapshot.
 *  Mirrors backend models.ListingContentOut. Every field is optional — an ASIN
 *  with no pulled listing yet returns a bare shell (asin only). */
export interface ListingDetail {
  asin: string
  marketplace: string | null
  title: string | null
  bullets: string[]
  description: string | null
  backend_keywords: string | null
  brand: string | null
  product_type: string | null
  image_url: string | null
  pulled_at: string | null
  star_rating: number | null
  review_count: number | null
  list_price: number | null
  buy_box_price: number | null
  bsr_rank: number | null
  uid: string | null
  product_name: string | null
  amazon_listing_url: string | null
  /** F9 (step 32/33): this feed's own cadence catalogue entry
   *  (bbpro_etl_jobs.cadence_h for job_id='bq.product_listings') — hours
   *  between scheduled pulls. The real feed is ~monthly (720h), NOT nightly.
   *  null when the job catalogue has no row for it. */
  feed_cadence_hours?: number | null
  /** bbpro_etl_freshness.status for the same job — 'green' | 'yellow' |
   *  'red' | null (no freshness row yet). Drives the drawer's staleness
   *  pill instead of a hard-coded "auto-refreshed nightly" claim. */
  feed_status?: 'green' | 'yellow' | 'red' | null
  /** bbpro_etl_freshness.age_hours for the same job — hours since the feed
   *  last actually wrote (not since `pulled_at`, which is per-ASIN). */
  feed_age_hours?: number | null
}

/** One mirrored product image. Bytes live under CLICKPAY_IMAGE_DIR on the mini
 *  and are served by this app's own /img mount, so `public_url` is a URL WE
 *  own — safe to paste into a Target+/Temu/Walmart bulk-upload file, unlike the
 *  m.media-amazon.com URL in md_products.main_image_url (not our domain,
 *  hotlink-blockable, and it silently changes the other channel's image
 *  whenever the Amazon hero shot changes). */
export interface ProductImage {
  /** 'main' for the hero shot, then '02', '03', ... in gallery order. */
  slot: string
  position: number | null
  /** Amazon's variant code: MAIN, PT01, PT02, ... */
  variant: string | null
  /** 'sp_api' = mirrored from the Amazon catalog; 'generated' = a listing
   *  image uploaded through POST /api/ops/products/{uid}/images/{slot}.
   *  Both are served identically from /img; the panel does not distinguish. */
  source: string | null
  public_url: string
  sha256: string | null
  width: number | null
  height: number | null
  bytes: number | null
  synced_at: string | null
}

export interface ProductImagesResponse {
  uid: string
  count: number
  items: ProductImage[]
  /** false when nothing has been mirrored for this uid yet — render
   *  "not mirrored yet", never an error. */
  available: boolean
  channel: string | null
  /** That channel's own column names → URL, ready to paste into its bulk
   *  template. Empty unless `channel` was requested. */
  columns: Record<string, string>
  /** URLs that did NOT fit the channel's image cap. Surfaced rather than
   *  silently trimmed: a feed that quietly loses images is the failure this
   *  endpoint exists to prevent. */
  dropped: string[]
}

export type ImageChannel = 'target_plus' | 'temu' | 'walmart'

/** GET /api/ops/products/{uid}/images — the mirrored gallery for one uid.
 *  Pass `channel` to also get that marketplace's bulk-upload columns. */
export function getProductImages(
  uid: string,
  opts?: { channel?: ImageChannel; signal?: AbortSignal },
): Promise<ProductImagesResponse> {
  const qs = new URLSearchParams()
  if (opts?.channel) qs.set('channel', opts.channel)
  const suffix = qs.toString() ? `?${qs}` : ''
  return opsFetch<ProductImagesResponse>(
    `/api/products/${encodeURIComponent(uid)}/images${suffix}`,
    { signal: opts?.signal },
  )
}

/** GET /api/ops/brand-analytics/listing-detail — the live listing for one ASIN.
 *  Returns a 200 shell (not a 404) for a non-Amazon marketplace / unpulled ASIN
 *  so the page renders its 'no listing pulled — re-pull' empty-state. */
export function getListingDetail(
  asin: string,
  marketplace: BAMarketplace,
  opts?: { signal?: AbortSignal },
): Promise<ListingDetail> {
  const qs = new URLSearchParams({ asin, marketplace })
  return opsFetch<ListingDetail>(`/api/brand-analytics/listing-detail?${qs}`, {
    signal: opts?.signal,
  })
}

/** One keyword in a per-ASIN keyword bank (A6). Mirrors backend
 *  models.KeywordBankRow. UNIONs PPC (keyword_performance) + SQP/organic
 *  (brand_analytics_sqp); the missing side's metrics are NULL. ``in_listing``
 *  flags whether the live copy already indexes the keyword — a high-performing
 *  ``in_listing=false`` keyword is the actionable add.
 *
 *  ``js_*`` fields are a Jungle Scout overlay (js_keyword_metrics /
 *  js_share_of_voice_daily, migrations 113/114) joined by keyword — all four
 *  are optional and NULL until the weekly/daily JS sync has pulled that
 *  keyword, which is the expected day-one state for every row. */
export interface KeywordBankRow extends Record<string, unknown> {
  keyword: string
  in_listing: boolean
  sources: string[]
  runs_ads: boolean
  has_sqp: boolean
  match_type: string | null
  ppc_impressions: number | null
  ppc_clicks: number | null
  ppc_spend: number | null
  ppc_sales: number | null
  ppc_orders: number | null
  ppc_acos: number | null
  current_bid: number | null
  search_query_volume: number | null
  sqp_impressions: number | null
  our_impressions: number | null
  impression_share: number | null
  click_share: number | null
  purchase_share: number | null
  js_search_volume?: number | null
  js_sov_pct?: number | null
  js_sov_rank?: number | null
  js_pulled_at?: string | null
  /** Build 29 — why this keyword is losing money and who fixes it. Derived at
   *  read time by the backend (`brand_analytics._diagnose_keyword`); nothing is
   *  stored. All three move together: a row carries a reason WITH its action
   *  and owner, or all three null. Null is the common, honest answer — it means
   *  nothing measurable is provably wrong, not that the keyword is fine. */
  losing_reason?: 'not_advertised' | 'paying_for_organic' | 'listing_relevance' | 'price' | null
  recommended_action?: string | null
  action_owner?: 'ppc' | 'listing' | 'pricing' | null
  /** An ENABLED ad target exists. Narrower than `runs_ads` (any target, any
   *  state) — a paused-only keyword has runs_ads true and ads_active false. */
  ads_active?: boolean
  /** Our SQP purchases minus ad orders, floored at 0. Covers only the newest
   *  ~30 days of shopper-search data the bank read — the ad orders subtracted
   *  are a 30-day figure, so the two sides have to span the same length of
   *  time. Null when shopper-search data has no recent rows for the keyword. */
  organic_orders?: number | null
  /** Our median price vs the whole-market median on this search (0.15 = we are
   *  15% dearer, negative = cheaper). Null whenever either median is missing,
   *  which is most rows. */
  price_premium_pct?: number | null
  /** The counts behind `purchase_share`, over the whole period covered. A
   *  share is unreadable without them — 100% of two purchases is not market
   *  dominance — so anything that shows the share shows these too. */
  sqp_purchases?: number | null
  market_purchases?: number | null
  /** Which of Amazon's two per-search medians `price_premium_pct` came from:
   *  what shoppers PAID, or what shoppers CLICKED on. Different claims — the
   *  copy shown to an operator changes with it. Null exactly when
   *  `price_premium_pct` is. */
  price_basis?: 'purchase' | 'click' | null
}

/** GET /api/ops/brand-analytics/keyword-bank — the per-ASIN keyword bank.
 *  Amazon-only + NULL-tolerant; an ASIN with no PPC + no SQP rows returns the
 *  honest empty-state (available=false). */
export function getKeywordBank(
  asin: string,
  marketplace: BAMarketplace,
  opts?: { from?: string; to?: string; signal?: AbortSignal },
): Promise<BAListResponse<KeywordBankRow>> {
  const qs = new URLSearchParams({ asin, marketplace })
  if (opts?.from) qs.set('from', opts.from)
  if (opts?.to) qs.set('to', opts.to)
  return opsFetch<BAListResponse<KeywordBankRow>>(`/api/brand-analytics/keyword-bank?${qs}`, {
    signal: opts?.signal,
  })
}

// ── Gap metrics (Track C P4) — Sessions/traffic gap + Subscribe&Save gap ─────
// Two READ surfaces consuming data that is still backfilling upstream, so both
// are NULL-tolerant + Amazon-only: empty/partial data renders cleanly and lights
// up as the producer lands data (Sessions via the Sales & Traffic report; SnS
// via the future md_sns_performance mirror — see CLICKPAY_GAP_PLUMBING_NOTE.md).
// Both surfaces ride their aggregate header as the first `items` element under
// `__summary__`, matching Listing Health / Reviews & Returns.

/** GET /api/ops/brand-analytics/traffic-gap — the Sessions vs page-views gap. */
export const getTrafficGap = (
  m: BAMarketplace, o?: { from?: string; to?: string; signal?: AbortSignal },
): Promise<BAListResponse> => getBrandAnalytics('traffic-gap', m, o)

/** Aggregate header for the Traffic-gap surface. Mirrors backend
 *  models.TrafficGapSummary. ``sessions_available`` is false until the daily
 *  Sales & Traffic report populates sessions — until then the session-based
 *  fields are NULL and the page-view conversion is the proxy. */
export interface TrafficGapSummary {
  total_units: number
  total_page_views: number
  total_sessions: number
  avg_conv_rate: number | null
  avg_session_conv_rate: number | null
  peer_session_conv_rate: number | null
  total_unconverted_sessions: number
  total_session_gap_units: number | null
  window_start: string | null
  window_end: string | null
  sessions_available: boolean
}

/** Per-ASIN Traffic-gap row (after the `__summary__` element). Mirrors backend
 *  models.TrafficGapRow. Session-based fields are NULL until the sessions feed
 *  lands; ``conv_rate`` (page-view conversion) is the proxy in the meantime. */
export interface TrafficGapRow {
  asin: string
  uid: string | null
  product_name: string | null
  image_url: string | null
  units: number | null
  page_views: number | null
  sessions: number | null
  revenue: number | null
  conv_rate: number | null
  session_conv_rate: number | null
  unconverted_sessions: number | null
  session_gap_units: number | null
}

/** GET /api/ops/brand-analytics/sns-gap — the Subscribe & Save gap. Returns the
 *  honest empty-state (available=false) until the md_sns_performance producer
 *  lands the table, then lights up automatically. */
export const getSnsGap = (
  m: BAMarketplace, o?: { from?: string; to?: string; signal?: AbortSignal },
): Promise<BAListResponse> => getBrandAnalytics('sns-gap', m, o)

/** Aggregate header for the SnS-gap surface. Mirrors backend
 *  models.SnsGapSummary. All zero/NULL until the SnS mirror lands;
 *  ``sns_available`` flags the transition. */
export interface SnsGapSummary {
  asin_count: number
  total_active_subscriptions: number
  total_forecast_units: number | null
  total_shipped_units: number
  total_units: number
  sns_units_share: number | null
  total_oos_risk_units: number | null
  window_start: string | null
  window_end: string | null
  sns_available: boolean
}

/** Per-ASIN SnS-gap row (after the `__summary__` element). Mirrors backend
 *  models.SnsGapRow. ``oos_risk_units`` (forecast SnS demand on-hand can't
 *  cover) is the actionable lost-subscriber number; NULL until a forecast lands. */
export interface SnsGapRow {
  asin: string
  uid: string | null
  product_name: string | null
  image_url: string | null
  active_subscriptions: number | null
  forecast_units: number | null
  shipped_units: number | null
  sns_units_share: number | null
  total_units: number | null
  oos_risk_units: number | null
}

/** One row of the Reviews & Returns 'top return reasons' breakdown. */
export interface ReturnReasonCount {
  reason: string | null
  count: number
}

// ── Listing Traffic over time (Track D / F5) — daily sessions/CVR chart ──────
// Per-product (or marketplace-wide) traffic history for the Listings-tab chart.
// Backend route registers at /api/ops/brand-analytics/listing-traffic-daily; we
// call `/api/brand-analytics/...` and let `withApiBase` add the single `/ops`
// segment (calling `/api/ops/...` here would double-prefix).

/** One day of listing traffic. `conv_rate` is units ÷ page_views (page-view
 *  conversion) until the sessions feed populates; `returned_units`/`return_rate`
 *  are null when there are no returns that day (or in aggregate mode). */
export interface ListingTrafficPoint {
  day: string
  units: number | null
  page_views: number | null
  sessions: number | null
  revenue: number | null
  conv_rate: number | null
  session_conv_rate: number | null
  returned_units: number | null
  return_rate: number | null
}

/** Envelope for the listing-traffic-daily surface. `points` is empty (not an
 *  error) off Amazon / absent perf view / empty window — the chart shows its
 *  own 'no data yet' state and lights up as the backfill lands. */
export interface ListingTrafficResponse {
  points: ListingTrafficPoint[]
  /** False until the sessions feed populates (page-view CVR only). */
  sessions_available: boolean
  asin: string | null
  marketplace: string
  window_start: string | null
  window_end: string | null
  available: boolean
}

export function getListingTrafficDaily(
  marketplace: BAMarketplace,
  opts?: { asin?: string; from?: string; to?: string; signal?: AbortSignal },
): Promise<ListingTrafficResponse> {
  const qs = new URLSearchParams({ marketplace })
  if (opts?.asin) qs.set('asin', opts.asin)
  if (opts?.from) qs.set('from', opts.from)
  if (opts?.to) qs.set('to', opts.to)
  return opsFetch<ListingTrafficResponse>(
    `/api/brand-analytics/listing-traffic-daily?${qs}`,
    { signal: opts?.signal },
  )
}

// ── BSR over time (per-product) — daily Best Sellers Rank chart ──────────────
// Always single-ASIN: BSR is per-ASIN + category-specific, never aggregated.
// Backend route registers at /api/ops/brand-analytics/bsr-daily; we call
// `/api/brand-analytics/...` and let `withApiBase` add the single `/ops` segment.

/** One day of Best Sellers Rank. `sales_rank` is LOWER = BETTER (rank #1 is the
 *  category's best-seller); the chart inverts the axis. Null when no rank was
 *  recorded that day. */
export interface BsrDailyPoint {
  date: string
  sales_rank: number | null
}

/** Envelope for the bsr-daily surface. `points` is empty (not an error) off
 *  Amazon / absent table / empty window — the chart shows its own 'no data yet'
 *  state and lights up as the daily sync + one-time backfill land points. */
export interface BsrDailyResponse {
  points: BsrDailyPoint[]
  asin: string | null
  marketplace: string
  window_start: string | null
  window_end: string | null
  available: boolean
}

export function getBsrDaily(
  marketplace: BAMarketplace,
  asin: string,
  opts?: { from?: string; to?: string; signal?: AbortSignal },
): Promise<BsrDailyResponse> {
  const qs = new URLSearchParams({ marketplace, asin })
  if (opts?.from) qs.set('from', opts.from)
  if (opts?.to) qs.set('to', opts.to)
  return opsFetch<BsrDailyResponse>(
    `/api/brand-analytics/bsr-daily?${qs}`,
    { signal: opts?.signal },
  )
}

// ── Share of Voice over time (per keyword) — Build 25 ────────────────────────
// Always single-keyword: SoV is a per-search-term contest, so averaging it
// across terms produces a number with no meaning. The keyword bank shows the
// LATEST snapshot (js_sov_pct / js_sov_rank); this is the history behind it.

/** One day of Share of Voice for one keyword. `our_sov` is a 0..1 share and is
 *  null on a day Jungle Scout tracked the keyword but did not report our brand
 *  at all — that is not a 0% share and must not be drawn as one.
 *  `top_competitor_brand` is whoever led the keyword THAT DAY excluding us, so
 *  it can legitimately change from point to point. */
export interface SovTrendPoint {
  date: string
  our_sov: number | null
  our_rank: number | null
  top_competitor_brand: string | null
  top_competitor_sov: number | null
  brands_tracked: number
}

/** Envelope for the sov-trend surface. `points` is empty (not an error) off
 *  Amazon / absent table / unresolvable brand / a window with no snapshots.
 *  `our_brand` is null EXACTLY when the brand could not be resolved from the
 *  ASIN's listing — the panel distinguishes that from "no history yet", because
 *  the two have completely different fixes. */
export interface SovTrendResponse {
  points: SovTrendPoint[]
  keyword: string
  asin: string | null
  marketplace: string
  our_brand: string | null
  window_start: string | null
  window_end: string | null
  days: number
  available: boolean
}

export function getSovTrend(
  marketplace: BAMarketplace,
  keyword: string,
  opts?: { asin?: string; brand?: string; from?: string; to?: string; signal?: AbortSignal },
): Promise<SovTrendResponse> {
  const qs = new URLSearchParams({ marketplace, keyword })
  if (opts?.asin) qs.set('asin', opts.asin)
  if (opts?.brand) qs.set('brand', opts.brand)
  if (opts?.from) qs.set('from', opts.from)
  if (opts?.to) qs.set('to', opts.to)
  return opsFetch<SovTrendResponse>(
    `/api/brand-analytics/sov-trend?${qs}`,
    { signal: opts?.signal },
  )
}

/** Aggregate header for the Reviews & Returns surface. Rides as the first
 *  `items` element under key `__summary__` so the panel renders a header
 *  without a second round-trip. */
export interface ReviewsReturnsSummary {
  avg_star_rating: number | null
  total_review_count: number | null
  rated_asin_count: number
  total_returns: number
  returned_units: number
  distinct_returned_asins: number
  return_window_start: string | null
  return_window_end: string | null
  top_return_reasons: ReturnReasonCount[]
}

/** Per-ASIN Reviews & Returns row (everything after the `__summary__` element). */
export interface ReviewsReturnsRow {
  asin: string
  star_rating: number | null
  review_count: number | null
  return_count: number
  returned_units: number
  top_return_reason: string | null
}

// ---------------- Customer-Order Stream (WS3 hub Orders sub-tab) ------------
// Read-only per-marketplace customer orders over the BQ-synced customer_orders
// mirror. Backend route registers at /api/ops/customer-orders/*; we call
// `/api/customer-orders/*` and let `withApiBase` add the single `/ops` segment
// (calling `/api/ops/...` here would double-prefix to `/api/ops/ops/...`).

/** One per-ORDER rollup row (LIST result + DETAIL header). */
export interface CustomerOrderRow {
  order_id: string
  order_date: string | null
  marketplace: string | null
  order_status: string | null
  line_count: number
  units: number
  revenue_usd: number | null
  total_fees_usd: number | null
  promo_usd: number | null
  cogs_usd: number | null
  order_profit_usd: number | null
  /** False when ANY line lacks a FIFO cost — drives the "partial" COGS badge. */
  all_cogs_covered: boolean
  /** B5-fe: true when `total_fees_usd`/`order_profit_usd` were computed with
   *  Amazon fees excluded (rendered as $0) rather than a real fee figure —
   *  drives the "estimated (fees excluded)" badge. Optional/undefined until
   *  the backend contract ships it; guard on it rather than assuming false. */
  fees_estimated?: boolean
  /** Ticket #20 — distinct per-line UIDs for this order, sorted + comma-
   *  joined (a multi-item order can span several UIDs). Null when the feed
   *  predates this field or every line missed the products uid bridge. */
  uids?: string | null
  /** Ticket #20 — distinct per-line listing ids (asin, falling back to sku
   *  when asin is NULL/empty), sorted + comma-joined. */
  listing_ids?: string | null
}

/** One order LINE: revenue, fee breakdown, COGS (NULL-preserved), profit. */
export interface CustomerOrderLine {
  order_id: string
  /** Ticket #20 — per-line UID (null on a products-bridge miss) so the
   *  order-detail expansion stays legible once the LIST row's `uids`
   *  becomes a comma-joined aggregate. */
  uid?: string | null
  asin: string | null
  sku: string | null
  title: string | null
  units: number
  revenue_usd: number | null
  /** Fees arrive negative-signed from Amazon (a cost) and stay negative. */
  referral_fee_usd: number | null
  fba_fee_usd: number | null
  other_fees_usd: number | null
  promo_usd: number | null
  refund_fee_usd: number | null
  /** null when no FIFO cost for this UID/date — preserved, never zeroed. */
  line_cogs_usd: number | null
  cogs_covered: boolean
  line_profit_usd: number | null
  line_margin_pct: number | null
}

/** DETAIL envelope: the order rollup header + its line items. */
export interface CustomerOrderDetail {
  order: CustomerOrderRow
  lines: CustomerOrderLine[]
}

/** F4 (step 19/20/21): the whole-WINDOW response envelope — `total`/`totals`
 *  are computed over every row the filters match (not the possibly-truncated
 *  `items` page), so a sticky footer can show the real window sum instead of
 *  footing whatever page it was handed. `totals` keys mirror `_TOTALS_COLS`
 *  on the backend exactly: units, revenue_usd, total_fees_usd, promo_usd,
 *  cogs_usd, order_profit_usd. `truncated` is true when `total` exceeds the
 *  number of items actually returned (the page didn't cover the whole
 *  window). Mirrors backend CustomerOrdersListResponse (ops/models.py). */
export interface CustomerOrdersListResponse extends ListResponse<CustomerOrderRow> {
  date_from: string
  date_to: string
  totals: Record<string, number>
  truncated: boolean
}

/** Per-marketplace customer-order LIST. The hub passes the level-1 tab
 *  marketplace down (incl. the synthetic 'all' Supplier-Reorder tab — hence
 *  BAMarketplace, which is HubMarketplace | 'all'); 'all' drops the filter so
 *  every marketplace's orders return. The backend empty-states a not-yet-synced
 *  feed (count:0) rather than erroring.
 *
 *  F4 — `opts.dateFrom`/`opts.dateTo` bound the window the returned
 *  `totals` are summed over (server default: trailing 30 days ending today,
 *  server wall-clock date — `order_date` is a plain DATE column, not a UTC
 *  timestamp). Omit both to use the server default. */
export function getCustomerOrders(
  marketplace: BAMarketplace,
  opts?: { signal?: AbortSignal; dateFrom?: string; dateTo?: string },
): Promise<CustomerOrdersListResponse> {
  const p = new URLSearchParams()
  if (marketplace !== 'all') p.set('marketplace', marketplace)
  if (opts?.dateFrom) p.set('date_from', opts.dateFrom)
  if (opts?.dateTo) p.set('date_to', opts.dateTo)
  const qs = p.toString() ? `?${p}` : ''
  return opsFetch<CustomerOrdersListResponse>(
    `/api/customer-orders${qs}`,
    { signal: opts?.signal },
  )
}

/** Per-order DETAIL drill-in (line items + per-line fees + profit). */
export function getCustomerOrderDetail(
  orderId: string,
  opts?: { signal?: AbortSignal },
): Promise<CustomerOrderDetail> {
  return opsFetch<CustomerOrderDetail>(
    `/api/customer-orders/${encodeURIComponent(orderId)}`,
    { signal: opts?.signal },
  )
}

// ---------------- Operators (TRACK D / F4.1) --------------------------------
// The operator (employee) list that drives the Manager picker on the Products
// tracker and the Listings sub-tab. UNLIKE every other call in this file, this
// endpoint lives on the MAIN ClickPay app (/api/operators), NOT the ops half —
// bbpro_operators is owned by the main migration chain (087/088) so the app-wide
// scoping phase (F4.6) can join it to the entity views. We therefore call the
// path AS-IS via `mainApi` (which does NOT apply `withApiBase`'s /ops rewrite);
// same-origin so the session cookie + X-User flow exactly as the ops `api`
// helper. NULL-tolerant: an unseeded table answers [] and the picker just offers
// nothing rather than erroring.
//
// The PM ASSIGNMENT itself does NOT live here. It is written by PATCHing
// /api/products/{uid} { manager }, which lands in md_product_managers — the one
// store the scoping resolver reads. The old /api/product-owner client
// (bbpro_product_owner) was removed 2026-07-14: it was a second, never-populated
// store, so the Listings tab showed every product as "Unassigned" regardless of
// the PM sheet. The backend endpoints survive as CRUD-only; nothing calls them.

/** Same-origin fetch to the MAIN app API (no /ops rewrite). Mirrors `api`'s
 *  header + error handling but leaves the path untouched. */
async function mainApi<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const headers: Record<string, string> = { [USER_HEADER]: _currentUser }
  if (_apiKey) headers[API_KEY_HEADER] = _apiKey
  // F4.6 — stamp the Operator scope for parity with the other wrappers. The
  // owner/operators CRUD endpoints ignore it, but keeping every wrapper
  // consistent means a future scoped main-app read picks it up automatically.
  Object.assign(headers, operatorScopeHeaders())
  if (opts.headers) Object.assign(headers, opts.headers)
  const init: RequestInit = {
    method: opts.method || 'GET',
    headers,
    signal: opts.signal,
  }
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(opts.body)
  }
  const res = await fetch(path, init)
  if (!res.ok) {
    if (res.status === 401) emitSessionExpired()
    const text = await res.text()
    let detail: unknown = text
    try { detail = JSON.parse(text) } catch { /* plain text */ }
    throw new Error(`${res.status} ${res.statusText}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** An operator (employee) — drives the Manager-picker dropdown options. The
 *  backend validates a manager assignment against these same display names. */
export interface Operator {
  id: number
  slug: string
  display_name: string
  email: string | null
  color: string | null
  last_active_at: string | null
}

/** List the active operators (the Manager-picker options). NULL-tolerant: an
 *  unseeded operators table answers `{operators: []}`. */
export function getOperators(opts?: { signal?: AbortSignal }): Promise<{ operators: Operator[] }> {
  return mainApi<{ operators: Operator[] }>('/api/operators', { signal: opts?.signal })
}



// ───────────────────────────────────────────────────────────────────────────
// Purchasing coverage (Build 40) — "how much of my received purchasing is not
// in the book", per PO, plus the button that fixes it.
//
// Every fetcher takes an AbortSignal. The panel fetches on mount and on
// explicit refresh ONLY — no polling anywhere in this feature. Open tabs
// polling unattended is what produced ERR_INSUFFICIENT_RESOURCES before.
// ───────────────────────────────────────────────────────────────────────────

/** One PO in the coverage panel's top-level list. */
export interface CoveragePo {
  po_number: string
  supplier: string | null
  /** GoFlow lines on this PO that report a received quantity. */
  goflow_lines: number
  /** Units GoFlow says arrived, across those lines. */
  goflow_units: number
  /** Lines ClickPay already holds in some form (covered, mismatched or deleted). */
  booked_lines: number
  gap_lines: number
  gap_units: number
  gap_dollars: number
  mismatch_lines: number
  deleted_lines: number
  covered_lines: number
  /** Units GoFlow says arrived that ClickPay does not record as arrived —
   *  whether the line is missing outright or present but still "Ordered". */
  unbooked_units: number
  ordered_date: string | null
  actual_arrival: string | null
  goflow_status: string | null
  /** Parked lines (see CoverageClass). A PO whose lines are all parked has
   *  zero gap lines and no Import button, which is why it still shows up in
   *  the default gaps-only view: parked and invisible is indistinguishable
   *  from fixed. */
  needs_normalization_lines: number
  duplicate_po_lines: number
  duplicate_of: string | null
  /** One sentence an employee can act on, or null when the PO is not parked. */
  parked_reason: string | null
}

/** How a (po, uid) pair compares between GoFlow and the book.
 *
 *  The last two are PARKED classes (Phase 7 D9/D13) — properties of the PO
 *  itself rather than of a comparison, evaluated before `gap` so the Import
 *  action excludes them with no second gate to keep in sync:
 *    needs_normalization  GoFlow states several PO numbers in one field.
 *    duplicate_po         another PO states the identical line set. */
export type CoverageClass = 'gap' | 'deleted_in_book' | 'mismatch' | 'covered'
  | 'needs_normalization' | 'duplicate_po'

/** One (po, uid) line, with BOTH sides' numbers carried side by side.
 *  A `mismatch` row deliberately reports goflow_* AND book_* without the
 *  backend expressing an opinion about which is correct. */
export interface CoverageLine {
  po_number: string
  uid: string
  coverage_class: CoverageClass
  goflow_lines: number
  goflow_qty: number | null
  goflow_received: number | null
  goflow_unit_cost: number | null
  goflow_total_cost: number | null
  goflow_status: string | null
  goflow_raw_status: string | null
  goflow_ordered_date: string | null
  goflow_eta: string | null
  goflow_actual_arrival: string | null
  book_rows: number
  book_live_rows: number
  book_qty: number | null
  /** Units ClickPay counts as ARRIVED. A line at status "Ordered" reads 0 even
   *  when GoFlow says the stock landed — that difference is a mismatch. */
  book_received: number | null
  book_unit_cost: number | null
  book_landed_po_cost: number | null
  book_status: string | null
  book_source: string | null
  gap_dollars: number | null
  unbooked_units: number
  supplier: string | null
  /** Only meaningful on a `duplicate_po` row: the OTHER PO number(s) stating
   *  this same line set, each labelled with its own earliest ordered date, so
   *  a genuine repeat order is distinguishable from a double entry at a
   *  glance. Carried on every row so the payload shape never changes. */
  duplicate_of: string | null
}

export interface CoverageMetrics {
  po_coverage_mode: 'baseline' | 'zero'
  po_coverage_baseline_lines: number | null
  po_coverage_mirror_lines: number | null
  po_coverage_mirror_synced_at: string | null
  po_coverage_received_lines: number | null
  po_coverage_received_units: number | null
  po_coverage_gap_lines: number | null
  po_coverage_gap_units: number | null
  po_coverage_gap_dollars: number | null
  po_coverage_gap_pos: number | null
  po_coverage_mismatch_lines: number | null
  po_coverage_deleted_lines: number | null
  po_coverage_covered_lines: number | null
  po_coverage_needs_normalization_lines: number | null
  po_coverage_duplicate_po_lines: number | null
  /** The invariant's own number: 0 means every received unit is in the book. */
  po_coverage_unbooked_received_units: number | null
  /** NULL when the ledger read cleanly. A string means the query itself FAILED,
   *  which otherwise reads exactly like a reconciled book — every counter above
   *  resolves null and the panel shows nothing to do. */
  po_coverage_query_error: string | null
}

export interface CoverageSummary {
  enabled: boolean
  invariant: string
  metrics: CoverageMetrics
  warnings: string[]
  pos: CoveragePo[]
}

/** What Import will do to one PO — returned by the detail fetch AND by a dry
 *  run, computed by the same backend function so a preview cannot promise
 *  something the commit does not deliver. */
export interface CoveragePreview {
  po_number: string
  import_lines: number
  import_units: number
  import_dollars: number
  /** Always 0 as of Phase 7 D8 — no imported line gets a received_at, because
   *  GoFlow records no arrival date at any grain. Kept for payload stability. */
  lines_with_received_date: number
  lines_without_received_date: number
  /** How the conditional status splits: a part-arrived line books 'In Transit',
   *  never a flat 'Received' (D6). */
  import_lines_received: number
  import_lines_in_transit: number
  skipped_mismatch_lines: number
  skipped_deleted_lines: number
  /** Lines held back because their PO is comma-joined or has a twin. */
  skipped_parked_lines: number
  goflow_lines_on_po: number
  pnl_effect: {
    cogs_effect_now: number
    cogs_effect_when_costed: number
    explanation: string
  }
  will_write: Record<string, string>
  lines: CoverageLine[]
  mismatches: CoverageLine[]
  deleted: CoverageLine[]
  parked: CoverageLine[]
  all_lines?: CoverageLine[]
  invariant?: string
  /** Present on an import response. */
  dry_run?: boolean
  imported_lines?: number
  imported?: Array<{ uid: string; qty: number; qty_received: number; dollars: number | null }>
  failed?: Array<{ uid: string; error: string }>
  message?: string
}

/** Feature-flag probe. No DB access on the backend — cheap enough for the tab
 *  strip to call once on mount to decide whether the tab exists at all. */
export function getCoverageEnabled(opts?: { signal?: AbortSignal }): Promise<{ enabled: boolean }> {
  return opsFetch<{ enabled: boolean }>('/api/orders/coverage?probe=1', { signal: opts?.signal })
}

/** Per-PO coverage summary. Read-only; this endpoint has no write path. */
export function getCoverageSummary(
  opts?: { gapsOnly?: boolean; limit?: number; signal?: AbortSignal },
): Promise<CoverageSummary> {
  const q = new URLSearchParams()
  if (opts?.gapsOnly === false) q.set('gaps_only', 'false')
  if (opts?.limit) q.set('limit', String(opts.limit))
  const qs = q.toString()
  return opsFetch<CoverageSummary>(`/api/orders/coverage${qs ? `?${qs}` : ''}`, {
    signal: opts?.signal,
  })
}

/** One PO, line by line, plus the import preview. */
export function getCoveragePo(
  poNumber: string,
  opts?: { signal?: AbortSignal },
): Promise<CoveragePreview> {
  return opsFetch<CoveragePreview>(
    `/api/orders/coverage/${encodeURIComponent(poNumber)}`,
    { signal: opts?.signal },
  )
}

/** Import a PO's missing received lines.
 *
 *  `dryRun` defaults to TRUE on the BACKEND as well — this wrapper always
 *  sends the field explicitly so a reader of the call site can see which one
 *  it is without checking two files. */
export function importCoveragePo(
  poNumber: string,
  opts: { dryRun: boolean; reason?: string; signal?: AbortSignal },
): Promise<CoveragePreview> {
  return opsFetch<CoveragePreview>(
    `/api/orders/coverage/${encodeURIComponent(poNumber)}/import`,
    {
      method: 'POST',
      body: { dry_run: opts.dryRun, reason: opts.reason ?? null },
      signal: opts.signal,
    },
  )
}

// ── Seller accounts — the per-marketplace account split ─────────────────────
// Backed by GET/PATCH /api/ops/marketplace-accounts. Mirrors the backend
// Pydantic models (AccountOut / MarketplaceGroupOut / AccountsResponse in
// backend/ops/routes/marketplace_accounts.py).
//
// WHY THIS EXISTS: we run more than one seller account on the same
// marketplace (two on Temu, three on Walmart, two on Shopify) and every
// number in the app collapsed them together. The split was always in the
// data — orders_daily.fulfillment_channel carries the GoFlow store id — and
// nothing had grouped by it.

/** One seller account, with its sales over the REQUESTED window. */
export interface MarketplaceAccount {
  account_key: string          // 'GoFlow:1016' | 'Amazon US'
  account_name: string         // 'BB Shop'
  marketplace: string          // 'Temu'
  store_id: number | null
  included_in_totals: boolean
  name_locked: boolean
  units: number
  /** PR 10 (plan I4, OD-3): the P&L figure — the account's share of its
   *  marketplace's P&L sales, the SAME number the Overview shows. */
  sales: number
  orders: number
  pnl_sales?: number
  pnl_units?: number
  /** The raw order feed for this account (every order row, before the P&L's
   *  sold / removal filters) — the "Order feed" column. */
  feed_sales?: number
  /** 'pnl' | 'scaled' (a switched-off account) | 'orders' (no P&L yet). */
  sales_basis?: string | null
  /** 0..1 of the marketplace's window sales, computed over ALL accounts
   *  (including excluded ones) so the shares always sum to 1. */
  share_of_marketplace: number | null
  first_sale_date: string | null
  last_sale_date: string | null
}

/** One marketplace and the accounts under it. Carries BOTH totals: `sales`
 *  counts included accounts only, `sales_all_accounts` counts every account.
 *  Both are on the wire so no caller has to derive one from the other and
 *  risk two screens disagreeing. */
export interface MarketplaceAccountGroup {
  marketplace: string
  accounts: MarketplaceAccount[]
  units: number
  sales: number
  units_all_accounts: number
  sales_all_accounts: number
  excluded_accounts: number
  /** PR 10 (I4): the marketplace's P&L figure (== `sales`), the included
   *  accounts' raw order feed, and how the two tie. */
  pnl_sales?: number
  feed_sales?: number
  reconciliation?: MarketplaceReconciliation | null
}

/** PR 10 (plan I4) — how a marketplace's raw order feed ties to its P&L
 *  figure over one window (backend marketplace_overview.reconcile_marketplace):
 *  feed − removal orders − cancelled (`never_sold`) − GoFlow orders not
 *  shipped yet (`pending_recent`, under 7 days; Amazon pending is counted by
 *  the P&L so it is 0 there) − not linked to a product − other = P&L.
 *  `pnl_behind` when `other` is more than 2 % of the P&L ("The P&L is still
 *  catching up"). USD. */
export interface MarketplaceReconciliation {
  pnl_sales: number
  feed_sales_all: number
  pending_recent: number
  never_sold: number
  removal_orders: number
  unlinked_sales: number
  other: number
  pnl_behind: boolean
  /** Temu: the P&L re-prices orders, so the feed is not expected to tie. */
  price_restated?: boolean
}

export interface MarketplaceAccountsResponse {
  date_from: string
  date_to: string
  marketplaces: MarketplaceAccountGroup[]
  total_sales: number
  total_sales_all_accounts: number
  /** PR 10 (I4): what set the window's end (the Overview's own rule;
   *  ['requested'] for an explicit range) and its length in days. */
  limited_by?: string[]
  window_days?: number
  /** FALSE while md_pnl (a nightly rebuild) still carries the pre-exclusion
   *  numbers, so the UI can say so instead of letting the operator discover
   *  that the P&L page and this one disagree. */
  pnl_reflects_exclusions: boolean
}

export async function getMarketplaceAccounts(opts: {
  dateFrom?: string
  dateTo?: string
  /** PR 10 (I4): with no dates, the last N COMPLETE days — the Overview's
   *  own window, so the two tabs read the same dates. */
  days?: number
  marketplace?: string
  signal?: AbortSignal
} = {}): Promise<MarketplaceAccountsResponse> {
  const p = new URLSearchParams()
  if (opts.days != null) p.set('days', String(opts.days))
  if (opts.dateFrom) p.set('date_from', opts.dateFrom)
  if (opts.dateTo) p.set('date_to', opts.dateTo)
  if (opts.marketplace) p.set('marketplace', opts.marketplace)
  const qs = p.toString()
  return opsFetch<MarketplaceAccountsResponse>(
    `/api/marketplace-accounts${qs ? `?${qs}` : ''}`,
    { signal: opts.signal },
  )
}

/** Rename an account, or switch it in/out of the totals. The key is
 *  encodeURIComponent'd because GoFlow keys contain a colon and the Amazon
 *  keys contain a space. */
export async function patchMarketplaceAccount(
  accountKey: string,
  patch: { included_in_totals?: boolean; account_name?: string; reason?: string },
): Promise<MarketplaceAccount> {
  return opsFetch<MarketplaceAccount>(
    `/api/marketplace-accounts/${encodeURIComponent(accountKey)}`,
    { method: 'PATCH', body: patch },
  )
}

// ── Inventory cubic feet (CBF) ──────────────────────────────────────────────
// Three legs — on order from the supplier, our own warehouses, FBA — each
// from a different source because the obvious one is wrong for each (see
// backend/ops/routes/inventory_volume.py's module docstring). Every leg
// reports its own coverage: units we hold but cannot size are counted in
// `units_without_volume` and EXCLUDED from `cubic_feet`, never folded in as 0.

/** One leg's total, with the coverage that qualifies it. */
export interface VolumeLeg {
  cubic_feet: number
  units: number
  /** Units with no size on file — excluded from `cubic_feet`, not zeroed. */
  units_without_volume: number
  uids_without_volume: number
  /** Plain-English provenance, rendered under the leg's card. */
  source_label: string
  as_of: string | null
  available: boolean
  /** Why this leg has nothing to show (e.g. no FBA feed for Amazon MX). */
  unavailable_reason: string | null
}

export interface VolumeItem {
  uid: string
  product_name: string | null
  /** Per-unit CBM for the leg named by `cbm_per_unit_leg` — NOT a per-product
   *  constant. A product with stock in both FBA and the warehouse is sized by
   *  two different figures, and this is only one of them. */
  cbm_per_unit: number | null
  /** Which rung of the resolver produced it: 'carton' | 'unit_dims' | 'none'. */
  volume_source: string
  /** Which leg `cbm_per_unit` describes: 'fba' | 'on_order_warehouse' | 'none'. */
  cbm_per_unit_leg: string
  /** Carton volume / case pack — what sized the on-order and warehouse legs. */
  cbm_per_unit_carton: number | null
  /** The product's own measured L x W x H — what sized the FBA leg. */
  cbm_per_unit_unit_dims: number | null
  /** Set when the two routes contradict each other by more than the
   *  response's `dim_conflict_ratio`: the product's cubic feet are reported
   *  but not trustworthy. Plain English, backend-written — render as-is. */
  volume_conflict: string | null
  on_order_units: number
  warehouse_units: number
  fba_units: number
  on_order_cbf: number | null
  warehouse_cbf: number | null
  fba_cbf: number | null
  total_cbf: number | null
}

export interface WarehouseSplit {
  warehouse_name: string | null
  units: number
  cubic_feet: number
}

export interface InventoryVolumeResponse {
  marketplace: string
  on_order: VolumeLeg
  warehouse: VolumeLeg
  fba: VolumeLeg
  /** Accepted by Amazon but not yet received — kept OUT of the FBA leg. */
  fba_inbound_units: number
  fba_inbound_cubic_feet: number
  warehouse_split: WarehouseSplit[]
  /** Products carrying stock whose carton route and unit-dimensions route
   *  contradict each other, and the units they represent. */
  volume_conflict_uids: number
  volume_conflict_units: number
  /** The factor the two per-unit volume routes must differ by before a row is
   *  flagged — the backend's own DIM_CONFLICT_RATIO (inventory_volume.py
   *  imports it from engine/carton_cbm.py). The tab prints THIS rather than a
   *  literal, so tuning the constant cannot leave the copy lying. */
  dim_conflict_ratio: number
  items: VolumeItem[]
}

/** GET /api/ops/inventory-volume — cubic feet on order / in WH / in FBA.
 *  Only the FBA leg is marketplace-scoped; stock is not owned by a
 *  marketplace until it ships to one. */
export function getInventoryVolume(
  marketplace: string,
  opts?: { signal?: AbortSignal },
): Promise<InventoryVolumeResponse> {
  const qs = new URLSearchParams({ marketplace })
  return opsFetch<InventoryVolumeResponse>(`/api/inventory-volume?${qs}`, {
    signal: opts?.signal,
  })
}


// ─── Receive workspace ────────────────────────────────────────────────────
// GET /api/receive/{ship} — the whole receive page in one call. Replaces the
// six-endpoint assembly (queue list + auto-fetch + scan-shifl + classify +
// receive/extract + reconcile/issues) the old page stitched together on the
// client, along with the filename regex it ran to guess what the server
// already knew.
export interface WorkspaceShipment {
  shipping_number: string
  status?: string | null
  etd?: string | null
  eta?: string | null
  delivered_date?: string | null
  carrier?: string | null
  container_number?: string | null
  container_type?: string | null
  origin?: string | null
  dest?: string | null
  docs_link?: string | null
  ocean_freight_usd?: number | null
  brokerage_usd?: number | null
  delivery_chassis_usd?: number | null
  customs_duty_usd?: number | null
  /** Real money that is not freight, brokerage, drayage or duty --
   *  liquidated damages, warehousing, and whatever Shifl invents next. */
  other_costs_usd?: number | null
}
export interface WorkspaceQueue {
  status?: string | null
  reason?: string | null
  dismiss_reason?: string | null
  queued_at?: string | null
  auto_fetched_at?: string | null
  actor?: string | null
}
export interface WorkspaceDocument {
  /** Where the file lives, not what it is — one list, provenance as a field. */
  source: 'shifl' | 'drive' | 'filed'
  id?: string | null
  name: string
  /** ClickPay code (PL / I / 7501 / BOL), null when unrecognized. */
  doc_type?: string | null
  raw_type?: string | null
  mime_type?: string | null
  web_view_link?: string | null
  shifl_path?: string | null
  size?: string | number | null
}
export interface WorkspaceLine {
  po_number: string
  uid: string
  ship_key: string
  product_name?: string | null
  status?: string | null
  qty?: number | null
  cartons?: number | null
  item_cost_fob?: number | null
  version?: number | null
  /** This line's volume (cartons x resolved CBM-per-carton). Null when the
   *  line has no cartons or no usable carton volume — never a silent 0. */
  cbm?: number | null
  /** Which rung of the carton-CBM cascade produced it, so a volume inferred
   *  from a shipment average is distinguishable from a measured one. */
  cbm_source?: string | null
  /** md_orders.supplier as typed on the line (#105). */
  supplier?: string | null
}
export interface WorkspaceTotals {
  cartons?: number | null
  cbm?: number | null
  units?: number | null
  /** Lines that could not contribute. A total that silently omits them reads
   *  as authoritative while being low. */
  cartons_missing: number
  cbm_missing: number
  /** False when any contributing volume came from an inferred rung. */
  cbm_measured: boolean
}
export interface WorkspaceCrossref {
  billed_by_shifl: string[]
  known_to_clickpay: string[]
  billed_but_not_linked: string[]
  linked_but_not_billed: string[]
}
export interface WorkspaceInvoice {
  invoice_id?: number | null
  invoice_number?: string | null
  invoice_date?: string | null
  total_amount?: number | null
  payment_status?: string | null
  columns?: Record<string, number>
  /** A service the mapping did not recognise. `booked_to` names the cost
   *  column it was folded into anyway, so the charge is in landed cost
   *  even though it could not be classified. */
  unmapped?: { service: string; amount: number; booked_to?: string }[]
  reconciles?: boolean
  containers?: string[]
}
export interface ReceiveWorkspace {
  shipment: WorkspaceShipment
  queue?: WorkspaceQueue | null
  documents: WorkspaceDocument[]
  lines: WorkspaceLine[]
  totals?: WorkspaceTotals
  costs?: { invoices?: WorkspaceInvoice[]; purchase_orders?: string[] } | null
  crossref: WorkspaceCrossref
  /** Why the Shifl half is thin. An empty list with no reason would claim the
   *  shipment has no paperwork, which is a much stronger statement. */
  shifl_error?: string | null
  /** The same problem, typed so the page can pick its tone and link. Older
   *  servers omit it; see shiflProblem() in lib/receiveWorkspace. */
  shifl_problem?: ShiflProblem | null
  warnings: string[]
  /** PR 18 (#111): is this shipment's paperwork verified against Shifl.
   *  Computed from the cache + DB — the GET never calls Shifl. */
  check?: ReceiveCheck | null
  /** PR 19 (Q3): the link matcher's suggestions for this shipment — the same
   *  payload as getLinkProposals. Null when the server has no matcher table. */
  link_proposals?: LinkProposals | null
}

// ─── Receive check (PR 18, #111) — mirrors services/receive_check.py ─────
export type ReceiveCheckState = 'complete' | 'needs_look' | 'cannot_check'
export interface ReceiveCheckRow {
  code: string
  label: string
  state: 'pass' | 'fail' | 'unknown' | 'info'
  /** One plain-English sentence. Rendered verbatim. */
  detail: string
  /** False for the info-only rows (suppliers, 7501) — they never decide state. */
  gating: boolean
}
export interface ReceiveCargoSide {
  cartons?: number | null
  cbm?: number | null
  suppliers?: string[]
}
/** shifl_documents.shifl_cargo — what Shifl's shipment detail reports. */
export interface ShiflCargo {
  containers?: { container_number?: string | null; cartons?: number | null; cbm?: number | null; weight_kg?: number | null }[]
  suppliers?: { name?: string | null; cartons?: number | null; cbm?: number | null; weight_kg?: number | null }[]
  total_cartons?: number | null
  total_cbm?: number | null
  total_weight_kg?: number | null
  supplier_name_missing?: boolean
}
/** One PO difference worth a person's look. Display-only: PR 19's Confirm /
 *  Undo live on LinkProposal (the server drops a `link_po` whose PO already
 *  has a live link suggestion, so the two never list the same PO). */
export interface LinkSuggestion {
  /** link_po = Shifl bills it, not linked here; check_po = linked, not billed. */
  kind: 'link_po' | 'check_po'
  shipping_number: string
  po_number: string
  source: 'shifl_invoice'
  evidence: string
  /** link_po only: the open lines of that PO (receive uid-options shape). */
  candidate_lines: Partial<ReceiveUidCandidate>[]
}
export interface ReceiveCheckOverride {
  note: string
  actor: string
  created_at?: string | null
}
export interface ReceiveCheck {
  state: ReceiveCheckState
  checked_against_at?: string | null
  checks: ReceiveCheckRow[]
  shifl_cargo?: ShiflCargo | null
  clickpay_cargo: ReceiveCargoSide
  suggestions: LinkSuggestion[]
  override?: ReceiveCheckOverride | null
}

/** POST /api/receive/{ship}/mark-checked — "Mark as checked anyway". The note
 *  (3–500 characters) is required and audited. Returns the fresh check. */
export function markReceiveChecked(ship: string, note: string): Promise<ReceiveCheck> {
  return opsFetch<ReceiveCheck>(
    `/api/receive/${encodeURIComponent(ship)}/mark-checked`,
    { method: 'POST', body: { note } },
  )
}
/** DELETE /api/receive/{ship}/mark-checked — undo the override (audited). */
export function unmarkReceiveChecked(ship: string): Promise<ReceiveCheck> {
  return opsFetch<ReceiveCheck>(
    `/api/receive/${encodeURIComponent(ship)}/mark-checked`,
    { method: 'DELETE' },
  )
}
/** GET /api/receive/{ship}/file — one document's bytes, for the View drawer.
 *  Read-only; the server serves only files in THIS shipment's cached list. */
export function getReceiveFile(
  ship: string,
  src: { source: 'shifl'; path: string } | { source: 'drive'; id: string },
  opts: { signal?: AbortSignal } = {},
): Promise<Blob> {
  const q = new URLSearchParams({ source: src.source })
  if (src.source === 'shifl') q.set('path', src.path)
  else q.set('id', src.id)
  return opsFetch<Blob>(
    `/api/receive/${encodeURIComponent(ship)}/file?${q}`,
    { signal: opts.signal, asBlob: true },
  )
}

// ─── Link suggestions (PR 19, Q2/Q3) — mirrors models.LinkProposal* ──────
// The PO <-> shipment matcher (services/link_matcher.py) proposes which open
// PO lines belong on a shipment; a person confirms, says "Not this", or undoes.
// Served on the shipments router, embedded in the Receive workspace. Every
// link goes through the same Ship # write as a Timeline edit.

/** One suggested (or applied) link. `reasons` / `blockers` are the engine's
 *  plain-English sentences — shown verbatim. */
export interface LinkProposal {
  id: number
  po_number: string
  uid: string
  product_name?: string | null
  /** link = the whole open line; split = carve `qty` off it; blocked = shown
   *  with its blocker, no Confirm. */
  action: 'link' | 'split' | 'blocked'
  /** Units this suggestion puts on the shipment. */
  qty: number
  /** The open line's quantity when it was scored. */
  open_qty?: number | null
  cartons?: number | null
  /** 0..1 description score; `margin` = gap to the runner-up line. */
  score: number
  margin?: number | null
  status: 'suggested' | 'auto' | 'confirmed' | 'rejected' | 'undone' | 'superseded' | (string & {})
  /** Every automatic-link check passed (stored even while automatic linking
   *  is off) — the "Strong match" pill. */
  auto_eligible: boolean
  /** The evidence contradicts itself (the book holds the item under another
   *  PO, another shipment claims the line, two packing-list lines point at
   *  it): confirmed on its own, never through "Confirm all". */
  needs_review?: boolean
  reasons: string[]
  blockers: string[]
  doc_description?: string | null
  doc_name?: string | null
  /** A person, or 'auto:matcher'. */
  decided_by?: string | null
  decided_at?: string | null
  batch_id?: string | null
  /** Applied ones only, worked out against the live line on read. */
  can_undo: boolean
  undo_blocked_reason?: string | null
}
export interface LinkProposals {
  shipping_number: string
  ship_status?: string | null
  /** The shipment is Delivered: the hourly sweep marks a linked line Received. */
  receives_on_confirm: boolean
  matcher_enabled: boolean
  auto_enabled: boolean
  /** Status suggested (blocked ones included), best score first. */
  suggested: LinkProposal[]
  /** Linked automatically or by a person in the last 14 days, newest first. */
  applied: LinkProposal[]
  rejected_count: number
  evidence?: {
    shifl_pos?: string[]
    shifl_checked_at?: string | null
    documents_read?: number
    documents_unread?: number
  }
  last_run_at?: string | null
}
export interface LinkBatchItem {
  id: number
  ok: boolean
  status: string
  /** One sentence. */
  message: string
}
export interface LinkBatchResult {
  /** Null when nothing linked (there is nothing to undo as a batch). */
  batch_id: string | null
  results: LinkBatchItem[]
}

const linkPath = (ship: string, rest = '') =>
  `/api/shipments/${encodeURIComponent(ship)}/link-proposals${rest}`

export function getLinkProposals(
  ship: string, opts: { signal?: AbortSignal } = {},
): Promise<LinkProposals> {
  return opsFetch<LinkProposals>(linkPath(ship), { signal: opts.signal })
}
/** Confirm the given suggestions; null = every suggested, non-blocked one.
 *  Each is its own write: a stale one fails alone with a sentence. */
export function confirmLinkProposals(ship: string, ids: number[] | null): Promise<LinkBatchResult> {
  return opsFetch<LinkBatchResult>(linkPath(ship, '/confirm'), { method: 'POST', body: { ids } })
}
/** "Not this" — never suggested again for this shipment. */
export function rejectLinkProposal(ship: string, id: number, note?: string | null): Promise<LinkProposal> {
  return opsFetch<LinkProposal>(linkPath(ship, `/${id}/reject`),
    { method: 'POST', body: { note: note ?? null } })
}
/** Reverse one applied link. 409 with the reason when the line has moved. */
export function undoLinkProposal(ship: string, id: number): Promise<LinkProposal> {
  return opsFetch<LinkProposal>(linkPath(ship, `/${id}/undo`), { method: 'POST' })
}
/** Undo every link one Confirm-all made. */
export function undoLinkBatch(ship: string, batchId: string): Promise<LinkBatchResult> {
  return opsFetch<LinkBatchResult>(linkPath(ship, '/undo-batch'),
    { method: 'POST', body: { batch_id: batchId } })
}
/** "Check again": re-score from what is already cached (no Shifl call). */
export function rescanLinkProposals(ship: string): Promise<LinkProposals> {
  return opsFetch<LinkProposals>(linkPath(ship, '/rescan'), { method: 'POST' })
}

export function getReceiveWorkspace(ship: string): Promise<ReceiveWorkspace> {
  return opsFetch<ReceiveWorkspace>(
    `/api/receive/${encodeURIComponent(ship)}`)
}

/** POST /api/receive/{ship}/parse — pull one document straight from Shifl and
 *  extract its line items with ranked UID candidates. Read-only; the operator
 *  confirms before anything is written. */
export function parseReceiveDocument(
  ship: string, body: { shifl_path: string; name?: string | null },
): Promise<ReceiveExtractResult> {
  return opsFetch<ReceiveExtractResult>(
    `/api/receive/${encodeURIComponent(ship)}/parse`,
    { method: 'POST', body },
  )
}
